let SQLite = null
try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { Platform } = require('react-native')
    if (Platform?.OS !== 'web') {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        SQLite = require('expo-sqlite')
    }
} catch {}
import { isWeb, getPlatform } from '../src/utils/platformUtils'
// Lazy require to avoid import cycles and ensure wrapped DB
let __dbModule = null
const buildLocalWrappedDb = () => {
    if (globalThis.__DOTS_SQLITE_WRAPPED_DB) {
        return globalThis.__DOTS_SQLITE_WRAPPED_DB
    }
    try {
    if (!SQLite) throw new Error('SQLite not available (web)')
    const localDb = SQLite.openDatabaseSync('myDB')
        if (getPlatform().OS === 'android') {
            try { localDb.execSync('PRAGMA journal_mode = WAL;') } catch {}
        }
        const wrapStatement = (stmt) => ({
            executeSync: (params = []) => {
                const res = stmt.executeSync(params)
                const rowsArray = Array.isArray(res?.rows) ? res.rows : Array.isArray(res?.rows?._array) ? res.rows._array : []
                return {
                    ...res,
                    getAllSync: () => rowsArray,
                    getFirstSync: () => (rowsArray && rowsArray.length ? rowsArray[0] : null),
                }
            },
            finalizeSync: () => stmt.finalizeSync(),
        })
        const wrapped = {
            execSync: (sql) => localDb.execSync(sql),
            prepareSync: (sql) => {
                const stmt = localDb.prepareSync(sql)
                const wrapped = wrapStatement(stmt)
                return {
                    executeSync: wrapped.executeSync,
                    finalizeSync: wrapped.finalizeSync,
                    getAllSync: (params = []) => wrapped.executeSync(params).getAllSync(),
                    getFirstSync: (params = []) => wrapped.executeSync(params).getFirstSync(),
                }
            },
        }
        globalThis.__DOTS_SQLITE_WRAPPED_DB = wrapped
        return wrapped
    } catch (e) {
        console.error('Fallback SQLite wrapper (schedules) failed:', e)
        return null
    }
}
const getWrappedDB = () => {
    if (!__dbModule) {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        __dbModule = require('../src/db/indexDB')
    }
    const fromIndex = __dbModule?.getNativeDB ? __dbModule.getNativeDB() : null
    return fromIndex || buildLocalWrappedDb()
}
import { POST_TABLE_NAMES } from '../src/db/constants'
import { openDB } from '../src/db/indexDB'
import { SCHEDULES_STORE_NAMES } from '../src/db/constants'
import { 
    updateFormAnswerSchedule as updateFormAnswerScheduleNew,
    getFormsAnswersInStore as getFormsAnswersInStoreNew
} from '../db-sqlite/repositories/schedule.repository'
import {
    FORM_SCHEDULE_STATUS,
    FORM_TYPES,
    ID_DROPDOWN_USERS,
    STATUS_FORM_PENDING_SYNC,
    STATUS_SEND,
    STORE_STATUS,
} from '../src/components/stores/constants/constants.stores'
import { distanceInMeters } from '../src/components/customUtils/calculateDistance'
import { PROGRESS_STEPS } from '../src/redux/zustand/progressScheduleStore'
import { captureAndSaveDataError } from '../src/utils/errorUtils'
import {
    generateIdDateNow,
    getDateNow,
    isLocalGeneratedId,
    isValidMongoId,
    padCodeComplete,
} from '../src/utils/basicUtils'
const USER_PROFILE = 'user_profiles'
const CANT_DIGIT_CODE_COMPANY = 5
const _INITIAL_CODE_ORGANIZATION = '00001'

const database = 'myDB'

let instance = null

const openDatabase = () => {
    if (instance) return instance
    if (isWeb()) {
        instance = { transaction: () => ({ executeSql: () => {} }) }
        return instance
    }

    const dbSync = getWrappedDB()
    const facade = {
        execSync: (sql) => dbSync.execSync(sql),
        prepareSync: (sql) => dbSync.prepareSync(sql),
        transaction: (callback, errorCallback, successCallback) => {
            try {
                const tx = {
                    executeSql: (sql, params = [], success, error) => {
                        try {
                            const isSelect = /^\s*SELECT/i.test(sql)
                            if (isSelect) {
                                const stmt = dbSync.prepareSync(sql)
                                const rowsArray = stmt.getAllSync(params)
                                stmt.finalizeSync()
                                const rows = {
                                    length: rowsArray.length,
                                    item: (i) => rowsArray[i],
                                    _array: rowsArray,
                                }
                                const result = { rows, rowsAffected: 0, insertId: undefined }
                                success && success(tx, result)
                            } else {
                                const stmt = dbSync.prepareSync(sql)
                                stmt.executeSync(params)
                                stmt.finalizeSync()
                                // rowsAffected and insertId might not be available cross-version reliably; do a safe fallback
                                let rowsAffected = 0
                                try {
                                    const chStmt = dbSync.prepareSync('SELECT changes() as rowsAffected')
                                    const chRes = chStmt.executeSync()
                                    const ch = chRes.getFirstSync()
                                    chStmt.finalizeSync()
                                    rowsAffected = ch?.rowsAffected ?? ch?.changes ?? 0
                                } catch {}
                                let insertId
                                try {
                                    if (/\bINSERT\b/i.test(sql)) {
                                        const liStmt = dbSync.prepareSync('SELECT last_insert_rowid() as insertId')
                                        const liRes = liStmt.executeSync()
                                        const li = liRes.getFirstSync()
                                        liStmt.finalizeSync()
                                        insertId = li?.insertId
                                    }
                                } catch {}
                                const result = {
                                    rows: { length: 0, item: () => undefined, _array: [] },
                                    rowsAffected: rowsAffected ?? 0,
                                    insertId,
                                }
                                success && success(tx, result)
                            }
                        } catch (e) {
                            if (error) return error(tx, e)
                            throw e
                        }
                    },
                }
                callback && callback(tx)
                successCallback && successCallback()
            } catch (e) {
                errorCallback && errorCallback(e)
            }
        },
    }

    instance = facade
    return instance
}

export const db = openDatabase()

/**
 * Queue de operaciones para evitar race conditions
 * Garantiza que las operaciones se ejecuten secuencialmente
 */
class FormUpdateQueue {
    constructor() {
        this.queue = Promise.resolve()
        this.isProcessing = false
    }

    /**
     * Encola una operación para ejecutarse secuencialmente
     * @param {Function} operation - Función async que retorna una Promise
     * @returns {Promise<T>} - Resultado de la operación
     */
    enqueue(operation) {
        const result = this.queue
            .then(() => {
                this.isProcessing = true
                return operation()
            })
            .catch((error) => {
                // Propagar el error pero no bloquear la queue
                throw error
            })
            .finally(() => {
                this.isProcessing = false
            })
        
        // Actualizar queue para próxima operación
        // Si hay error, la queue continúa (no se bloquea)
        this.queue = result.catch(() => {})
        
        return result
    }

    /**
     * Verifica si hay operaciones en proceso
     */
    get isPending() {
        return this.isProcessing
    }
}

// Instancia global de la queue
const formUpdateQueue = new FormUpdateQueue()

class schedulesService {
    static isUpdating = false // DEPRECATED - usar formUpdateQueue en su lugar

    constructor() {
        this.db = db
        this.updateQueue = formUpdateQueue
    }

    dbExecuteSql = (query, params) => {
        const db = openDatabase()
        return new Promise((resolve, reject) => {
            db.transaction((tx) => {
                tx.executeSql(
                    query,
                    params,
                    (_, result) => resolve(result),
                    (_, error) => reject(error),
                ),
                    (error) => {
                        console.error('dbExecuteSql - Error en la transacción:', error)
                        reject(error)
                    },
                    () => {
                        console.log('dbExecuteSql - Transacción completada exitosamente')
                        resolve(null)
                    }
            })
        })
    }

    addPosSchedules = (posSchedules, batchSize = 1000, step, setProgress) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                reject(new Error('No se puede realizar una inserción masiva en IndexedDB en entorno web.'))
                return
            } else {
                db.transaction(
                    (tx) => {
                        if (posSchedules.length > 0) {
                            let startIndex = 0

                            const totalposSchedules = posSchedules.length

                            const updateProgress = (currentIndex) => {
                                const { start, end } = PROGRESS_STEPS[step]

                                const progress = Math.round(start + (end - start) * (currentIndex / totalposSchedules))
                                setProgress(progress)
                            }

                            const insertNextBatch = () => {
                                const batchStores = posSchedules.slice(startIndex, startIndex + batchSize)
                                if (batchStores.length > 0) {
                                    const placeholders = batchStores.map(() => '(?,?,?,?,?,?,?,?,?,?)').join(',')
                                    const values = batchStores.flatMap((schedule) => [
                                        schedule._id,
                                        schedule.store_id,
                                        schedule.schedule_id,
                                        JSON.stringify(schedule.forms || []),
                                        JSON.stringify(schedule.forms_relevant),
                                        JSON.stringify(schedule.forms_featured),
                                        schedule.campaign_status,
                                        parseFloat(schedule.lat),
                                        parseFloat(schedule.lng),
                                        true,
                                    ])

                                    tx.executeSql(
                                        `INSERT OR REPLACE INTO ${SCHEDULES_STORE_NAMES.SCHEDULES_POS} (
                                                    _id,
                                                    store_id,
                                                    schedule_id,
                                                    forms,
                                                    forms_relevant,
                                                    forms_featured,
                                                    campaign_status,
                                                    lat,
                                                    lng,
                                                    existsInDB
                                                ) VALUES ${placeholders};`,
                                        values,
                                        (_, result) => {
                                            startIndex += batchSize
                                            if (step != null && setProgress != null) {
                                                updateProgress(startIndex)
                                            }
                                            insertNextBatch() // Insertar el próximo lote
                                        },
                                        (_, error) => {
                                            // console.error('Transaction error:', error)
                                            reject(error)
                                        },
                                    )
                                } else {
                                    resolve()
                                }
                            }

                            insertNextBatch() // Comenzar la inserción en lotes
                        } else {
                            resolve()
                        }
                    },
                    (error) => {
                        console.error('Transaction addPosSchedules error:', error)
                        reject(error)
                    },
                    (_, success) => {
                        resolve()
                    },
                )
            }
        })
    }

    addAdvancesSchedules = async (advancesSchedules) => {
        console.log('📊 addAdvancesSchedules (wrapper) => ', advancesSchedules.length)
        
        // ✅ Usar la nueva implementación de schedule.repository.ts
        const scheduleRepository = await import('../db-sqlite/repositories/schedule.repository')
        return await scheduleRepository.addAdvancesSchedules(advancesSchedules)
    }

    addOrUpdateAdvanceSchedule = (schedule) => {
        const db = openDatabase()

        // console.log("172 -- 🚀 ~ schedulesService ~ schedule:", JSON.stringify(schedule));
        return new Promise((resolve, reject) => {
            if (isWeb()) {
                return
            } else {
                db.transaction(
                    (tx) => {
                        let query = ''
                        let selectParams = []
                        let whereClause = ''
                        let updateParams = []
                        let commonParams = [
                            schedule._id,
                            JSON.stringify(schedule.forms || []),
                            schedule.status,
                            schedule.form_id || '',
                            schedule.progress,
                            schedule.score,
                            schedule.checkin_id,
                            schedule.date_modified,
                            JSON.stringify(schedule.objectives || []),
                        ]

                        // Caso 1: Si es form_id
                        if (schedule.form_id && schedule.form_id !== '') {
                            whereClause = `form_id = ? AND schedule_id = ? AND date(date_init) = date('now')`
                            selectParams = [schedule.form_id, schedule.schedule_id]
                        }
                        // Caso 2: Si es distributor_id
                        else if (schedule.distributor_id && schedule.distributor_id !== '') {
                            whereClause = `distributor_id = ? AND schedule_id = ? AND date(date_init) = date('now')`
                            selectParams = [schedule.distributor_id, schedule.schedule_id]
                        }
                        // Caso 3: Si es pos_id
                        else {
                            if (schedule.recover_forms) {
                                whereClause = `pos_id = ? AND schedule_id = ? AND (checkin_id = ? OR checkin_id IS NULL)`
                                selectParams = [schedule.pos_id, schedule.schedule_id, schedule.checkin_id]
                            } else {
                                whereClause = `pos_id = ? AND schedule_id = ? AND date(date_init) = date('now') AND (checkin_id = ? OR checkin_id IS NULL)`
                                selectParams = [schedule.pos_id, schedule.schedule_id, schedule.checkin_id]
                            }
                        }

                        // Construimos la consulta SELECT
                        query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} WHERE ${whereClause} ORDER BY date_modified DESC;`

                        // Ejecuta la consulta SELECT
                        tx.executeSql(
                            query,
                            selectParams,
                            (_, result) => {
                                if (result.rows._array.length > 0) {
                                    const formsLocal = JSON.parse(result.rows._array[0].forms)
                                    const formsLlega = schedule.forms

                                    if (formsLocal && formsLocal.length > 0) {
                                        const formsUpdate = formsLocal.map((formLocal) => {
                                            const formLlega = formsLlega.find((formLlega) => formLlega._id === formLocal._id)
                                            if (formLlega) return formLlega
                                            else return formLocal
                                        })

                                        commonParams = [
                                            schedule._id,
                                            JSON.stringify(formsUpdate || []),
                                            schedule.status,
                                            schedule.form_id || '',
                                            schedule.progress,
                                            schedule.score,
                                            schedule.checkin_id,
                                            schedule.date_modified,
                                            JSON.stringify(schedule.objectives || []),
                                        ]
                                    } else {
                                        commonParams = [
                                            schedule._id,
                                            JSON.stringify(formsLlega || []),
                                            schedule.status,
                                            schedule.form_id || '',
                                            schedule.progress,
                                            schedule.score,
                                            schedule.checkin_id,
                                            schedule.date_modified,
                                            JSON.stringify(schedule.objectives || []),
                                        ]
                                    }

                                    updateQuery = `UPDATE ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES}
                                                     SET _id = ?, forms = ?, status = ?, form_id = ?, progress = ?,score = ?, checkin_id = ?, date_modified = ?, objectives = ?
                                                     WHERE ${whereClause}` //${whereClause};

                                    // Los parámetros de la actualización combinan los comunes y los de selección
                                    updateParams = [...commonParams, ...selectParams]

                                    tx.executeSql(
                                        updateQuery,
                                        updateParams,
                                        (_, result) => {
                                            resolve(result)
                                        },
                                        (_, error) => {
                                            console.error('Transaction update error:', error)
                                            reject(error)
                                        },
                                    )
                                } else {
                                    // Si no existe, inserta el nuevo registro
                                    const insertQuery = `INSERT INTO ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} (
                                                            _id, pos_id, schedule_id, forms, status, date_init, form_id, progress, score, checkin_id, date_modified, distributor_id, objectives
                                                         ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?,?,?);`

                                    const insertParams = [
                                        schedule._id,
                                        schedule.pos_id,
                                        schedule.schedule_id,
                                        JSON.stringify(schedule.forms || []),
                                        schedule.status,
                                        schedule.date_init,
                                        schedule.form_id || '',
                                        schedule.progress,
                                        schedule.score,
                                        schedule.checkin_id,
                                        schedule.date_modified,
                                        schedule.distributor_id || '',
                                        JSON.stringify(schedule.objectives || []),
                                    ]

                                    tx.executeSql(
                                        insertQuery,
                                        insertParams,
                                        (_, result) => {
                                            resolve(result)
                                        },
                                        (_, error) => {
                                            console.error('Transaction insert error:', error)
                                            reject(error)
                                        },
                                    )
                                }
                            },
                            (_, error) => {
                                console.error('Transaction select error:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction addOrUpdateAdvanceSchedule error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    /**
     * 🆕 MIGRADO A TYPESCRIPT REPOSITORY
     * 
     * Wrapper de compatibilidad que usa la nueva implementación moderna
     * de getFormStatusFromAdvances del repository TypeScript.
     * 
     * Mantiene la misma interfaz para no romper código existente.
     * 
     * @description Obtiene el estado de progreso de un formulario desde SCHEDULES_ADVANCES
     * @param {*} schedule_id - ID del schedule
     * @param {*} form_id - ID del formulario (para forms de agenda matutino/vespertino)
     * @param {*} dateSelected - Fecha seleccionada (actualmente no se usa en la query)
     * @returns {Promise<any | null>} Registro del advance o null si no se encuentra
     */
    getFormStatusFromAdvances = async (schedule_id, form_id, dateSelected) => {
        try {
            // eslint-disable-next-line @typescript-eslint/no-var-requires
            const scheduleRepository = await import('../db-sqlite/repositories/schedule.repository')

            return await scheduleRepository.getFormStatusFromAdvances(schedule_id, form_id, dateSelected)
        } catch (error) {
            console.error('❌ Error en getFormStatusFromAdvances (wrapper):', error)
            throw error
        }
    }

    // ========================================================================
    // 🗑️ DEPRECATED - Implementación antigua (mantener comentada como referencia)
    // Para ver la implementación anterior, revisar git history
    // ========================================================================
    /*
    getFormStatusFromAdvances_OLD = (schedule_id, form_id, dateSelected) => {
        const db = openDatabase()

        if (form_id) {
            //para forms de agenda (matutino/vesp)
            return new Promise(async (resolve, reject) => {
                const db = openDatabase()

                db.transaction((tx) => {
                    tx.executeSql(
                        `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} 
                             WHERE schedule_id = ? AND form_id = ? `,
                        [schedule_id, form_id],
                        (_, { rows }) => {
                            if (rows.length > 0) {
                                resolve(rows.item(0))
                            } else {
                                resolve(null) // No encontrado
                            }
                        },
                        (_, error) => {
                            console.error('Query error:', error)
                            reject(error)
                        },
                    )
                })
            })
        }
    }
    */
    // ========================================================================
    // FIN DEPRECATED
    // ========================================================================

    getProgressByForm = (schedule_id, form_id) => {
        const db = openDatabase()

        if (form_id) {
            return new Promise((resolve, reject) => {
                db.transaction((tx) => {
                    tx.executeSql(
                        `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} WHERE schedule_id = ?`,
                        [schedule_id],
                        (_, { rows }) => {
                            let count = 0
                            let storesCounted = new Set()

                            // consulta a adv
                            for (let i = 0; i < rows._array.length; i++) {
                                const item = rows._array[i]
                                try {
                                    if (item.forms) {
                                        const formsArray = JSON.parse(item.forms)
                                        formsArray.forEach((form) => {
                                            if (form._id === form_id && !storesCounted.has(item.pos_id)) {
                                                storesCounted.add(item.pos_id)
                                                count += 1
                                            }
                                        })
                                    }
                                } catch (parseError) {
                                    console.error('Error al parsear forms:', parseError)
                                }
                            }
                            // consulta a FORMS_QUESTION_ANSWERS por forms locales
                            tx.executeSql(
                                `SELECT * FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} WHERE schedule_id = ? AND form_id = ? AND status NOT IN (${FORM_SCHEDULE_STATUS.BLOCKED}, ${FORM_SCHEDULE_STATUS.PENDING}, ${FORM_SCHEDULE_STATUS.IN_PROGRESS})`,
                                [schedule_id, form_id],
                                (_, { rows: answerRows }) => {
                                    for (let j = 0; j < answerRows._array.length; j++) {
                                        const answerItem = answerRows._array[j]
                                        if (!storesCounted.has(answerItem.store_id)) {
                                            storesCounted.add(answerItem.store_id)
                                            count += 1
                                        }
                                    }
                                    resolve(count)
                                },
                                (_, error) => {
                                    console.error('Query error in FORMS_QUESTION_ANSWERS:', error)
                                    reject(error)
                                },
                            )
                        },
                        (_, error) => {
                            console.error('Query error in SCHEDULES_ADVANCES:', error)
                            reject(error)
                        },
                    )
                })
            })
        }
    }
    getProgressByForms = (schedule_id, form_ids) => {
        const db = openDatabase()

        if (Array.isArray(form_ids) && form_ids.length > 0) {
            return new Promise((resolve, reject) => {
                db.transaction((tx) => {
                    tx.executeSql(
                        `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} WHERE schedule_id = ?`,
                        [schedule_id],
                        (_, { rows }) => {
                            let count = 0
                            let storesCounted = new Set()

                            // Consulta a adv
                            for (let i = 0; i < rows._array.length; i++) {
                                const item = rows._array[i]
                                try {
                                    if (item.forms) {
                                        const formsArray = JSON.parse(item.forms)
                                        formsArray.forEach((form) => {
                                            if (form_ids.includes(form._id) && !storesCounted.has(item.pos_id)) {
                                                storesCounted.add(item.pos_id)
                                                count += 1
                                            }
                                        })
                                    }
                                } catch (parseError) {
                                    console.error('Error al parsear forms:', parseError)
                                }
                            }

                            // Consulta a FORMS_QUESTION_ANSWERS por forms locales
                            tx.executeSql(
                                `SELECT * FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} WHERE schedule_id = ? AND form_id IN (${form_ids
                                    .map(() => '?')
                                    .join(',')}) AND status NOT IN (${FORM_SCHEDULE_STATUS.BLOCKED}, ${FORM_SCHEDULE_STATUS.PENDING}, ${
                                    FORM_SCHEDULE_STATUS.IN_PROGRESS
                                })`,
                                [schedule_id, ...form_ids],
                                (_, { rows: answerRows }) => {
                                    for (let j = 0; j < answerRows._array.length; j++) {
                                        const answerItem = answerRows._array[j]
                                        if (!storesCounted.has(answerItem.store_id)) {
                                            storesCounted.add(answerItem.store_id)
                                            count += 1
                                        }
                                    }
                                    resolve(count)
                                },
                                (_, error) => {
                                    console.error('Query error in FORMS_QUESTION_ANSWERS:', error)
                                    reject(error)
                                },
                            )
                        },
                        (_, error) => {
                            console.error('Query error in SCHEDULES_ADVANCES:', error)
                            reject(error)
                        },
                    )
                })
            })
        } else {
            return Promise.reject('Invalid form_ids parameter. Must be a non-empty array.')
        }
    }

    getLatestAdvanceSchedule = () => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                return
            } else {
                db.transaction((tx) => {
                    tx.executeSql(
                        `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} ORDER BY date_modified DESC LIMIT 1;`,
                        [],
                        (_, { rows }) => {
                            if (rows.length > 0) {
                                resolve(rows.item(0))
                            } else {
                                resolve(null) // No encontrado
                            }
                        },
                        (_, error) => {
                            console.error('Query error:', error)
                            resolve(null)
                        },
                    )
                })
            }
        })
    }

    getLatestAdvancePendingsSchedule = () => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                return
            } else {
                db.transaction((tx) => {
                    tx.executeSql(
                        `SELECT * 
                        FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES}
                        WHERE status = ? 
                        ORDER BY date_modified 
                        DESC LIMIT 1
                        ;`,
                        [STORE_STATUS.PENDING_CONNECTION],
                        (_, { rows }) => {
                            if (rows.length > 0) {
                                resolve(rows.item(0))
                            } else {
                                resolve(null) // No encontrado
                            }
                        },
                        (_, error) => {
                            console.error('Query error:', error)
                            resolve(null)
                        },
                    )
                })
            }
        })
    }

    /**
     * 🆕 MIGRADO A TYPESCRIPT REPOSITORY
     * 
     * Wrapper de compatibilidad que usa la nueva implementación moderna
     * de upsertFormStatus del repository TypeScript.
     * 
     * Mantiene la misma interfaz para no romper código existente.
     * 
     * @description Realiza un UPSERT (UPDATE o INSERT) del estado de progreso de un formulario
     * @param {*} schedule_id - ID del schedule
     * @param {*} form_id - ID del formulario
     * @param {*} newStatus - Nuevo estado del formulario
     * @param {*} dateInit - Fecha de inicio
     * @param {*} progress - Progreso del formulario (0-100)
     * @param {*} scoreObtained - Puntaje obtenido
     * @returns {Promise<{ success: boolean; action: 'inserted' | 'updated' }>}
     */
    upsertFormStatus = async (schedule_id, form_id, newStatus, dateInit, progress, scoreObtained) => {
        try {
            console.log('🚀🚀🚀🚀🚀 ~ upsertFormStatus (wrapper) ============> ')
            // eslint-disable-next-line @typescript-eslint/no-var-requires
            const scheduleRepository = await import('../db-sqlite/repositories/schedule.repository')
            return await scheduleRepository.upsertFormStatus(schedule_id, form_id, newStatus, dateInit, progress, scoreObtained)
        } catch (error) {
            console.error('❌ Error en upsertFormStatus (wrapper):', error)
            throw error
        }
    }

    // ========================================================================
    // 🗑️ DEPRECATED - Implementación antigua (mantener comentada como referencia)
    // Para ver la implementación anterior, revisar git history
    // ========================================================================
    /*
    upsertFormStatus_OLD = (schedule_id, form_id, newStatus, dateInit, progress, scoreObtained) => {
        const db = openDatabase()

        //actualiza el status o crea el registro
        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    // Intentamos actualizar el registro si existe
                    const updateQuery = `UPDATE ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES}
                                         SET status = ?, progress=?, score=?
                                         WHERE schedule_id = ? AND form_id = ?`

                    tx.executeSql(
                        updateQuery,
                        [newStatus, progress, scoreObtained, schedule_id, form_id],
                        (_, result) => {
                            if (result.rowsAffected === 0) {
                                // Si no se encontró ningún registro para actualizar, insertamos uno nuevo
                                const insertQuery = `INSERT INTO ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES}
                                                     (_id, schedule_id, form_id, status, date_init, progress, score)
                                                     VALUES (?, ?, ?, ?, ?, ?, ?)`
                                const newId = generateUniqueId() // Genera un nuevo ID único
                                console.log('🚀 ~ upsertFormStatus ~ newId:', newId)
                                tx.executeSql(
                                    insertQuery,
                                    [newId, schedule_id, form_id, newStatus, dateInit, progress, scoreObtained],
                                    (_, insertResult) => {
                                        // console.log('🚀 ~ schedulesService ~ returnnewPromise ~ insertResult:', insertResult)
                                        resolve(insertResult)
                                    },
                                    (_, insertError) => {
                                        console.error('Insert error:', insertError)
                                        reject(insertError)
                                    },
                                )
                            } else {
                                resolve(result)
                            }
                        },
                        (_, updateError) => {
                            console.error('Update error:', updateError)
                            reject(updateError)
                        },
                    )
                },
                (transactionError) => {
                    console.error('Transaction error:', transactionError)
                    reject(transactionError)
                },
            )
        })
    }
    */
    // ========================================================================
    // FIN DEPRECATED
    // ========================================================================

    getStoreScheduleAdvances = (scheduleId, storeId, distributor = false) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    const query = distributor
                        ? `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} WHERE schedule_id = ? AND distributor_id = ? ORDER BY date_modified DESC LIMIT 1;`
                        : `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} WHERE schedule_id = ? AND pos_id = ? ORDER BY date_modified DESC LIMIT 1;`

                    tx.executeSql(
                        query,
                        [scheduleId, storeId],
                        (_, result) => {
                            const rows = result.rows
                            resolve(rows._array[0])
                        },
                        (_, error) => {
                            console.error('Transaction error:', error)
                            reject(error)
                        },
                    )
                },
                (error) => {
                    console.error('Transaction getPosSchedulesByScheduleId error:', error)
                    reject(error)
                },
            )
        })
    }

    getAllScheduleAdvances = () => {
        const db = openDatabase()

        console.log('estoy en getAllScheduleAdvances')
        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    const query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES};`

                    tx.executeSql(
                        query,
                        [],
                        (_, result) => {
                            const rows = result.rows._array
                            let schedule_adv = rows.map((item) => {
                                return {
                                    _id: item.id,
                                    schedule_id: item.schedule_id,
                                    pos_id: item.pos_id,
                                    forms: JSON.parse(item.forms),
                                }
                            })
                            resolve(schedule_adv)
                        },
                        (_, error) => {
                            console.error('Transaction error:', error)
                            reject(error)
                        },
                    )
                },
                (error) => {
                    console.error('Transaction getAllScheduleAdvances error:', error)
                    reject(error)
                },
            )
        })
    }

    getPosSchedulesByScheduleId = (scheduleId) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    tx.executeSql(
                        `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_POS} WHERE schedule_id = ?;`,
                        [scheduleId],
                        (_, result) => {
                            const rows = result.rows
                            const posSchedules = []
                            for (let i = 0; i < rows.length; i++) {
                                const item = rows.item(i)
                                posSchedules.push({
                                    _id: item._id,
                                    store_id: item.store_id,
                                    schedule_id: item.schedule_id,
                                    forms: JSON.parse(item.forms),
                                    campaign_status: item.campaign_status,
                                    lat: item.lat,
                                    lng: item.lng,
                                })
                            }
                            resolve(posSchedules)
                        },
                        (_, error) => {
                            console.error('Transaction error:', error)
                            reject(error)
                        },
                    )
                },
                (error) => {
                    console.error('Transaction getPosSchedulesByScheduleId error:', error)
                    reject(error)
                },
            )
        })
    }

    getProgressStatus = (scheduleId, statusMap) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    // Primero obtenemos el total de tiendas
                    tx.executeSql(
                        `SELECT COUNT(*) as total 
                         FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_POS} 
                         WHERE schedule_id = ?;`,
                        [scheduleId],
                        (_, result) => {
                            const totalStores = result.rows._array[0].total

                            // Luego, obtenemos los avances por estado
                            let queryOld = `SELECT status, COUNT(*) as count 
                                 FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} 
                                 WHERE schedule_id = ? AND pos_id IS NOT NULL
                                 GROUP BY status;`

                            let queryNew = `
                                SELECT status, COUNT(*) as count 
                                FROM (
                                    SELECT adv.pos_id, adv.schedule_id, adv.status
                                    FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} AS adv
                                    INNER JOIN (
                                        SELECT pos_id, schedule_id, MAX(date_modified) AS latest_date 
                                        FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES}
                                        WHERE schedule_id = ? AND pos_id IS NOT NULL
                                        GROUP BY pos_id, schedule_id
                                    ) AS latest_adv
                                    ON adv.pos_id = latest_adv.pos_id 
                                    AND adv.schedule_id = latest_adv.schedule_id 
                                    AND adv.date_modified = latest_adv.latest_date
                                ) AS recent_advances
                                GROUP BY status;
                                `
                            tx.executeSql(
                                queryNew,
                                [scheduleId],
                                (_, result) => {
                                    //console.log("🚀 ~ schedulesService ~ returnnewPromise ~ result:", result)
                                    const rows = result.rows._array
                                    let statusCounts = rows.map((row) => ({
                                        _id: row?.status,
                                        label: statusMap[row.status]?.label || 'Unknown',
                                        value: row.count,
                                        color_hex: statusMap[row.status]?.color_hex || '#000000', // Default color if not mapped
                                    }))

                                    // Calcular las tiendas sin avances (status desconocido)
                                    const totalAdvances = rows.reduce((sum, row) => sum + row.count, 0)
                                    const pending = totalStores - totalAdvances

                                    if (pending > 0) {
                                        statusCounts.push({
                                            _id: 0,
                                            label: statusMap[0]?.label || 'Unknown',
                                            value: pending,
                                            color_hex: statusMap[0]?.color_hex || '#000000',
                                        })
                                    }

                                    resolve(statusCounts)
                                },
                                (_, error) => {
                                    console.error('Transaction error in second query:', error)
                                    reject(error)
                                },
                            )
                        },
                        (_, error) => {
                            console.error('Transaction error in first query:', error)
                            reject(error)
                        },
                    )
                },
                (error) => {
                    console.error('Transaction error in transaction:', error)
                    reject(error)
                },
            )
        })
    }

    getForm = (formId) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                const transaction = db.transaction([SCHEDULES_STORE_NAMES.FORMS], 'readonly')
                const objectStore = transaction.objectStore(SCHEDULES_STORE_NAMES.FORMS)

                const request = objectStore.get(formId)

                request.onsuccess = (event) => {
                    const form = event.target.result
                    if (form) {
                        form.type_form = JSON.parse(form.type_form)
                        form.contents = JSON.parse(form.contents)
                        form.segments = JSON.parse(form.segments)
                        form.tags = JSON.parse(form.tags)
                    }
                    resolve(form)
                }

                request.onerror = (event) => {
                    reject(event.target.error)
                }
            } else {
                db.transaction(
                    (tx) => {
                        tx.executeSql(`SELECT * FROM ${SCHEDULES_STORE_NAMES.FORMS} WHERE _id = ?`, [formId], (_, result) => {
                            if (result.rows.length > 0) {
                                const form = result.rows.item(0)
                                form.type_form = JSON.parse(form.type_form)
                                form.contents = JSON.parse(form.contents)
                                form.segments = JSON.parse(form.segments)
                                form.tags = JSON.parse(form.tags)
                                resolve(form)
                            } else {
                                resolve(null) // El formulario no fue encontrado
                            }
                        })
                    },
                    (error) => {
                        console.error('Transaction getForm error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    getFormName = (formId) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                const transaction = db.transaction([SCHEDULES_STORE_NAMES.FORMS], 'readonly')
                const objectStore = transaction.objectStore(SCHEDULES_STORE_NAMES.FORMS)

                const request = objectStore.get(formId)

                request.onsuccess = (event) => {
                    const form = event.target.result
                    if (form) {
                        resolve(form.name) // Solo devolver el nombre del formulario
                    } else {
                        resolve(null) // El formulario no fue encontrado
                    }
                }

                request.onerror = (event) => {
                    reject(event.target.error)
                }
            } else {
                db.transaction(
                    (tx) => {
                        tx.executeSql(`SELECT name_form FROM ${SCHEDULES_STORE_NAMES.FORMS} WHERE _id = ?`, [formId], (_, result) => {
                            if (result.rows.length > 0) {
                                const form = result.rows.item(0)
                                resolve(form.name_form) // Solo devolver el nombre del formulario
                            } else {
                                resolve(null) // El formulario no fue encontrado
                            }
                        })
                    },
                    (error) => {
                        console.error('Transaction getFormName error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    /**
     * @description updatea formularios de la tabla SCHEDULES_STORE_NAMES.FORMS
     * @param {*} formId
     * @returns
     */
    updateStatusForm = (formId, storeId, status) => {
        const db = openDatabase()

        // de ese form , o sea qie lo pase a completed:true
        return new Promise((resolve, reject) => {
            if (isWeb()) {
            } else {
                db.transaction(
                    (tx) => {
                        const query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS}
                                    SET status = ?
                                    WHERE _id = ? AND store_id = ?;`

                        const queryParams = [status, formId, storeId]

                        tx.executeSql(
                            query,
                            queryParams,
                            (_, result) => {
                                console.log('Registro actualizado con exito: updateStatusForm')
                                resolve()
                            },
                            (_, error) => {
                                console.error('Transaction error: updateStatusForm =>', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction updateStatusForm error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    /**
     * @description es para remplazar
     * @param {*} formId
     */
    getFormAndUpdateSecondaryAction = (formId, storeId, secondary_actions) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
            } else {
                db.transaction(
                    (tx) => {
                        tx.executeSql(`SELECT * FROM ${SCHEDULES_STORE_NAMES.FORMS} WHERE _id = ?`, [formId], (_, result) => {
                            if (result.rows.length > 0) {
                                const form = result.rows.item(0)
                                form.contents = JSON.parse(form.contents)
                                form.contents.forEach((question) => {
                                    if (question.input_type && question.input_type['_id'] && question.input_type['_id'] === ID_DROPDOWN_USERS) {
                                        question.secondary_actions = secondary_actions
                                    }
                                })
                                this.updateFormSecondaryAction(formId, storeId, JSON.stringify(form.contents))
                                    .then((res) => {
                                        //  console.log('615 -- updateFormSecondaryAction: exito')
                                    })
                                    .catch((error) => {
                                        console.error('618 ---- updateFormSecondaryAction - error: ', error)
                                    })

                                resolve()
                            } else {
                                resolve(null) // El formulario no fue encontrado
                            }
                        })
                    },
                    (error) => {
                        console.error('Transaction updateSecondaryActionForm error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    updateFormsSecondaryActionUsersDropdown = (arrayFormUsers, storeId) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                resolve()
                return
            } else {
                const formIds = arrayFormUsers.map((form) => `'${form.form_id}'`).join(',')
                if (!formIds) {
                    resolve(null)
                    return
                }
                db.transaction(
                    (tx) => {
                        tx.executeSql(`SELECT * FROM ${SCHEDULES_STORE_NAMES.FORMS} WHERE _id IN (${formIds})`, [], (_, result) => {
                            if (result.rows.length === 0) {
                                resolve(null)
                                return
                            }

                            const formUpdates = {}

                            arrayFormUsers.forEach((form) => {
                                formUpdates[form.form_id] = form.secondary_action
                            })

                            const updatedForms = []

                            for (let i = 0; i < result.rows.length; i++) {
                                let form = result.rows.item(i)
                                form.contents = JSON.parse(form.contents)

                                form.contents.forEach((question) => {
                                    if (question.input_type && question.input_type['_id'] === ID_DROPDOWN_USERS) {
                                        question.secondary_actions = formUpdates[form._id] || []
                                    }
                                })

                                updatedForms.push({
                                    id: form._id,
                                    contents: JSON.stringify(form.contents),
                                })
                            }

                            if (updatedForms.length === 0) {
                                resolve(null)
                                return
                            }

                            let query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS} SET contents = CASE `
                            let ids = []

                            updatedForms.forEach(({ id, contents }, index) => {
                                query += `WHEN _id = ? THEN ? `
                                ids.push(id, contents)
                            })

                            query += `END WHERE _id IN (${updatedForms.map(() => '?').join(', ')});`
                            ids.push(...updatedForms.map(({ id }) => id))

                            tx.executeSql(
                                query,
                                ids,
                                () => {
                                    console.log('Formularios actualizados correctamente')
                                    resolve()
                                },
                                (_, error) => {
                                    console.error('Error actualizando formularios:', error)
                                    reject(error)
                                },
                            )
                        })
                    },
                    (error) => {
                        console.error('Transaction updateFormsSecondaryActionUsersDropdown error: ', error)
                        reject(error)
                    },
                )
            }
        })
    }

    updateFormSecondaryAction = (formId, storeId, arrayFormsModified) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
            } else {
                db.transaction(
                    (tx) => {
                        const query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS}
                                    SET contents = ?
                                    WHERE _id = ?;`

                        const queryParams = [arrayFormsModified, formId]

                        tx.executeSql(
                            query,
                            queryParams,
                            (_, result) => {
                                // console.log('Registro actualizado con exito: updateFormSecondaryAction')
                                resolve()
                            },
                            (_, error) => {
                                console.error('Transaction error: updateFormSecondaryAction =>', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction updateFormSecondaryAction error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    updateFormSchedule = (formId) => {
        const db = openDatabase()

        // de ese form , o sea qie lo pase a completed:true
        return new Promise((resolve, reject) => {
            if (isWeb()) {
                const transaction = db.transaction([SCHEDULES_STORE_NAMES.FORMS], 'readonly')
                const objectStore = transaction.objectStore(SCHEDULES_STORE_NAMES.FORMS)

                const request = objectStore.get(formId)

                request.onsuccess = (event) => {
                    const form = event.target.result
                    if (form) {
                        form.type_form = JSON.parse(form.type_form)
                        form.contents = JSON.parse(form.contents)
                        form.segments = JSON.parse(form.segments)
                    }
                    resolve(form)
                }

                request.onerror = (event) => {
                    reject(event.target.error)
                }
            } else {
                db.transaction(
                    (tx) => {
                        tx.executeSql(`SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES} WHERE _id = ?`, [formId], (_, result) => {
                            if (result.rows.length > 0) {
                                const form = result.rows.item(0)
                                form.type_form = JSON.parse(form.type_form)
                                form.contents = JSON.parse(form.contents)
                                form.segments = JSON.parse(form.segments)
                                resolve(form)
                            } else {
                                resolve(null) // El formulario no fue encontrado
                            }
                        })
                    },
                    (error) => {
                        console.error('Transaction getForm error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    addOrUpdateSchedule = (schedule) => {
        const db = openDatabase()

        // console.log('🚀 ~ schedulesService ~ schedule:', schedule._id)
        return new Promise((resolve, reject) => {
            if (isWeb()) {
                reject(new Error('No se puede realizar una inserción masiva en IndexedDB en entorno web.'))
                return
            }

            db.transaction(
                (tx) => {
                        tx.executeSql(`SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.SCHEDULES} WHERE _id = ?`, [schedule._id], (_, result) => {
                            const count = result.rows.item(0).count || 0
                        let query, params

                        if (count === 0) {
                            // Insertar nuevo registro
                            // query = `INSERT INTO ${SCHEDULES_STORE_NAMES.SCHEDULES} (
                            //     _id,
                            //     date_finish,
                            //     date_init,
                            //     form_finish,
                            //     form_init,
                            //     stores
                            // ) VALUES (?, ?, ?, ?, ?, ?)`;
                            // params = [
                            //     schedule._id,
                            //     schedule.date_finish,
                            //     schedule.date_init,
                            //     JSON.stringify(schedule.form_finish || {}),
                            //     JSON.stringify(schedule.form_init || {}),
                            //     JSON.stringify(schedule.stores || [])
                            // ];
                        } else {
                            // Actualizar registro existente
                            query = `UPDATE ${SCHEDULES_STORE_NAMES.SCHEDULES} SET
                                    date_finish = ?,
                                    date_init = ?,
                                    form_finish = ?,
                                    form_init = ?,
                                    stores = ?
                                    WHERE _id = ?`
                            params = [
                                schedule.date_finish,
                                schedule.date_init,
                                JSON.stringify(schedule.form_finish || {}),
                                JSON.stringify(schedule.form_init || {}),
                                JSON.stringify(schedule.stores || []),
                                schedule._id,
                            ]
                        }

                        tx.executeSql(
                            query,
                            params,
                            (_, result) => {
                                console.log('🚀 ~ Agenda guardada/actualizada', result)
                                resolve(result)
                            },
                            (_, error) => {
                                console.error('Transaction error:', error)
                                reject(error)
                            },
                        )
                    })
                },
                (error) => {
                    console.error('Transaction error:', error)
                    reject(error)
                },
            )
        })
    }

    // Función para obtener la agenda vigente de hoy
    getTodaySchedule = (today) => {
        const db = openDatabase()

        console.log('🚀 ~ schedulesService ~ today:', today)
        return new Promise((resolve, reject) => {
            if (isWeb()) {
                const transaction = db.transaction([SCHEDULES_STORE_NAMES.SCHEDULES], 'readonly')
                const objectStore = transaction.objectStore(SCHEDULES_STORE_NAMES.SCHEDULES)

                const request = objectStore.openCursor()

                request.onsuccess = (event) => {
                    const cursor = event.target.result
                    if (cursor) {
                        const schedule = cursor.value
                        const scheduleDateInit = new Date(schedule.date_init).toISOString().split('T')[0]
                        const scheduleDateFinish = new Date(schedule.date_finish).toISOString().split('T')[0]

                        console.log(`Comparando fechas: ${scheduleDateInit} <= ${today} && ${scheduleDateFinish} >= ${today}`)

                        if (scheduleDateInit <= today && scheduleDateFinish >= today) {
                            resolve(schedule)
                            return
                        }
                        cursor.continue()
                    } else {
                        resolve(null)
                    }
                }

                request.onerror = (event) => {
                    reject(event.target.error)
                }
            } else {
                db.transaction(
                    (tx) => {
                        tx.executeSql(
                            `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES} WHERE date_init <= ? AND date_finish >= ? LIMIT 1`,
                            [today, today],
                            (_, result) => {
                                if (result.rows._array.length > 0) {
                                    const scheduleRow = result.rows._array[0]
                                    const schedule = {
                                        _id: scheduleRow._id,
                                        additional: JSON.parse(scheduleRow.additional),
                                        date_finish: scheduleRow.date_finish,
                                        date_init: scheduleRow.date_init,
                                    }
                                    resolve(schedule)
                                } else {
                                    console.log('----------------. else')
                                    resolve(new Error('Schedule not found'))
                                }
                            },
                        )
                    },
                    (error) => {
                        reject(error)
                    },
                )
            }
        })
    }

    update_schedule = async (schedule_id, key, value) => {
        const db = openDatabase()

        try {
            return new Promise((resolve, reject) => {
                db.transaction(
                    (tx) => {
                        tx.executeSql(
                            `UPDATE ${SCHEDULES_STORE_NAMES.SCHEDULES} SET ${key} = ? WHERE _id = ?;`,
                            [value, schedule_id],
                            (_, result) => {
                                console.log('agenda actualizada con éxito', result)
                                resolve(result)
                            },
                            (_, error) => {
                                console.error('Error al actualizar la agenda:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Error en la transacción de actualización:', error)
                        reject(error)
                    },
                    null,
                )
            })
        } catch (error) {
            console.error('Error en la actualización del contenido:', error)
            throw error // Rechazar el error para que sea manejado por el código que llama a esta función
        }
    }
    get_schedule = async (schedule_id) => {
        const db = openDatabase()

        try {
            return new Promise((resolve, reject) => {
                db.transaction(
                    (tx) => {
                        tx.executeSql(
                            `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES} WHERE _id = ?;`,
                            [schedule_id],
                            (_, result) => {
                                //  console.log('Obtuve la agenda', result)
                                let scheduleRow = result.rows._array[0]
                                if (scheduleRow) {
                                    const schedule_formate = {
                                        _id: scheduleRow._id,
                                        date_finish: scheduleRow.date_finish,
                                        date_init: scheduleRow.date_init,
                                        form_finish: scheduleRow.form_finish.length > 6 ? JSON.parse(scheduleRow.form_finish) : null,
                                        form_init: scheduleRow.form_init.length > 6 ? JSON.parse(scheduleRow.form_init) : null,
                                        stores: [], //JSON.parse(scheduleRow.stores), // [],
                                        progress_diary: scheduleRow.progress_diary,
                                        additional: JSON.parse(scheduleRow.additional),
                                    }
                                    resolve(schedule_formate)
                                } else {
                                    reject('No se encontró la agenda')
                                }
                            },
                            (_, error) => {
                                console.error('No se pudo obtener la agenda:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Error en la transacción de actualización:', error)
                        reject(error)
                    },
                    null,
                )
            })
        } catch (error) {
            console.error('Error en la actualización del contenido:', error)
            throw error // Rechazar el error para que sea manejado por el código que llama a esta función
        }
    }

    getStore = (storeId) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                // Implementación para entorno web
                const transaction = db.transaction([SCHEDULES_STORE_NAMES.STORES], 'readonly')
                const objectStore = transaction.objectStore(SCHEDULES_STORE_NAMES.STORES)
                const request = objectStore.get(storeId)

                request.onsuccess = (event) => {
                    const store = event.target.result
                    if (store) {
                        resolve(store)
                    } else {
                        reject(new Error(`Store with ID ${storeId} not found`))
                    }
                }

                request.onerror = (event) => {
                    reject(event.target.error)
                }
            } else {
                // Implementación para dispositivos móviles
                db.transaction(
                    (tx) => {
                        tx.executeSql(
                            `SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES} WHERE _id = ?`,
                            [storeId],
                            (_, result) => {
                                if (result.rows.length > 0) {
                                    const store = result.rows.item(0)
                                    let forms_store = []

                                    resolve(store)
                                } else {
                                    reject(new Error(`Store with ID ${storeId} not found`))
                                }
                            },
                            (_, error) => {
                                console.error('Transaction error:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction getStore error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    getStoreForGoto = (storeId) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                // Lógica para la plataforma web
            } else {
                db.transaction(
                    (tx) => {
                        const query = `
                            SELECT pos.*, adv.status AS campaign_status, adv.forms AS forms_advances
                            FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_POS} AS pos
                            LEFT JOIN (
                                SELECT adv.pos_id, adv.schedule_id, adv.status , adv.forms
                                FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} AS adv
                                INNER JOIN (
                                    SELECT pos_id , MAX(date_modified) AS latest_date 
                                    FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES}
                                    WHERE pos_id = ?
                                    GROUP BY pos_id
                                ) AS latest_adv
                                ON adv.pos_id = latest_adv.pos_id 
                                AND adv.date_modified = latest_adv.latest_date
                            ) AS adv
                            ON pos.store_id = adv.pos_id
                            WHERE pos.store_id = ?`

                        let params = [storeId, storeId]
                        tx.executeSql(
                            query,
                            params,
                            (_, result) => {
                                if (result.rows.length > 0) {
                                    const store = result.rows.item(0)
                                    resolve(store)
                                } else {
                                    reject(new Error(`Store with ID ${storeId} not found`))
                                }
                            },
                            (_, error) => {
                                console.error('Transaction error:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction getStore error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    getAllStores = (pageNumber, pageSize, campaignStatus) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                const transaction = db.transaction([SCHEDULES_STORE_NAMES.STORES], 'readonly')
                const objectStore = transaction.objectStore(SCHEDULES_STORE_NAMES.STORES)

                // Define las variables para paginación en web
                const offset = pageNumber * pageSize
                const request = objectStore.getAll(null, pageSize, offset)

                request.onsuccess = (event) => {
                    const stores = event.target.result

                    // Filtrar las tiendas por etiquetas si se proporcionan
                    let filteredStores = stores
                    if (campaignStatus != null && campaignStatus != -1) {
                        filteredStores = stores.filter((store) => store.campaign_status === campaignStatus)
                    }
                    resolve(filteredStores)
                }

                request.onerror = (event) => {
                    reject(event.target.error)
                }
            } else {
                //Obtener la agenda

                const offset = pageNumber * pageSize
                console.log('🚀 ~ returnnewPromise ~ offset:', offset)

                let query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES} LIMIT ${pageSize} OFFSET ${offset};`

                if (campaignStatus != null && campaignStatus != -1) {
                    query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES} WHERE campaign_status = '${campaignStatus}' LIMIT ${pageSize} OFFSET ${offset};`
                }

                db.transaction(
                    (tx) => {
                        tx.executeSql(query, [campaignStatus, pageSize, offset], (_, result) => {
                            const stores = []
                            for (let i = 0; i < result.rows.length; i++) {
                                stores.push(result.rows.item(i))
                            }
                            resolve(stores)
                        })
                    },
                    (error) => {
                        console.error('Transaction getAllStores error:', error)
                        reject(error)
                    },
                )
            }
        })
    }
    validateCheckins = (schedule_id) => {
        const db = openDatabase()

        console.log('🚀 ~ schedulesService ~ schedule_id:', schedule_id)
        return new Promise((resolve, reject) => {
            if (isWeb()) {
                return
            } else {
                db.transaction(
                    (tx) => {
                        let query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} WHERE schedule_id = ? AND status = 4 ORDER BY date_init DESC`
                        tx.executeSql(
                            query,
                            [schedule_id],
                            (_, result) => {
                                if (result.rows._array.length > 0) {
                                    const schedule_advances = result.rows._array[0]
                                    let res_store = {}
                                    // buscar aca los datos del checkin
                                    try {
                                        if (schedule_advances && schedule_advances.pos_id) {
                                            console.log('1398 -- estoy')
                                            this.getStore(schedule_advances.pos_id).then((store) => {
                                                res_store = store

                                                resolve({
                                                    status: false,
                                                    store: res_store,
                                                    schedule_advances: schedule_advances,
                                                })
                                            })
                                        } else {
                                            resolve({
                                                status: true,
                                                store: {},
                                            })
                                        }
                                    } catch (error) {
                                        console.error('852 --- error getStore: ', error)
                                        resolve({
                                            status: false,
                                            store: {},
                                        })
                                    }
                                } else {
                                    resolve({
                                        status: true,
                                        store: {},
                                    })
                                }
                            },
                            (_, error) => {
                                console.error('Transaction error:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction getAllStores error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    getStoresFromSchedule = (pageNumber, pageSize, scheduleID, campaignStatus, lat, lng, radius, schedule_free, dateSelected) => {
        const db = openDatabase()

        return new Promise(async (resolve, reject) => {
            const fetchStores = async (currentRadius, accumulatedStores = []) => {
                const query = `
                        SELECT pos.*, stores.name, stores.code, stores.address, stores.segment, stores.cluster, stores.frequency,stores.topstore, stores.potencial,
                               adv.forms AS forms_advances, pos.forms_relevant, pos.forms_featured,
                               COALESCE(adv.status, 0) AS campaign_status,
                               adv.incidence_answer
                        FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_POS} AS pos 
                        INNER JOIN ${SCHEDULES_STORE_NAMES.STORES} AS stores 
                        ON pos.store_id = stores._id
                        LEFT JOIN (
                            SELECT adv.pos_id, adv.schedule_id, adv.forms, adv.status, adv.incidence_answer 
                            FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} AS adv
                            INNER JOIN (
                                SELECT pos_id, schedule_id, MAX(date_init) AS latest_date 
                                FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES}
                                GROUP BY pos_id, schedule_id
                            ) AS latest_adv
                            ON adv.pos_id = latest_adv.pos_id 
                            AND adv.schedule_id = latest_adv.schedule_id 
                            AND adv.date_init = latest_adv.latest_date
                        ) AS adv
                        ON pos.store_id = adv.pos_id AND pos.schedule_id = adv.schedule_id 
                        WHERE pos.schedule_id = ?`
                let params = [scheduleID]

                const result = await this.dbExecuteSql(query, params)

                const rows = result.rows._array
                //  console.log("1308 --- 🚀 ~ schedulesService ~ fetchStores ~ rows:", rows)
                const stores = rows.map((item) => ({
                    _id: schedule_free ? item._id : item.store_id,
                    schedule_id: item.schedule_id,
                    name: item.name,
                    code: item.code,
                    address: item.address,
                    forms: item.forms ? JSON.parse(item.forms) : [],
                    campaign_status: item.campaign_status ? item.campaign_status : 0,
                    lat: item.lat,
                    lng: item.lng,
                    forms_relevant: item.forms_relevant ? JSON.parse(item.forms_relevant) : [],
                    forms_featured: item.forms_featured ? JSON.parse(item.forms_featured) : [],
                    segment: item.segment ?? '',
                    forms_advances: item.forms_advances ? JSON.parse(item.forms_advances) : [],
                    cluster: item.cluster ?? '',
                    distance: newcalculateDistance(lat, lng, item.lat, item.lng), // Calcula la distancia
                    incidence_answer: item.incidence_answer || null,
                    frequency: item.frequency || '',
                    topstore: item.topstore || '',
                    potencial: item.potencial || '',
                }))

                accumulatedStores.push(...stores)

                if (campaignStatus !== null && campaignStatus !== -1) {
                    if (campaignStatus === 0) {
                        accumulatedStores = accumulatedStores.filter((item) => item.campaign_status == 0)
                        // Ordenar las tiendas pendientes por distancia
                        accumulatedStores.sort((a, b) => a.distance - b.distance)
                    } else {
                        if (campaignStatus === STORE_STATUS.INCIDENCE_REPORTED) {
                            accumulatedStores = accumulatedStores.filter((item) => {
                                if (item.campaign_status == STORE_STATUS.INCIDENCE_REPORTED) return true
                                if (item.campaign_status == STORE_STATUS.INCIDENCE_REPORTED_OTHER) return true
                                if (item.campaign_status == STORE_STATUS.INCIDENCE_REPORTED_PERMANENTE) return true
                                return false
                            })
                        } else {
                            accumulatedStores = accumulatedStores.filter((item) => item.campaign_status == campaignStatus)
                        }
                    }
                } else {
                    accumulatedStores.sort((a, b) => b.campaign_status - a.campaign_status)
                    // Ordenar las pendientes por distancia
                    const pendingStores = accumulatedStores.filter((item) => item.campaign_status === 0)
                    pendingStores.sort((a, b) => a.distance - b.distance)

                    // Reorganizar acumuladas: primero las no pendientes, luego las pendientes
                    accumulatedStores = [...accumulatedStores.filter((item) => item.campaign_status !== 0), ...pendingStores]
                }

                // Filtrar las tiendas con campaign_status = 3
                const priorityStores = accumulatedStores.filter((item) => item.campaign_status === 4)

                // Filtrar el resto de las tiendas
                const otherStores = accumulatedStores.filter((item) => item.campaign_status !== 4)

                // Ordenar el resto de las tiendas por distancia
                otherStores.sort((a, b) => a.distance - b.distance)

                // Combinar ambas listas: primero las tiendas con campaign_status = 3, luego el resto ordenadas por distancia
                accumulatedStores = [...priorityStores, ...otherStores]

                //@FixMe: devolver todas las tiendas sin paginado y resolver de front como no tildar la vista
                const startIndex = pageNumber * pageSize
                const endIndex = startIndex + pageSize
                const paginatedStores = accumulatedStores.slice(startIndex, endIndex)
                //  console.log("1351 -- 🚀 ~ schedulesService ~ fetchStores ~ paginatedStores:", paginatedStores)

                if (pageSize != 0) {
                    resolve(paginatedStores)
                } else {
                    resolve(accumulatedStores)
                }

                // db.transaction(
                //     (tx) => {
                //         //let query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES}`
                //         // let query = `
                //         //     SELECT pos.*, stores.name, stores.code, stores.address, stores.segment, stores.cluster,
                //         //            adv.forms AS forms_advances, pos.forms_relevant, pos.forms_featured,
                //         //            COALESCE(adv.status, 0) AS campaign_status
                //         //     FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_POS} AS pos
                //         //     INNER JOIN ${SCHEDULES_STORE_NAMES.STORES} AS stores
                //         //     ON pos.store_id = stores._id
                //         //     INNER JOIN ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} AS adv
                //         //     ON pos.store_id = adv.pos_id AND pos.schedule_id = adv.schedule_id
                //         //     WHERE pos.schedule_id = ? `;
                //         let query = `
                //         SELECT pos.*, stores.name, stores.code, stores.address, stores.segment, stores.cluster,
                //                adv.forms AS forms_advances, pos.forms_relevant, pos.forms_featured,
                //                COALESCE(adv.status, 0) AS campaign_status
                //         FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_POS} AS pos
                //         INNER JOIN ${SCHEDULES_STORE_NAMES.STORES} AS stores
                //         ON pos.store_id = stores._id
                //         LEFT JOIN (
                //             SELECT adv.pos_id, adv.schedule_id, adv.forms, adv.status
                //             FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} AS adv
                //             INNER JOIN (
                //                 SELECT pos_id, schedule_id, MAX(date_modified) AS latest_date
                //                 FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES}
                //                 GROUP BY pos_id, schedule_id
                //             ) AS latest_adv
                //             ON adv.pos_id = latest_adv.pos_id
                //             AND adv.schedule_id = latest_adv.schedule_id
                //             AND adv.date_modified = latest_adv.latest_date
                //         ) AS adv
                //         ON pos.store_id = adv.pos_id AND pos.schedule_id = adv.schedule_id
                //         WHERE pos.schedule_id = ?`

                //         let params = [scheduleID]

                //         tx.executeSql(
                //             query,
                //             params,
                //             (_, result) => {
                //                 const rows = result.rows._array
                //                 //  console.log("1308 --- 🚀 ~ schedulesService ~ fetchStores ~ rows:", rows)

                //                 const stores = rows.map((item) => ({
                //                     _id: schedule_free ? item._id : item.store_id,
                //                     schedule_id: item.schedule_id,
                //                     name: item.name,
                //                     code: item.code,
                //                     address: item.address,
                //                     forms: item.forms ? JSON.parse(item.forms) : [],
                //                     campaign_status: item.campaign_status ? item.campaign_status : 0,
                //                     lat: item.lat,
                //                     lng: item.lng,
                //                     forms_relevant: item.forms_relevant ? JSON.parse(item.forms_relevant) : [],
                //                     forms_featured: item.forms_featured ? JSON.parse(item.forms_featured) : [],
                //                     segment: item.segment ?? '',
                //                     forms_advances: item.forms_advances ? JSON.parse(item.forms_advances) : [],
                //                     cluster: item.cluster ?? '',
                //                     distance: newcalculateDistance(lat, lng, item.lat, item.lng), // Calcula la distancia
                //                 }))

                //                 accumulatedStores.push(...stores)

                //                 if (campaignStatus !== null && campaignStatus !== -1) {
                //                     if (campaignStatus === 0) {
                //                         accumulatedStores = accumulatedStores.filter((item) => item.campaign_status == 0)
                //                         // Ordenar las tiendas pendientes por distancia
                //                         accumulatedStores.sort((a, b) => a.distance - b.distance)
                //                     } else {
                //                         accumulatedStores = accumulatedStores.filter((item) => item.campaign_status == campaignStatus)
                //                     }
                //                 } else {
                //                     accumulatedStores.sort((a, b) => b.campaign_status - a.campaign_status)
                //                     // Ordenar las pendientes por distancia
                //                     const pendingStores = accumulatedStores.filter((item) => item.campaign_status === 0)
                //                     pendingStores.sort((a, b) => a.distance - b.distance)

                //                     // Reorganizar acumuladas: primero las no pendientes, luego las pendientes
                //                     accumulatedStores = [...accumulatedStores.filter((item) => item.campaign_status !== 0), ...pendingStores]
                //                 }

                //                 // Filtrar las tiendas con campaign_status = 3
                //                 const priorityStores = accumulatedStores.filter((item) => item.campaign_status === 4)

                //                 // Filtrar el resto de las tiendas
                //                 const otherStores = accumulatedStores.filter((item) => item.campaign_status !== 4)

                //                 // Ordenar el resto de las tiendas por distancia
                //                 otherStores.sort((a, b) => a.distance - b.distance)

                //                 // Combinar ambas listas: primero las tiendas con campaign_status = 3, luego el resto ordenadas por distancia
                //                 accumulatedStores = [...priorityStores, ...otherStores]

                //                 //@FixMe: devolver todas las tiendas sin paginado y resolver de front como no tildar la vista
                //                 const startIndex = pageNumber * pageSize
                //                 const endIndex = startIndex + pageSize
                //                 const paginatedStores = accumulatedStores.slice(startIndex, endIndex)
                //                 //  console.log("1351 -- 🚀 ~ schedulesService ~ fetchStores ~ paginatedStores:", paginatedStores)

                //                 if (pageSize != 0) {
                //                     resolve(paginatedStores)
                //                 } else {
                //                     resolve(accumulatedStores)
                //                 }
                //             },
                //             (_, error) => {
                //                 console.error('Query Execution Error:', error)
                //                 reject(error)
                //             },
                //         )
                //     },
                //     (error) => {
                //         console.error('Transaction Error:', error)
                //         reject(error)
                //     },

                // )
            }

            fetchStores(radius)
        })
    }
    searchStoresFromSchedule = (pageNumber, pageSize, scheduleID, campaignStatus, lat, lng, radius, schedule_free, dateSelected) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            const fetchStores = (currentRadius, accumulatedStores = []) => {
                db.transaction(
                    (tx) => {
                        let query = `
                        SELECT pos.*, stores.name, stores.code, stores.address, stores.segment, stores.cluster, 
                               adv.forms AS forms_advances, pos.forms_relevant, pos.forms_featured, 
                               COALESCE(adv.status, 0) AS campaign_status
                        FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_POS} AS pos 
                        INNER JOIN ${SCHEDULES_STORE_NAMES.STORES} AS stores 
                        ON pos.store_id = stores._id
                        LEFT JOIN (
                            SELECT adv.pos_id, adv.schedule_id, adv.forms, adv.status 
                            FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} AS adv
                            INNER JOIN (
                                SELECT pos_id, schedule_id, MAX(date_modified) AS latest_date 
                                FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES}
                                GROUP BY pos_id, schedule_id
                            ) AS latest_adv
                            ON adv.pos_id = latest_adv.pos_id 
                            AND adv.schedule_id = latest_adv.schedule_id 
                            AND adv.date_modified = latest_adv.latest_date
                        ) AS adv
                        ON pos.store_id = adv.pos_id AND pos.schedule_id = adv.schedule_id 
                        WHERE pos.schedule_id = ?`

                        let params = [scheduleID]

                        tx.executeSql(
                            query,
                            params,
                            (_, result) => {
                                const rows = result.rows._array
                                //  console.log("🚀 ~ schedulesService ~ fetchStores ~ rows:", rows.length)

                                const stores = rows.map((item) => ({
                                    _id: schedule_free ? item._id : item.store_id,
                                    schedule_id: item.schedule_id,
                                    name: item.name,
                                    code: item.code,
                                    address: item.address,
                                    forms: item.forms ? JSON.parse(item.forms) : [],
                                    campaign_status: item.campaign_status ? item.campaign_status : 0,
                                    lat: item.lat,
                                    lng: item.lng,
                                    forms_relevant: item.forms_relevant ? JSON.parse(item.forms_relevant) : [],
                                    forms_featured: item.forms_featured ? JSON.parse(item.forms_featured) : [],
                                    segment: item.segment ?? '',
                                    forms_advances: item.forms_advances ? JSON.parse(item.forms_advances) : [],
                                    cluster: item.cluster ?? '',
                                    distance: newcalculateDistance(lat, lng, item.lat, item.lng), // Calcula la distancia
                                    frequency: item.frequency || '',
                                    topstore: item.topstore || '',
                                    potencial: item.potencial || '',
                                }))

                                accumulatedStores.push(...stores)

                                if (campaignStatus !== null && campaignStatus !== -1) {
                                    if (campaignStatus === 0) {
                                        accumulatedStores = accumulatedStores.filter((item) => item.campaign_status == 0)
                                        // Ordenar las tiendas pendientes por distancia
                                        accumulatedStores.sort((a, b) => a.distance - b.distance)
                                    } else {
                                        accumulatedStores = accumulatedStores.filter((item) => item.campaign_status == campaignStatus)
                                    }
                                } else {
                                    accumulatedStores.sort((a, b) => b.campaign_status - a.campaign_status)
                                    // Ordenar las pendientes por distancia
                                    const pendingStores = accumulatedStores.filter((item) => item.campaign_status === 0)
                                    pendingStores.sort((a, b) => a.distance - b.distance)

                                    // Reorganizar acumuladas: primero las no pendientes, luego las pendientes
                                    accumulatedStores = [...accumulatedStores.filter((item) => item.campaign_status !== 0), ...pendingStores]
                                }
                                resolve(accumulatedStores)
                            },
                            (_, error) => {
                                console.error('Query Execution Error:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction Error:', error)
                        reject(error)
                    },
                )
            }

            fetchStores(radius)
        })
    }
    searchStoresByText = (scheduleID, searchText) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    let params = [scheduleID]
                    let query = `
                   
                    WITH filtered_stores AS (
                        SELECT _id, name, code, address, segment, cluster 
                        FROM ${SCHEDULES_STORE_NAMES.STORES}
                        WHERE LOWER(name) LIKE LOWER(?) 
                        OR LOWER(code) LIKE LOWER(?) 
                        OR LOWER(address) LIKE LOWER(?)
                    )
                    SELECT pos.*, fs.name, fs.code, fs.address, fs.segment, fs.cluster, 
                           adv.forms AS forms_advances, pos.forms_relevant, pos.forms_featured, 
                           COALESCE(adv.status, 0) AS campaign_status
                    FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_POS} AS pos 
                    INNER JOIN filtered_stores AS fs ON pos.store_id = fs._id
                    LEFT JOIN (
                        SELECT adv.pos_id, adv.schedule_id, adv.forms, adv.status 
                        FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} AS adv
                        INNER JOIN (
                            SELECT pos_id, schedule_id, MAX(date_modified) AS latest_date 
                            FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES}
                            GROUP BY pos_id, schedule_id
                        ) AS latest_adv
                        ON adv.pos_id = latest_adv.pos_id 
                        AND adv.schedule_id = latest_adv.schedule_id 
                        AND adv.date_modified = latest_adv.latest_date
                    ) AS adv
                    ON pos.store_id = adv.pos_id AND pos.schedule_id = adv.schedule_id 
                    WHERE pos.schedule_id = ?;`

                    if (searchText && searchText.trim() !== '') {
                        params.unshift(`%${searchText}%`, `%${searchText}%`, `%${searchText}%`)
                    }

                    tx.executeSql(
                        query,
                        params,
                        (_, result) => {
                            const rows = result.rows._array
                            const stores = rows.map((item) => ({
                                _id: item.store_id,
                                schedule_id: item.schedule_id,
                                name: item.name,
                                code: item.code,
                                address: item.address,
                                forms: item.forms ? JSON.parse(item.forms) : [],
                                campaign_status: item.campaign_status ?? 0,
                                segment: item.segment ?? '',
                                cluster: item.cluster ?? '',
                            }))

                            resolve(stores)
                        },
                        (_, error) => {
                            console.error('Query Execution Error:', error)
                            reject(error)
                        },
                    )
                },
                (error) => {
                    console.error('Transaction Error:', error)
                    reject(error)
                },
            )
        })
    }

    deleteAllAdvanceSchedule = () => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
            } else {
                db.transaction(
                    (tx) => {
                        tx.executeSql(`SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES};`, [], (_, result) => {
                            const count = result.rows.item(0).count
                            if (count > 0) {
                                tx.executeSql(`DELETE FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES};`)
                            }
                        })
                        tx.executeSql(`SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.CHECKIN};`, [], (_, result) => {
                            const count = result.rows.item(0).count
                            if (count > 0) {
                                tx.executeSql(`DELETE FROM ${SCHEDULES_STORE_NAMES.CHECKIN};`)
                            }
                        })
                        tx.executeSql(`SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.CHECKOUT};`, [], (_, result) => {
                            const count = result.rows.item(0).count
                            if (count > 0) {
                                tx.executeSql(`DELETE FROM ${SCHEDULES_STORE_NAMES.CHECKOUT};`)
                            }
                        })
                    },
                    null,
                    () => {
                        console.log('590 La tabla schdeule advance eliminada exitosamente')
                        resolve()
                    },
                    (error) => {
                        console.log('🚀 ~ returnnewPromise ~ error:', error)
                        console.error('595 Error al eliminar las tablas psoteos:', error)
                        reject(error)
                    },
                )
            }
        })
    }
    deleteAllTablesSchedule = () => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
            } else {
                db.transaction(
                    (tx) => {
                        tx.executeSql(`SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.CAMPAINGS};`, [], (_, result) => {
                            const count = result.rows.item(0).count
                            if (count > 0) {
                                tx.executeSql(`DELETE FROM ${SCHEDULES_STORE_NAMES.CAMPAINGS};`)
                            }
                        })
                        tx.executeSql(`SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.SCHEDULES};`, [], (_, result) => {
                            const count = result.rows.item(0).count
                            if (count > 0) {
                                tx.executeSql(`DELETE FROM ${SCHEDULES_STORE_NAMES.SCHEDULES};`)
                            }
                        })
                        tx.executeSql(`SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_POS};`, [], (_, result) => {
                            const count = result.rows.item(0).count
                            if (count > 0) {
                                tx.executeSql(`DELETE FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_POS};`)
                            }
                        })
                        tx.executeSql(`SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES};`, [], (_, result) => {
                            const count = result.rows.item(0).count
                            if (count > 0) {
                                tx.executeSql(`DELETE FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES};`)
                            }
                        })
                        tx.executeSql(`SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.FORMS};`, [], (_, result) => {
                            const count = result.rows.item(0).count
                            if (count > 0) {
                                tx.executeSql(`DELETE FROM ${SCHEDULES_STORE_NAMES.FORMS};`)
                            }
                        })
                        tx.executeSql(`SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.STORES};`, [], (_, result) => {
                            const count = result.rows.item(0).count
                            if (count > 0) {
                                tx.executeSql(`DELETE FROM ${SCHEDULES_STORE_NAMES.STORES};`)
                            }
                        })
                        tx.executeSql(`SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.FORMS_ADDITIONALS};`, [], (_, result) => {
                            const count = result.rows.item(0).count
                            if (count > 0) {
                                tx.executeSql(`DELETE FROM ${SCHEDULES_STORE_NAMES.FORMS_ADDITIONALS};`)
                            }
                        })
                    },
                    null,
                    () => {
                        console.log('590 La tabla schdeule advance eliminada exitosamente')
                        resolve()
                    },
                    (error) => {
                        console.log('🚀 ~ returnnewPromise ~ error:', error)
                        console.error('595 Error al eliminar las tablas psoteos:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    add_checkin = (data, checkin_id = '', distributor_id = '') => {
        const db = openDatabase()

        // console.log('🚀 ~ schedulesService ~ schedule:', data)
        if (data) {
            return new Promise((resolve, reject) => {
                if (isWeb()) {
                    return
                } else {
                    db.transaction(
                        (tx) => {
                            const query = `INSERT INTO ${SCHEDULES_STORE_NAMES.CHECKIN} (
                                date,
                                store_id,
                                lat_checkin,
                                lng_checkin,
                                checkin_id,
                                schedule_id,
                                date_init,
                                dif_utc,
                                status,
                                distributor_id,
                                objectives,
                                steps_distributor,
                                formsIdsFqa
                            ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`
                            const params = [
                                data.date,
                                data.store_id,
                                data.lat_checkin,
                                data.lng_checkin,
                                checkin_id,
                                data.schedule_id,
                                data.date_init,
                                data.dif_utc,
                                data.status,
                                distributor_id,
                                JSON.stringify(data.objectives || []),
                                JSON.stringify(data.steps_distributor || []),
                                JSON.stringify(data.formsIdsFqa || []),
                            ]

                            tx.executeSql(
                                query,
                                params,
                                (_, result) => {
                                    // console.log('🚀 ~ se guardo el checkin', result)
                                    resolve(result)
                                },
                                (_, error) => {
                                    console.error('Transaction error:', error)
                                    reject(error)
                                },
                            )
                        },
                        (error) => {
                            console.error('Transaction error:', error)
                            reject(error)
                        },
                    )
                }
            })
        } else {
        }
    }

    add_checkin_steps = (data) => {
        const db = openDatabase()

        // console.log('🚀 ~ schedulesService ~ schedule:', data)
        if (data) {
            return new Promise((resolve, reject) => {
                if (isWeb()) {
                    return
                } else {
                    db.transaction(
                        (tx) => {
                            const query = `INSERT INTO ${SCHEDULES_STORE_NAMES.CHECKIN_STEPS} (
                                date,
                                store_id,
                                schedule_id
                            ) VALUES (?, ?, ?)`
                            const params = [data.date, data.store_id, data.schedule_id]

                            tx.executeSql(
                                query,
                                params,
                                (_, result) => {
                                    // console.log('🚀 ~ se guardo el checkin', result)
                                    resolve(result)
                                },
                                (_, error) => {
                                    console.error('Transaction error:', error)
                                    reject(error)
                                },
                            )
                        },
                        (error) => {
                            console.error('Transaction error:', error)
                            reject(error)
                        },
                    )
                }
            })
        } else {
        }
    }

    updateCheckinActivities = (scheduleId, distributorId, activityId, comment) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                return
            } else {
                db.transaction(
                    (tx) => {
                        // 1. Buscar el registro existente
                        tx.executeSql(
                            `SELECT activities, checkin_id FROM ${SCHEDULES_STORE_NAMES.CHECKIN}
                             WHERE schedule_id = ? AND distributor_id = ?`,
                            [scheduleId, distributorId],
                            (_, result) => {
                                if (result.rows.length > 0) {
                                    // 2. Parsear el campo activities
                                    const existingRecord = result.rows.item(0)
                                    let activities = JSON.parse(existingRecord.activities || '[]')

                                    // 3. Agregar la nueva actividad
                                    let findActivity = activities.find((a) => a.activity_id === activityId)
                                    if (findActivity) {
                                        // Si la actividad ya existe, actualiza el comentario
                                        findActivity.comment = comment
                                    } else {
                                        // Si la actividad no existe, agrégala
                                        activities.push({ activity_id: activityId, comment: comment })
                                    }

                                    // 4. Stringificar el campo activities
                                    const updatedActivities = JSON.stringify(activities)

                                    // Actualizar el registro con el nuevo campo activities
                                    const updateQuery = `UPDATE ${SCHEDULES_STORE_NAMES.CHECKIN}
                                                         SET activities = ?
                                                         WHERE schedule_id = ? AND distributor_id = ?`

                                    tx.executeSql(
                                        updateQuery,
                                        [updatedActivities, scheduleId, distributorId],
                                        (_, updateResult) => {
                                            console.log('Registro actualizado:', updateResult)
                                            let data_update = existingRecord
                                            data_update.activities = activities
                                            resolve(data_update)
                                        },
                                        (_, updateError) => {
                                            console.error('Error al actualizar el registro:', updateError)
                                            reject(updateError)
                                        },
                                    )
                                } else {
                                    // Si no se encontró ningún registro
                                    console.log('No se encontró el check-in para actualizar')
                                    reject(new Error('No se encontró el check-in para actualizar'))
                                }
                            },
                            (_, error) => {
                                console.error('Error al buscar el registro:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Error en la transacción:', error)
                        reject(error)
                    },
                )
            }
        })
    }
    updateScheduleActivities = (scheduleId, activityId, comment) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                return
            } else {
                db.transaction(
                    (tx) => {
                        // 1. Buscar el registro existente
                        tx.executeSql(
                            `SELECT activities FROM ${SCHEDULES_STORE_NAMES.SCHEDULE_ACTIVITIES_ADVANCES}
                             WHERE schedule_id = ?`,
                            [scheduleId],
                            (_, result) => {
                                if (result.rows.length > 0) {
                                    // 2. Parsear el campo activities
                                    const existingRecord = result.rows.item(0)
                                    let activities = JSON.parse(existingRecord.activities || '[]')

                                    // 3. Agregar la nueva actividad o actualizarla
                                    let findActivity = activities.find((a) => a.activity_id === activityId)
                                    if (findActivity) {
                                        // Si la actividad ya existe, actualiza el comentario
                                        findActivity.comment = comment
                                    } else {
                                        // Si la actividad no existe, agrégala
                                        activities.push({ activity_id: activityId, comment: comment })
                                    }

                                    // 4. Stringificar el campo activities
                                    const updatedActivities = JSON.stringify(activities)

                                    // Actualizar el registro con el nuevo campo activities
                                    const updateQuery = `UPDATE ${SCHEDULES_STORE_NAMES.SCHEDULE_ACTIVITIES_ADVANCES}
                                                         SET activities = ?
                                                         WHERE schedule_id = ?`

                                    tx.executeSql(
                                        updateQuery,
                                        [updatedActivities, scheduleId],
                                        (_, updateResult) => {
                                            console.log('Registro actualizado:', updateResult)
                                            resolve(updateResult)
                                        },
                                        (_, updateError) => {
                                            console.error('Error al actualizar el registro:', updateError)
                                            reject(updateError)
                                        },
                                    )
                                } else {
                                    // Si no se encontró ningún registro, crear uno nuevo
                                    const newActivities = JSON.stringify([{ activity_id: activityId, comment: comment }])
                                    const insertQuery = `INSERT INTO ${SCHEDULES_STORE_NAMES.SCHEDULE_ACTIVITIES_ADVANCES}
                                                         (_id, schedule_id, activities, status, date)
                                                         VALUES (?, ?, ?, ?, ?)`

                                    tx.executeSql(
                                        insertQuery,
                                        [scheduleId, scheduleId, newActivities, 1, new Date().toISOString()],
                                        (_, insertResult) => {
                                            console.log('Registro creado:', insertResult)
                                            resolve(insertResult)
                                        },
                                        (_, insertError) => {
                                            console.error('Error al crear el registro:', insertError)
                                            reject(insertError)
                                        },
                                    )
                                }
                            },
                            (_, error) => {
                                console.error('Error al buscar el registro:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Error en la transacción:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    get_checkin_steps = (store_id, schedule_id) => {
        const db = openDatabase()

        console.log('🚀 ~ schedulesService ~ get_checkin_steps for store_id:', store_id, 'and schedule_id:', schedule_id)
        return new Promise((resolve, reject) => {
            if (isWeb()) {
                const transaction = db.transaction([SCHEDULES_STORE_NAMES.CHECKIN_STEPS], 'readonly')
                const objectStore = transaction.objectStore(SCHEDULES_STORE_NAMES.CHECKIN_STEPS)

                const index = objectStore.index('store_id_schedule_id')
                const range = IDBKeyRange.bound([store_id, schedule_id], [store_id, schedule_id])
                const request = index.openCursor(range)

                request.onsuccess = (event) => {
                    const cursor = event.target.result
                    if (cursor) {
                        const checkin = cursor.value
                        console.log('🚀 ~ checkin steps found for store_id and schedule_id:', checkin)
                        resolve(checkin)
                    } else {
                        console.log('🚀 ~ no checkin steps found for store_id and schedule_id')
                        resolve(false)
                    }
                }

                request.onerror = (event) => {
                    console.error('Transaction error:', event.target.error)
                    reject(event.target.error)
                }
            } else {
                db.transaction(
                    (tx) => {
                        const query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.CHECKIN_STEPS} WHERE store_id = ? AND schedule_id = ?`
                        const params = [store_id, schedule_id]

                        tx.executeSql(
                            query,
                            params,
                            (_, result) => {
                                if (result.rows._array.length > 0) {
                                    console.log('🚀 ~ checkin steps found for store_id and schedule_id:', result.rows._array)
                                    resolve(result.rows._array)
                                } else {
                                    console.log('🚀 ~ no checkin steps found for store_id and schedule_id:', result)
                                    resolve(false)
                                }
                            },
                            (_, error) => {
                                console.error('Transaction error:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    get_checkin = (store_id, schedule_id, distributor = false) => {
        const db = openDatabase()

        // console.log('🚀 ~ schedulesService ~ get_checkin for store_id:', store_id, 'and schedule_id:', schedule_id)
        return new Promise((resolve, reject) => {
            if (isWeb()) {
                const transaction = db.transaction([SCHEDULES_STORE_NAMES.CHECKIN], 'readonly')
                const objectStore = transaction.objectStore(SCHEDULES_STORE_NAMES.CHECKIN)

                const index = objectStore.index('store_id_schedule_id') // Suponiendo que tienes un índice compuesto por store_id y schedule_id
                const range = IDBKeyRange.bound([store_id, schedule_id], [store_id, schedule_id])
                const request = index.openCursor(range)

                request.onsuccess = (event) => {
                    const cursor = event.target.result
                    if (cursor) {
                        const checkin = cursor.value
                        // console.log('🚀 ~ checkin found for store_id and schedule_id:', checkin)
                        resolve(checkin)
                    } else {
                        console.log('🚀 ~ no checkin found for store_id and schedule_id')
                        resolve(false)
                    }
                }

                request.onerror = (event) => {
                    console.error('Transaction error:', event.target.error)
                    reject(event.target.error)
                }
            } else {
                db.transaction(
                    (tx) => {
                        const query = distributor
                            ? `SELECT * FROM ${SCHEDULES_STORE_NAMES.CHECKIN} WHERE distributor_id = ? AND schedule_id = ?`
                            : `SELECT * FROM ${SCHEDULES_STORE_NAMES.CHECKIN} WHERE store_id = ? AND schedule_id = ?`
                        const params = [store_id, schedule_id]

                        tx.executeSql(
                            query,
                            params,
                            (_, result) => {
                                // console.log('🚀 ~ schedulesService ~ returnnewPromise ~ result:', result)
                                if (result.rows._array.length > 0) {
                                    // console.log('🚀 ~ checkin found for store_id and schedule_id:', result.rows._array)
                                    const rows = result.rows._array

                                    const check_ins = rows.map((item) => ({
                                        ...item,
                                        activities: item.activities ? JSON.parse(item.activities) : [],
                                    }))
                                    resolve(check_ins)
                                } else {
                                    // console.log('🚀 ~ no checkin found for store_id and schedule_id:', result)
                                    resolve(false)
                                }
                            },
                            (_, error) => {
                                console.error('Transaction error:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction error:', error)
                        reject(error)
                    },
                )
            }
        })
    }
    get_advances_distributor = (distributor_id, schedule_id) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
            } else {
                db.transaction(
                    (tx) => {
                        const query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} WHERE distributor_id = ? AND schedule_id = ?`
                        const params = [distributor_id, schedule_id]

                        tx.executeSql(
                            query,
                            params,
                            (_, result) => {
                                if (result.rows._array.length > 0) {
                                    const rows = result.rows._array
                                    resolve(rows)
                                } else {
                                    resolve(false)
                                }
                            },
                            (_, error) => {
                                console.error('Transaction error: get_advances_distributor', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction error: get_advances_distributor', error)
                        reject(error)
                    },
                )
            }
        })
    }
    get_checkins_by_id = (id) => {
        const db = openDatabase()

        // console.log('🚀 ~ schedulesService ~ get_checkin for store_id:', store_id, 'and schedule_id:', schedule_id)
        return new Promise((resolve, reject) => {
            if (isWeb()) {
            } else {
                db.transaction(
                    (tx) => {
                        const query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.CHECKIN} WHERE _id =?`
                        const params = [parseInt(id)]

                        tx.executeSql(
                            query,
                            params,
                            (_, result) => {
                                console.log('🚀 ~ schedulesService ~ returnnewPromise ~ result:', result)
                                // console.log('🚀 ~ schedulesService ~ returnnewPromise ~ result:', result)
                                if (result.rows._array.length > 0) {
                                    resolve(result.rows._array[0])
                                } else {
                                    resolve(false)
                                }
                            },
                            (_, error) => {
                                console.error('Transaction error:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction error:', error)
                        reject(error)
                    },
                )
            }
        })
    }
    get_checkout_by_id = (checkin_id_local) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
            } else {
                db.transaction(
                    (tx) => {
                        const query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.CHECKOUT} WHERE checkin_id_local = ? `
                        const params = [checkin_id_local.toString()]

                        tx.executeSql(
                            query,
                            params,
                            (_, result) => {
                                console.log('checkout result:', result)
                                if (result.rows._array.length > 0) {
                                    const modifiedCheckout = {
                                        ...result.rows._array[0],
                                        forms_view: JSON.parse(result.rows._array[0]['forms_view']),
                                    }
                                    console.log('modifiedCheckout => ', modifiedCheckout)
                                    resolve(modifiedCheckout)
                                } else {
                                    resolve(false)
                                }
                            },
                            (_, error) => {
                                console.error('Transaction error:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    update_checkout_key_checkinID = (checkin_id_local, checkin_id) => {
        const db = openDatabase()
        return new Promise((resolve, reject) => {
            if (isWeb()) {
            } else {
                db.transaction(
                    (tx) => {
                        const query = `UPDATE ${SCHEDULES_STORE_NAMES.CHECKOUT} SET checkin_id = ? WHERE checkin_id_local = ?`
                        const params = [checkin_id.toString(), checkin_id_local.toString()]

                        tx.executeSql(
                            query,
                            params,
                            (_, result) => {
                                console.log('Checkout updated successfully:', result)
                                resolve(true)
                            },
                            (_, error) => {
                                console.error('Transaction error:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    get_checkins_pending = (status_pending_connection) => {
        const db = openDatabase()

        // console.log('🚀 ~ schedulesService ~ get_checkin for store_id:', store_id, 'and schedule_id:', schedule_id)
        return new Promise((resolve, reject) => {
            if (isWeb()) {
            } else {
                db.transaction(
                    (tx) => {
                        const query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.CHECKIN} WHERE status =?`
                        const params = [status_pending_connection]

                        tx.executeSql(
                            query,
                            params,
                            (_, result) => {
                                // console.log('🚀 ~ schedulesService ~ returnnewPromise ~ result:', result)
                                if (result.rows._array.length > 0) {
                                    resolve(result.rows._array)
                                } else {
                                    resolve([])
                                }
                            },
                            (_, error) => {
                                console.error('Transaction error:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction error:', error)
                        reject(error)
                    },
                )
            }
        })
    }
    get_checkouts_pending = (status_pending_connection) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
            } else {
                db.transaction(
                    (tx) => {
                        const query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.CHECKOUT} WHERE status =?`
                        const params = [status_pending_connection]

                        tx.executeSql(
                            query,
                            params,
                            (_, result) => {
                                console.log('result.rows._array: ', result.rows._array)
                                if (result.rows._array.length > 0) {
                                    const updateResult = result.rows._array.map((checkout) => {
                                        return {
                                            ...checkout,
                                            forms_view: JSON.parse(checkout.forms_view),
                                        }
                                    })
                                    resolve(updateResult)
                                } else {
                                    resolve([])
                                }
                            },
                            (_, error) => {
                                console.error('Transaction error:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    get_newstores_pending = (status_pending_connection) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
            } else {
                db.transaction(
                    (tx) => {
                        // Primera consulta: obtener registros pendientes de la tabla SCHEDULES_POS
                        const posQuery = `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_POS} WHERE campaign_status = ?`
                        const posParams = [status_pending_connection]

                        tx.executeSql(
                            posQuery,
                            posParams,
                            (_, posResult) => {
                                const pendingPos = posResult.rows._array

                                if (pendingPos.length > 0) {
                                    // Segunda consulta: obtener detalles de las tiendas de la tabla STORES
                                    const storeIds = pendingPos.map((item) => `'${item.store_id}'`).join(',')
                                    const storeQuery = `SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES} WHERE _id IN (${storeIds})`

                                    tx.executeSql(
                                        storeQuery,
                                        [],
                                        (_, storeResult) => {
                                            const storesData = storeResult.rows._array

                                            // Combinar los datos de ambas tablas en un solo array
                                            const combinedData = pendingPos.map((pos) => {
                                                let store = storesData.find((store) => store._id === pos.store_id)
                                                if (store) {
                                                    store = { ...store, schedule_id: pos.schedule_id }
                                                }
                                                return {
                                                    ...pos,
                                                    storeDetails: store || {},
                                                }
                                            })

                                            resolve(combinedData)
                                        },
                                        (_, error) => {
                                            console.error('Error al obtener datos de STORES:', error)
                                            reject(error)
                                        },
                                    )
                                } else {
                                    resolve([])
                                }
                            },
                            (_, error) => {
                                console.error('Error al obtener datos de SCHEDULES_POS:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    updateCheckinStatus = (_id, status, checkin_id) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                // Implementar para la web si es necesario
            } else {
                db.transaction(
                    (tx) => {
                        const query = `UPDATE ${SCHEDULES_STORE_NAMES.CHECKIN} SET status = ?, checkin_id = ? WHERE _id = ?`
                        const params = [status, checkin_id, _id]

                        tx.executeSql(
                            query,
                            params,
                            (_, result) => {
                                if (result.rowsAffected > 0) {
                                    console.log(`🚀 ~ Check-in with id ${_id} updated successfully`)
                                    resolve(true)
                                } else {
                                    console.log(`🚀 ~ No check-in found with id ${_id} to update`)
                                    resolve(false)
                                }
                            },
                            (_, error) => {
                                console.error('Transaction error:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction error:', error)
                        reject(error)
                    },
                )
            }
        })
    }
    updateCheckoutStatus = (_id, status) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                // Implementar para la web si es necesario
            } else {
                db.transaction(
                    (tx) => {
                        const query = `UPDATE ${SCHEDULES_STORE_NAMES.CHECKOUT} SET status = ? WHERE _id = ?`
                        const params = [status, _id]

                        tx.executeSql(
                            query,
                            params,
                            (_, result) => {
                                if (result.rowsAffected > 0) {
                                    console.log(`🚀 ~ Checkout updated successfully`)
                                    resolve(true)
                                } else {
                                    console.log(`🚀 ~ No checkout to update`)
                                    resolve(false)
                                }
                            },
                            (_, error) => {
                                console.error('Transaction error:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    add_checkout = (data, checkout_id = '', distributor_id = '') => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                return
            } else {
                db.transaction(
                    (tx) => {
                        const query = `INSERT INTO ${SCHEDULES_STORE_NAMES.CHECKOUT} (
                                date,
                                store_id,
                                lat_checkout,
                                lng_checkout,
                                checkout_id,
                                checkin_id,
                                schedule_id,
                                date_finish,
                                dif_utc, 
                                status,
                                checkin_id_local,
                                distributor_id,
                                forms_view
                            ) VALUES (?, ?, ?, ?, ?, ?, ?,?,?,?,?,?,?)`
                        const params = [
                            data.date,
                            data.store_id,
                            data.lat_checkout,
                            data.lng_checkout,
                            checkout_id,
                            data.checkin_id,
                            data.schedule_id,
                            data.date_finish,
                            data.dif_utc,
                            data.status,
                            data.checkin_id_local,
                            distributor_id,
                            data.forms_view,
                        ]

                        tx.executeSql(
                            query,
                            params,
                            (_, result) => {
                                // console.log('🚀 ~ se guardo el checkout', result)
                                //   console.log('🚀 ~ se guardo el checkout', result)
                                resolve(result)
                            },
                            (_, error) => {
                                console.error('Transaction error:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    update_status_store = (storeId, scheduleId, newStatus, dateInit, distributor = false) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    // Primero intentamos actualizar el registro si existe
                    let updateQuery = `UPDATE ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES}
                    SET status = ?
                    WHERE pos_id = ? AND schedule_id = ?;`

                    if (distributor) {
                        updateQuery = `UPDATE ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES}
                             SET status = ?
                             WHERE distributor_id = ? AND schedule_id = ? AND status = ${FORM_SCHEDULE_STATUS.IN_PROGRESS};`
                    }

                    tx.executeSql(
                        updateQuery,
                        [newStatus, storeId, scheduleId],
                        (_, result) => {
                            if (result.rowsAffected == 0) {
                                // Si no se encontró ningún registro para actualizar, insertamos uno nuevo
                                const insertQuery = distributor
                                    ? `INSERT INTO ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES}
                                (_id, distributor_id, schedule_id, status, date_init, date_modified)
                                VALUES (?,?,?,?,?,?);`
                                    : `INSERT INTO ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES}
                                                     (_id, pos_id, schedule_id, status, date_init, date_modified)
                                                     VALUES (?,?,?,?,?,?);`
                                const newId = generateUniqueId() // Genera un nuevo ID único
                                // console.log('🚀 ~ schedulesService ~ returnnewPromise ~ newId:', newId)
                                tx.executeSql(
                                    insertQuery,
                                    [newId, storeId, scheduleId, newStatus, dateInit, new Date().toISOString()],
                                    (_, insertResult) => {
                                        //console.log('🚀 ~ schedulesService ~ returnnewPromise ~ insertResult:', insertResult)
                                        resolve(insertResult)
                                    },
                                    (_, insertError) => {
                                        console.error(insertError)
                                        reject(insertError)
                                    },
                                )
                            } else {
                                resolve(result)
                            }
                        },
                        (_, updateError) => {
                            console.error(updateError)
                            reject(updateError)
                        },
                    )
                },
                (transactionError) => {
                    console.error(transactionError)
                    reject(transactionError)
                },
            )
        })
    }

    get_checkout = (store_id, schedule_id, distributor_id = false) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                const transaction = db.transaction([SCHEDULES_STORE_NAMES.CHECKOUT], 'readonly')
                const objectStore = transaction.objectStore(SCHEDULES_STORE_NAMES.CHECKOUT)

                const index = objectStore.index('store_id_schedule_id') // Suponiendo que tienes un índice compuesto por store_id y schedule_id
                const range = IDBKeyRange.bound([store_id, schedule_id], [store_id, schedule_id])
                const request = index.openCursor(range)

                request.onsuccess = (event) => {
                    const cursor = event.target.result
                    if (cursor) {
                        const checkout = cursor.value
                        console.log('🚀 ~ CHECKOUT found for store_id and schedule_id:', checkout)
                        resolve(checkout)
                    } else {
                        console.log('🚀 ~ no CHECKOUT found for store_id and schedule_id')
                        resolve(false)
                    }
                }

                request.onerror = (event) => {
                    console.error('Transaction error:', event.target.error)
                    reject(event.target.error)
                }
            } else {
                db.transaction(
                    (tx) => {
                        const query = distributor_id
                            ? `SELECT * FROM ${SCHEDULES_STORE_NAMES.CHECKOUT} WHERE distributor_id = ? AND schedule_id = ?`
                            : `SELECT * FROM ${SCHEDULES_STORE_NAMES.CHECKOUT} WHERE store_id = ? AND schedule_id = ?`
                        const params = [store_id, schedule_id]

                        // const query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.CHECKOUT} WHERE store_id = ? AND schedule_id = ?`
                        // const params = [store_id, schedule_id]

                        tx.executeSql(
                            query,
                            params,
                            (_, result) => {
                                if (result.rows._array.length > 0) {
                                    //console.log('🚀 ~CHECKOUT found for store_id and schedule_id:', result.rows._array)
                                    resolve(result.rows._array)
                                } else {
                                    //  console.log('🚀 ~ no CHECKOUT found for store_id and schedule_id:', result)
                                    resolve(false)
                                }
                            },
                            (_, error) => {
                                console.error(error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error(error)
                        reject(error)
                    },
                )
            }
        })
    }
    get_all_checkout = () => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
            } else {
                db.transaction(
                    (tx) => {
                        const query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.CHECKOUT}`
                        const params = []

                        tx.executeSql(
                            query,
                            params,
                            (_, result) => {
                                if (result.rows._array.length > 0) {
                                    console.log('🚀 ~get_all_checkout found for store_id and schedule_id:', result.rows._array)
                                    resolve(result.rows._array)
                                } else {
                                    console.log('🚀 ~ no get_all_checkout found for store_id and schedule_id:', result)
                                    resolve(false)
                                }
                            },
                            (_, error) => {
                                console.error(error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error(error)
                        reject(error)
                    },
                )
            }
        })
    }

    get_total_checkin_count = (schedule_id) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                const transaction = db.transaction([SCHEDULES_STORE_NAMES.CHECKIN], 'readonly')
                const objectStore = transaction.objectStore(SCHEDULES_STORE_NAMES.CHECKIN)

                const request = objectStore.count()

                request.onsuccess = (event) => {
                    const count = event.target.result
                    //  console.log('🚀 ~ total checkin count:', count)
                    resolve(count)
                }

                request.onerror = (event) => {
                    console.error('Transaction error:', event.target.error)
                    reject(event.target.error)
                }
            } else {
                db.transaction(
                    (tx) => {
                        const queryOld = `SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} WHERE schedule_id = ? AND status IN (${FORM_SCHEDULE_STATUS.COMPLETED}, ${FORM_SCHEDULE_STATUS.PENDING_CONNECTION}) AND pos_id IS NOT NULL`
                        const queryNew = `
                            SELECT COUNT(*) as count
                            FROM (
                                SELECT adv.pos_id, adv.schedule_id, adv.status
                                FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} AS adv
                                INNER JOIN (
                                    SELECT pos_id, schedule_id, MAX(date_modified) AS latest_date 
                                    FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES}
                                    WHERE schedule_id = ? AND pos_id IS NOT NULL
                                    GROUP BY pos_id, schedule_id
                                ) AS latest_adv
                                ON adv.pos_id = latest_adv.pos_id 
                                AND adv.schedule_id = latest_adv.schedule_id 
                                AND adv.date_modified = latest_adv.latest_date
                                WHERE adv.status IN (${FORM_SCHEDULE_STATUS.COMPLETED}, ${FORM_SCHEDULE_STATUS.PENDING_CONNECTION}, ${STORE_STATUS.INCIDENCE_REPORTED}, ${STORE_STATUS.INCIDENCE_REPORTED_PERMANENTE}, ${STORE_STATUS.INCIDENCE_REPORTED_CLOSE})
                            ) AS recent_advances;
                        `
                        const params = [schedule_id]
                        // const query = `SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} WHERE schedule_id = ? AND status = 1 AND pos_id IS NOT NULL`

                        tx.executeSql(
                            queryNew,
                            params,
                            (_, result) => {
                                if (result.rows.length > 0) {
                                    const count = result.rows.item(0).count
                                    //  console.log('🚀 ~ total checkin count:', count)
                                    resolve(count)
                                } else {
                                    console.log('🚀 ~ no checkins found')
                                    resolve(0)
                                }
                            },
                            (_, error) => {
                                console.error('Transaction error:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction error:', error)
                        reject(error)
                    },
                )
            }
        })
    }
    isFormComplete = (form_id, store_id, schedule_id, status_complete, status_pending_connection, status_pending_form_moderation) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                const transaction = db.transaction([SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS], 'readonly')
                const objectStore = transaction.objectStore(SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS)

                const index = objectStore.index('form_id_store_id_schedule_id')
                const range = IDBKeyRange.bound([form_id, store_id, schedule_id], [form_id, store_id, schedule_id])
                const request = index.openCursor(range)

                request.onsuccess = (event) => {
                    const cursor = event.target.result
                    if (cursor) {
                        const record = cursor.value
                        if (record.status === 3) {
                            //console.log('🚀 ~ Formulario completo:', record)
                            resolve(true)
                        } else {
                            console.log('🚀 ~ Formulario no completo:', record)
                            resolve(false)
                        }
                    } else {
                        console.log('🚀 ~ No se encontró el formulario')
                        resolve(false)
                    }
                }

                request.onerror = (event) => {
                    console.error('Transaction error:', event.target.error)
                    reject(event.target.error)
                }
            } else {
                const generateIdDate = generateIdDateNow()

                db.transaction(
                    (tx) => {
                        const query = `SELECT status FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} WHERE form_id = ? AND store_id = ? AND schedule_id = ? AND day_unique_id = ?;`
                        const params = [form_id, store_id, schedule_id, generateIdDate]

                        tx.executeSql(
                            query,
                            params,
                            (_, result) => {
                                if (result.rows.length > 0) {
                                    const status = result.rows.item(0).status
                                    if (
                                        status === status_complete ||
                                        status == status_pending_connection ||
                                        status == status_pending_form_moderation
                                    ) {
                                        resolve(true)
                                    } else {
                                        resolve(false)
                                    }
                                } else {
                                    resolve(false)
                                }
                            },
                            (_, error) => {
                                console.error('Transaction error:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    clearFqaAnswersById = (fqaId) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
            } else {
                db.transaction(
                    (tx) => {
                        const query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                        SET answers = ?
                                        WHERE _id = ?;
                        `
                        const params = ['[]', fqaId]

                        tx.executeSql(
                            query,
                            params,
                            (_, result) => {
                                resolve(result)
                            },
                            (_, error) => {
                                console.error('clearFqaAnswersById: Transaction error:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction error:', error)
                        reject(error)
                    },
                )
            }
        })
    }
    /**
     * @description funcion para updatear el status de un registro de formulario de la tabla SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS
     * @param {*} status
     * @param {*} schedule_id
     * @param {*} form_id
     * @returns
     */
    updateStatusFormAnswerSchedule = (status, schedule_id, form_id, store_id = null, from = 'null', register_id = null) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
            } else {
                db.transaction((tx) => {
                    const generateIdDate = generateIdDateNow()
                    let querySelect
                    let queryParamsSelect

                    if (register_id) {
                        querySelect = `SELECT _id, status FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                    WHERE _id = ?;`
                        queryParamsSelect = [register_id]
                    } else {
                        if (store_id) {
                            querySelect = `SELECT _id, status FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                        WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND day_unique_id = ?
                                        ORDER BY _id DESC;`
                            queryParamsSelect = [schedule_id, form_id, store_id, generateIdDate]
                        } else {
                            querySelect = `SELECT _id, status FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                        WHERE schedule_id = ? AND form_id = ? AND day_unique_id = ?
                                        ORDER BY _id DESC;`
                            queryParamsSelect = [schedule_id, form_id, generateIdDate]
                        }
                    }

                    tx.executeSql(
                        querySelect,
                        queryParamsSelect,
                        (_, result) => {
                            let updatedId
                            const registerId = result.rows.item(0)._id
                            const currentStatus = result.rows.item(0).status

                            let query
                            let queryParams

                            if (register_id) {
                                query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                                SET status = ?
                                                WHERE _id = ?;`

                                queryParams = [status, register_id]

                                updatedId = register_id
                            } else {
                                if (store_id) {
                                    query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                                    SET status = ?
                                                    WHERE _id = ?;`

                                    queryParams = [status, registerId]

                                    updatedId = registerId
                                } else {
                                    if (from == 'PENDING_CONNECTION') {
                                        query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                                        SET status = ?
                                                        WHERE _id = ?;` //AND status NOT IN (${FORM_SCHEDULE_STATUS.COMPLETED}, ${FORM_SCHEDULE_STATUS.PENDING_CONNECTION})

                                        queryParams = [status, registerId]

                                        updatedId = registerId
                                    } else {
                                        query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                            SET status = ?
                                            WHERE _id =?;` //
                                        // WHERE schedule_id = ? AND form_id = ? AND status NOT IN (${FORM_SCHEDULE_STATUS.COMPLETED}, ${FORM_SCHEDULE_STATUS.PENDING_CONNECTION});` //

                                        queryParams = [status, registerId]
                                        updatedId = registerId
                                    }
                                }
                            }

                            tx.executeSql(
                                query,
                                queryParams,
                                (_, result) => {
                                    this.updateFormPendingSyncStatus(schedule_id, form_id, status)
                                    this.updateFormPendingSyncStatusSend(schedule_id, form_id, STATUS_FORM_PENDING_SYNC.NO_SENDED)
                                    resolve({ ...result, updatedId })
                                },
                                (_, error) => {
                                    console.error('Transaction error: updateStatusFormAnswerSchedule =>', error)
                                    reject(error)
                                },
                            )
                        },
                        (_, error) => {
                            console.error('updateStatusFormAnswerSchedule - Error en la consulta del querySelect estado actual:', error)
                            reject(error)
                        },
                    )
                })
            }
        })
    }
    updateStatusSendForm = (status, schedule_id, form_id, store_id = null, from = 'null', register_id = null, date_finish = null) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
            } else {
                db.transaction((tx) => {
                    const generateIdDate = generateIdDateNow()

                    let querySelect
                    let queryParamsSelect

                    if (register_id) {
                        querySelect = `SELECT _id, status_send FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                    WHERE _id = ?;`
                        queryParamsSelect = [register_id]
                    } else {
                        if (store_id) {
                            querySelect = `SELECT _id, status_send FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                        WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND day_unique_id = ?
                                        ORDER BY _id DESC;`
                            queryParamsSelect = [schedule_id, form_id, store_id, generateIdDate]
                        } else {
                            querySelect = `SELECT _id, status_send FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                        WHERE schedule_id = ? AND form_id = ? AND day_unique_id = ?
                                        ORDER BY _id DESC;`
                            queryParamsSelect = [schedule_id, form_id, generateIdDate]
                        }
                    }

                    // console.log('2923 --- querySelect:', querySelect)
                    // console.log('2924 --- queryParamsSelect:', queryParamsSelect)

                    tx.executeSql(
                        querySelect,
                        queryParamsSelect,
                        (_, result) => {
                            // console.log('2930 ---- result.rows: ', result.rows)
                            const registerId = result.rows.item(0)._id
                            const currentStatus = result.rows.item(0).status

                            let query
                            let queryParams

                            if (register_id) {
                                if (date_finish != null) {
                                    query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                                    SET status_send = ?, date_finish = ?
                                                    WHERE _id = ?;`

                                    queryParams = [status, date_finish, register_id]
                                } else {
                                    query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                                    SET status_send = ?
                                                    WHERE _id = ?;`

                                    queryParams = [status, register_id]
                                }
                            } else {
                                if (store_id) {
                                    if (date_finish != null) {
                                        query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                                        SET status_send = ?, date_finish = ?
                                                        WHERE _id = ?;`

                                        queryParams = [status, date_finish, registerId]
                                    } else {
                                        query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                                        SET status_send = ?
                                                        WHERE _id = ?;`

                                        queryParams = [status, registerId]
                                    }
                                } else {
                                    if (from == 'PENDING_CONNECTION') {
                                        if (date_finish != null) {
                                            query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                                            SET status_send = ?, date_finish = ?
                                                            WHERE _id = ?;`

                                            queryParams = [status, date_finish, registerId]
                                        } else {
                                            query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                                            SET status_send = ?
                                                            WHERE _id = ?;`

                                            queryParams = [status, registerId]
                                        }
                                    } else {
                                        if (date_finish != null) {
                                            query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                                SET status_send = ?, date_finish = ?
                                                WHERE _id =?;`

                                            queryParams = [status, date_finish, registerId]
                                        } else {
                                            query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                                SET status_send = ?
                                                WHERE _id =?;`

                                            queryParams = [status, registerId]
                                        }
                                    }
                                }
                            }

                            tx.executeSql(
                                query,
                                queryParams,
                                (_, result) => {
                                    resolve()
                                },
                                (_, error) => {
                                    console.error('Transaction error: updateStatusSendForm =>', error)
                                    reject(error)
                                },
                            )
                        },
                        (_, error) => {
                            console.error('updateStatusSendForm - Error en la consulta del querySelect estado actual:', error)
                            reject(error)
                        },
                    )
                })
            }
        })
    }

    getFormsAnswersByStatus = (status) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
            } else {
                db.transaction((tx) => {
                    let query = `SELECT * 
                                FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                    WHERE status = ?;`

                    let queryParams = [status]

                    tx.executeSql(
                        query,
                        queryParams,
                        (_, result) => {
                            if (result.rows.length > 0) {
                                // console.log('1925 ---- ', result.rows)
                                resolve(result.rows._array)
                            } else {
                                resolve([])
                            }
                        },
                        (error) => {
                            console.error('Error en la transacción getFormsAnswersByStatus :', error)
                            reject(error)
                        },
                    )
                })
            }
        })
    }

    getFormsAnswersByStatus2 = (status1, status2, incidence = false) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
            } else {
                db.transaction((tx) => {
                    let query = `
                        SELECT *
                        FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} AS fqa
                        WHERE (status_send = ? OR status = ?)
                    `

                    let queryParams = [status1, status2]

                    if (incidence) {
                        query += `
                            AND store_id IS NOT NULL
                            AND store_id != ''
                        `
                    } else {
                        query += `
                            AND (
                                (store_id IS NOT NULL AND store_id != '' AND checkout = 0)
                                OR (store_id IS NOT NULL AND store_id != '' AND checkout = 1 AND EXISTS (
                                    SELECT 1
                                    FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} AS sa
                                    WHERE sa.status = 1
                                    AND sa.schedule_id = fqa.schedule_id
                                    AND sa.pos_id = fqa.store_id
                                    AND sa.date_modified = (
                                        SELECT MAX(sub_sa.date_modified)
                                        FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} AS sub_sa
                                        WHERE sub_sa.schedule_id = sa.schedule_id
                                        AND sub_sa.pos_id = sa.pos_id
                                    )
                                ))
                                OR (store_id IS NULL OR store_id = '')
                            )
                        `
                    }

                    // date_finish se actualiza cuando tocas el boton enviar..
                    query += ` ORDER BY date_finish ASC, _id ASC`

                    tx.executeSql(
                        query,
                        queryParams,
                        (_, result) => {
                            if (result.rows.length > 0) {
                                if (incidence) {
                                    const updatesResults = result.rows._array.filter((fqa) => {
                                        const typeForm = JSON.parse(fqa.type_form)
                                        return typeForm.type == FORM_TYPES.FORM_INCIDENCE
                                    })
                                    resolve(updatesResults)
                                } else {
                                    resolve(result.rows._array)
                                }
                            } else {
                                resolve([])
                            }
                        },
                        (error) => {
                            console.error('Error en la transacción getFormsAnswersByStatus :', error)
                            reject(error)
                        },
                    )
                })
            }
        })
    }

    /**
     * @description obtiene todos los formularios guardados en FORMS_QUESTION_ANSWERS para debugging
     * @param {number} limit - límite de registros a obtener (default: 100)
     * @returns {Promise<Array>} Array con todos los registros de formularios
     */
    getAllFormsAnswers = (limit = 100) => {
        const db = openDatabase()
        return new Promise((resolve, reject) => {
            if (isWeb()) {
                resolve([])
            } else {
                db.transaction((tx) => {
                    const query = `
                        SELECT 
                            fqa._id,
                            fqa.answers,
                            fqa.name_form,
                            fqa.status,
                            fqa.status_send,
                            fqa.form_id,
                            fqa.store_id,
                            fqa.schedule_id,
                            fqa.date_init,
                            fqa.date_finish,
                            s.name as store_name,
                            s.code as store_code,
                            s.address as store_address
                        FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} fqa
                        LEFT JOIN ${SCHEDULES_STORE_NAMES.STORES} s ON fqa.store_id = s._id
                        ORDER BY fqa.date_init DESC
                        LIMIT ?
                    `

                    tx.executeSql(
                        query,
                        [limit],
                        (_, result) => {
                            if (result.rows._array && result.rows._array.length > 0) {
                                // Procesar los datos para mostrar la cantidad de answers
                                const processedResults = result.rows._array.map((row) => ({
                                    ...row,
                                    answers_count: row.answers ? JSON.parse(row.answers).length : 0,
                                    answers_preview: row.answers ? JSON.parse(row.answers).slice(0, 2) : [],
                                }))
                                resolve(processedResults)
                            } else {
                                resolve([])
                            }
                        },
                        (error) => {
                            console.error('Error en la transacción getAllFormsAnswers:', error)
                            reject(error)
                        },
                    )
                })
            }
        })
    }

    updateStatusCheckoutFormsQuestionsAnswers = (schedule_id, store_id, status_update) => {
        const db = openDatabase()
        return new Promise((resolve, reject) => {
            if (isWeb()) {
                resolve()
            } else {
                db.transaction((tx) => {
                    // ✅ FIX: Solo marcar checkout en formularios del día actual
                    // Checkout de hoy NO habilita formularios de días anteriores
                    const generateIdDate = generateIdDateNow()
                    
                    const queryUpdate = `
                                UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                SET checkout = ?
                                WHERE schedule_id = ?
                                AND store_id = ?
                                AND day_unique_id = ?
                                AND checkout IS NULL;
                        `
                    const paramsUpdate = [status_update, schedule_id, store_id, generateIdDate]

                    tx.executeSql(
                        queryUpdate,
                        paramsUpdate,
                        (_, resultUpdate) => {
                            console.log('✅ updateStatusCheckoutFormsQuestionsAnswers: Formularios actualizados del día actual:', {
                                schedule_id,
                                store_id,
                                day_unique_id: generateIdDate,
                                rowsAffected: resultUpdate.rowsAffected
                            })
                            resolve(resultUpdate.rowsAffected)
                        },
                        (_, error) => {
                            console.error('3638 - Error en la transacción updateStatusCheckoutFromsQuestionsAnswers :', error)
                            reject(error)
                        },
                    )
                })
            }
        })
    }

    /**
     * @description funcion para traerme los registros de ese form y respuestas que guardo, devuelvo las asnwers, date_init, repeat, status, totalScore, scoreObtained de la tabla FORMS_QUESTION_ANSWERS
     * @param {*} schedule_id
     * @param {*} form_id
     * @param {?} store_id
     * @returns {*} {answers, date_init, repeat, status, scoreObtained, cant_questions_yes_na, progress_questions_yes_na, scoreTotalQuestionsPonderar}
     */
    getFormsAnswers = (schedule_id, form_id, store_id = null, register_id = null, repeat = null) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                const transaction = db.transaction([SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS], 'readonly')
                const objectStore = transaction.objectStore(SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS)
                const request = objectStore.getAll()

                request.onsuccess = (event) => {
                    const courses = event.target.result
                    resolve(courses)
                }

                request.onerror = (event) => {
                    reject(event.target.error)
                }
            } else {
                db.transaction(
                    (tx) => {
                        let query
                        let queryParams

                        const generateIdDate = generateIdDateNow()

                        if (register_id) {
                            query = `SELECT _id, answers, date_init, date_finish, repeat, status, totalScore, scoreObtainedFloat, cant_questions_yes_na, progress_questions_yes_na, scoreTotalQuestionsPonderarFloat, name_form, questions, heladera, create_refrigerator, type_form, form_id_father, checkin_id
                                FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                WHERE _id = ?;`
                            queryParams = [register_id]
                        } else {
                            if (store_id) {
                                if (repeat && repeat == 1) {
                                    query = `SELECT _id, answers, date_init, date_finish, repeat, status, totalScore, scoreObtainedFloat, cant_questions_yes_na, progress_questions_yes_na, scoreTotalQuestionsPonderarFloat, name_form, questions, heladera, create_refrigerator, type_form, form_id_father, checkin_id
                                            FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                            WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND status NOT IN (${FORM_SCHEDULE_STATUS.COMPLETED}, ${FORM_SCHEDULE_STATUS.PENDING_CONNECTION});`
                                    queryParams = [schedule_id, form_id, store_id]
                                } else {
                                    query = `SELECT _id, answers, date_init, date_finish, repeat, status, totalScore, scoreObtainedFloat, cant_questions_yes_na, progress_questions_yes_na, scoreTotalQuestionsPonderarFloat, name_form, questions, heladera, create_refrigerator, type_form, form_id_father, checkin_id
                                            FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                            WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND day_unique_id = ?;`
                                    queryParams = [schedule_id, form_id, store_id, generateIdDate]
                                }
                            } else {
                                query = `SELECT _id, answers, date_init, date_finish, repeat, status, totalScore, scoreObtainedFloat, cant_questions_yes_na, progress_questions_yes_na, scoreTotalQuestionsPonderarFloat, name_form, questions, heladera, create_refrigerator, type_form, form_id_father, checkin_id
                                    FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                    WHERE schedule_id = ? AND form_id = ? AND day_unique_id = ? AND status NOT IN (${FORM_SCHEDULE_STATUS.COMPLETED}, ${FORM_SCHEDULE_STATUS.PENDING_CONNECTION});`
                                queryParams = [schedule_id, form_id, generateIdDate]
                            }
                        }

                        tx.executeSql(
                            query,
                            queryParams,
                            (_, result) => {
                                if (result.rows._array.length > 0) {
                                    const rowToday = result.rows.item(0)
                                    const answers = JSON.parse(rowToday.answers)
                                    const questions = JSON.parse(rowToday.questions)
                                    const type_form = JSON.parse(rowToday.type_form)
                                    resolve({
                                        fqa_id: rowToday._id,
                                        answers: answers,
                                        date_init: rowToday.date_init,
                                        date_finish: rowToday.date_finish,
                                        status: rowToday.status,
                                        repeat: rowToday.repeat,
                                        totalScore: rowToday.totalScore,
                                        scoreObtained: rowToday.scoreObtainedFloat,
                                        cant_questions_yes_na: rowToday.cant_questions_yes_na,
                                        progress_questions_yes_na: rowToday.progress_questions_yes_na,
                                        scoreTotalQuestionsPonderar: rowToday.scoreTotalQuestionsPonderarFloat,
                                        name_form: rowToday.name_form,
                                        questions: questions,
                                        heladera: rowToday.heladera,
                                        create_refrigerator: rowToday.create_refrigerator,
                                        type_form: type_form,
                                        form_id_father: rowToday.form_id_father ?? null,
                                        checkin_id: rowToday.checkin_id ?? null,
                                    })
                                } else {
                                    resolve({
                                        answers: [],
                                        date_init: null,
                                        form: null,
                                    })
                                }
                            },
                            (error) => {
                                console.error('1111 - Error en la transacción getFormsAnswers :', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Error en la transacción getFormsAnswers - sqlite:', error)
                        reject(error)
                    },
                    null,
                )
            }
        })
    }

    /**
     * 🆕 MIGRADO A TYPESCRIPT REPOSITORY
     * 
     * Wrapper de compatibilidad que usa la nueva implementación moderna
     * de getFormsAnswersInStore del repository TypeScript.
     * 
     * Mantiene la misma interfaz para no romper código existente.
     * 
     * @description Obtiene formulario guardado en FORMS_QUESTION_ANSWERS filtrado por día de hoy
     * @param {*} schedule_id - ID del schedule
     * @param {*} form_id - ID del formulario
     * @param {*} store_id - ID de la tienda (opcional)
     * @param {*} register_id - ID del registro específico (opcional)
     * @param {*} repeat - Flag de repetición (1 = formulario repetible)
     * @returns Promise con los datos del formulario
     */
    getFormsAnswersInStore = async (schedule_id, form_id, store_id = null, register_id = null, repeat = null) => {
        try {
            // ✅ Usar nueva implementación TypeScript del repository (import ES6)
            // console.log(`🚀 ~ getFormsAnswersInStore (wrapper) ~ Invocando nueva implementación TypeScript... schedule_id: ${schedule_id}, form_id: ${form_id}, store_id: ${store_id}, register_id: ${register_id}, repeat: ${repeat}`)
            const result = await getFormsAnswersInStoreNew(
                schedule_id,
                form_id,
                store_id,
                register_id,
                repeat
            )

            // console.log('🚀 ~ getFormsAnswersInStore (wrapper) ~ result:', result.answers.length)
            
            return result
        } catch (error) {
            console.error('❌ Error en getFormsAnswersInStore (wrapper):', error)
            throw error
        }
    }

    // ========================================================================
    // 🗑️ DEPRECATED - Implementación antigua (mantener comentada como referencia)
    // Para ver la implementación anterior, revisar git history
    // ========================================================================
    /*
    getFormsAnswersInStore_OLD = (schedule_id, form_id, store_id = null, register_id = null, repeat = null) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                const transaction = db.transaction([SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS], 'readonly')
                const objectStore = transaction.objectStore(SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS)
                const request = objectStore.getAll()

                request.onsuccess = (event) => {
                    const courses = event.target.result
                    resolve(courses)
                }

                request.onerror = (event) => {
                    reject(event.target.error)
                }
            } else {
                db.transaction(
                    (tx) => {
                        const generateIdDate = generateIdDateNow()

                        let query
                        let queryParams

                        if (register_id) {
                            query = `SELECT _id, answers, date_init, date_finish, repeat, status, totalScore, scoreObtainedFloat, cant_questions_yes_na, progress_questions_yes_na, scoreTotalQuestionsPonderarFloat, name_form, questions, heladera, create_refrigerator, type_form, form_id_father
                                FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                WHERE _id = ?;`
                            queryParams = [register_id]
                        } else {
                            if (store_id) {
                                if (repeat && repeat == 1) {
                                    query = `SELECT _id, answers, date_init, date_finish, repeat, status, totalScore, scoreObtainedFloat, cant_questions_yes_na, progress_questions_yes_na, scoreTotalQuestionsPonderarFloat, name_form, questions, heladera, create_refrigerator, type_form, form_id_father
                                            FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                            WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND status NOT IN (${FORM_SCHEDULE_STATUS.COMPLETED}, ${FORM_SCHEDULE_STATUS.PENDING_CONNECTION}) AND date(date_init) = date('now');`
                                    queryParams = [schedule_id, form_id, store_id]
                                } else {
                                    query = `SELECT _id, answers, date_init, date_finish, repeat, status, totalScore, scoreObtainedFloat, cant_questions_yes_na, progress_questions_yes_na, scoreTotalQuestionsPonderarFloat, name_form, questions, heladera, create_refrigerator, type_form, form_id_father
                                            FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                            WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND day_unique_id = ?;`
                                    queryParams = [schedule_id, form_id, store_id, generateIdDate]
                                }
                            } else {
                                query = `SELECT _id, answers, date_init, date_finish, repeat, status, totalScore, scoreObtainedFloat, cant_questions_yes_na, progress_questions_yes_na, scoreTotalQuestionsPonderarFloat, name_form, questions, heladera, create_refrigerator, type_form, form_id_father
                                    FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                    WHERE schedule_id = ? AND form_id = ? AND day_unique_id = ? AND status NOT IN (${FORM_SCHEDULE_STATUS.COMPLETED}, ${FORM_SCHEDULE_STATUS.PENDING_CONNECTION});`
                                queryParams = [schedule_id, form_id, generateIdDate]
                            }
                        }

                        tx.executeSql(
                            query,
                            queryParams,
                            (_, result) => {
                                if (result.rows._array.length > 0) {
                                    const rowToday = result.rows.item(0)
                                    const answers = JSON.parse(rowToday.answers)
                                    const questions = JSON.parse(rowToday.questions)
                                    const type_form = JSON.parse(rowToday.type_form)
                                    resolve({
                                        fqa_id: rowToday._id,
                                        answers: answers,
                                        date_init: rowToday.date_init,
                                        date_finish: rowToday.date_finish,
                                        status: rowToday.status,
                                        repeat: rowToday.repeat,
                                        totalScore: rowToday.totalScore,
                                        scoreObtained: rowToday.scoreObtainedFloat,
                                        cant_questions_yes_na: rowToday.cant_questions_yes_na,
                                        progress_questions_yes_na: rowToday.progress_questions_yes_na,
                                        scoreTotalQuestionsPonderar: rowToday.scoreTotalQuestionsPonderarFloat,
                                        name_form: rowToday.name_form,
                                        questions: questions,
                                        heladera: rowToday.heladera,
                                        create_refrigerator: rowToday.create_refrigerator,
                                        type_form: type_form,
                                        form_id_father: rowToday.form_id_father,
                                    })
                                } else {
                                    resolve({
                                        answers: [],
                                        date_init: null,
                                        form: null,
                                    })
                                }
                            },
                            (error) => {
                                console.error('1111 - Error en la transacción getFormsAnswers :', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Error en la transacción getFormsAnswers - sqlite:', error)
                        reject(error)
                    },
                    null,
                )
            }
        })
    }
    */
    // ========================================================================
    // FIN DEPRECATED
    // ========================================================================


    /**
     * 🆕 MIGRADO A TYPESCRIPT REPOSITORY
     * 
     * Wrapper de compatibilidad que usa la nueva implementación moderna
     * de updateFormAnswerSchedule del repository TypeScript.
     * 
     * Mantiene la misma interfaz para no romper código existente.
     * 
     * @description updatea la key answers del registro del formulario: agrega o updatea al array las answers que vaya respondiendo el user, debe tambien updatear la key scoreObtained en base a lo que me llega en la newAnswer.questionScore
     * @param {*} newAnswer - Objeto con la respuesta nueva
     * @param {*} schedule_id - ID del schedule
     * @param {*} form_id - ID del formulario
     * @param {*} store_id - ID de la tienda (opcional)
     * @param {*} remplaceAnswers - Si debe reemplazar todas las respuestas
     * @param {*} clearAnswers - Si debe limpiar respuestas
     * @param {*} type - Tipo de operación (para logs)
     * @returns Promise<number> - 1 si éxito, -1 si error
     */
    updateFormAnswerSchedule = async (newAnswer, schedule_id, form_id, store_id = null, remplaceAnswers = false, clearAnswers = false, type = 'null') => {
        try {
            // ✅ Usar nueva implementación TypeScript del repository
            const result = await updateFormAnswerScheduleNew({
                newAnswer,
                schedule_id,
                form_id,
                store_id,
                replaceAnswers: remplaceAnswers,
                clearAnswers,
                type
            })
            
            // Retornar en formato compatible con código antiguo
            return result.status === 1 ? 1 : -1
        } catch (error) {
            console.error('❌ Error en updateFormAnswerSchedule (wrapper):', error)
            throw error
        }
    }

    // ========================================================================
    // 🗑️ DEPRECATED - Implementación antigua (mantener comentada como referencia)
    // Para ver la implementación anterior, revisar git history
    // ========================================================================
    /*
    updateFormAnswerSchedule_OLD = (newAnswer, schedule_id, form_id, store_id = null, remplaceAnswers = false, clearAnswers = false, type = 'null') => {
        // Código antiguo comentado...
            try {
                const db = openDatabase()

                // DEPRECATED: Mantener por compatibilidad pero queue ya maneja concurrencia
                if (schedulesService.isUpdating) {
                    console.log('⚠️ Otra operación está en curso (flag legacy). Por favor, espera.')
                    return Promise.reject('Otra operación está en curso.')
                }

                schedulesService.isUpdating = true

                let statusResponse = -1
                
                return new Promise((resolve, reject) => {
                    if (isWeb()) {
                        // Web handling
                    } else {
                        const generateIdDate = generateIdDateNow()
                        
                        // Validar day_unique_id
                        if (!generateIdDate || generateIdDate.length !== 8) {
                            const error = new Error(`❌ day_unique_id inválido: ${generateIdDate}`)
                            console.error(error.message)
                            reject(error)
                            return
                        }
                        
                        let query
                        let queryParams

                        if (store_id) {
                            query = `SELECT answers, status, date_init
                                    FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} 
                                    WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND day_unique_id = ? AND status NOT IN (${FORM_SCHEDULE_STATUS.PENDING_CONNECTION});`
                            queryParams = [schedule_id, form_id, store_id, generateIdDate]
                        } else {
                            query = `SELECT answers, status, date_init
                                    FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} 
                                    WHERE schedule_id = ? AND form_id = ? AND day_unique_id = ? AND status NOT IN (${FORM_SCHEDULE_STATUS.COMPLETED}, ${FORM_SCHEDULE_STATUS.PENDING_CONNECTION});`
                            queryParams = [schedule_id, form_id, generateIdDate]
                        }
                        db.transaction(
                            (tx) => {
                                tx.executeSql(
                                    query,
                                    queryParams,
                                    (_, result) => {
                                        if (result.rows._array.length > 0) {
                                            // Validar duplicados
                                            if (result.rows._array.length > 1) {
                                                console.warn('⚠️ Múltiples registros encontrados para el mismo formulario/día:', {
                                                    count: result.rows._array.length,
                                                    schedule_id,
                                                    form_id,
                                                    store_id,
                                                    day_unique_id: generateIdDate
                                                })
                                            }
                                            
                                            const today = new Date().toISOString().split('T')[0]
                                            let rowToday = null

                                            for (let i = 0; i < result.rows._array.length; i++) {
                                                const row = result.rows.item(i)
                                                if (row.date_init && row.date_init.split('T')[0] === today) {
                                                    rowToday = row
                                                    break
                                                }
                                            }

                                            if (!rowToday) {
                                                const dataLogs = {
                                                    query,
                                                    queryParams,
                                                    today,
                                                    result,
                                                }
                                                const dataCapture = {
                                                    log: JSON.stringify(dataLogs),
                                                    date: new Date().toISOString(),
                                                    end: '0updateFormAnswerSchedule',
                                                    additional: {
                                                        schedule_id: schedule_id,
                                                        form_id: form_id,
                                                        store_id: store_id,
                                                        newAnswer: newAnswer,
                                                    },
                                                }

                                                captureAndSaveDataError(dataCapture)
                                                reject('No existe registro para hoy')
                                                return
                                            }

                                            let status = result.rows.item(0).status
                                            if (status != undefined && status != FORM_SCHEDULE_STATUS.IN_PROGRESS) {
                                                this.updateStatusFormAnswerSchedule(
                                                    FORM_SCHEDULE_STATUS.IN_PROGRESS,
                                                    schedule_id,
                                                    form_id,
                                                    store_id,
                                                    `opcion:${type}`,
                                                )
                                            }

                                            let answers = JSON.parse(result.rows.item(0).answers || '[]')

                                            if (remplaceAnswers) {
                                                answers = []
                                            }

                                            let findIndex = -1

                                            if (newAnswer && newAnswer._id) {
                                                findIndex = answers.findIndex((answer) => answer._id.toString() === newAnswer._id.toString())
                                            }

                                            if (findIndex !== -1) {
                                                answers[findIndex] = { ...answers[findIndex], ...newAnswer }
                                            } else {
                                                if (newAnswer) answers.push(newAnswer)
                                            }

                                        const updatedAnswers = JSON.stringify(answers)
                                        // console.log(
                                        //     '1933 ----- 🚀 ~ file: schedules.database.js:1925 ~ schedulesService ~ db.transaction ~ updatedAnswers:',
                                        //     updatedAnswers,
                                        // )

                                        // actualizo
                                        let updateFormAnswer
                                        let insertQuery
                                        if (store_id) {
                                            updateFormAnswer = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                                                SET answers = ?
                                                                WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND day_unique_id = ? AND status NOT IN (${FORM_SCHEDULE_STATUS.PENDING_CONNECTION});`
                                            insertQuery = [updatedAnswers, schedule_id, form_id, store_id, generateIdDate]
                                        } else {
                                            updateFormAnswer = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                                                SET answers = ?
                                                                WHERE schedule_id = ? AND form_id = ? AND day_unique_id = ? AND status NOT IN (${FORM_SCHEDULE_STATUS.COMPLETED}, ${FORM_SCHEDULE_STATUS.PENDING_CONNECTION});`
                                            // WHERE schedule_id = ? AND form_id = ?;`
                                            insertQuery = [updatedAnswers, schedule_id, form_id, generateIdDate]
                                        }
                                        tx.executeSql(
                                            updateFormAnswer,
                                            insertQuery,
                                            (_, result) => {
                                                // console.log('🟢 Registro actualizado con exito: updateFormAnswerSchedule')
                                                statusResponse = 1
                                                resolve({ status: statusResponse })
                                            },
                                            (_, error) => {
                                                console.error('Transaction error: updateFormAnswerSchedule =>', error)
                                                reject(error)
                                            },
                                        )

                                        // get de todas mis answers para caulcular mi suma total

                                        this.getFormsAnswers(schedule_id, form_id, store_id).then((getFormsAnswersResponse) => {
                                            if (getFormsAnswersResponse) {
                                                const answers = getFormsAnswersResponse.answers // las respuestas que guarde
                                                // console.log("1967 ---- 🚀 ~ file: schedules.database.js:1967 ~ schedulesService ~ this.getFormsAnswers ~ answers:", answers)
                                                const questions = getFormsAnswersResponse.questions // las respuestas que guarde
                                                // console.log()

                                                let answersToPonderate = answers.filter((obj) => obj.ponderar === true)
                                                let answersIDS = answersToPonderate.map((q) => q._id)
                                                // console.log('Answers IDs To Ponderate:', answersIDS)
                                                let cantQuestionPonderar = questions.filter((obj) => answersIDS.includes(obj._id))

                                                let scoreTotalQuestionsPonderar = cantQuestionPonderar.reduce((acc, item) => {
                                                    const score = item.questionScore > 0 ? item.questionScore : item.score > 0 ? item.score : 0
                                                    return acc + (parseFloat(score) || 0)
                                                }, 0)
                                                // console.log(
                                                //     'Preguntas totales a ponderar >= ',
                                                //     cantQuestionPonderar.length,
                                                //     ' - scoreTotalQuestionsPonderar =>',
                                                //     scoreTotalQuestionsPonderar,
                                                // )
                                                let score_obtenido = 0
                                                let score_total_na = 0
                                                const answersNa = []

                                                for (const answer of answers) {
                                                    const isNa = isNaOptionSelected(answer)

                                                    if (answer.ponderar) {
                                                        if (isNa) {
                                                            answersNa.push(answer)
                                                            continue
                                                        }
                                                        // console.log('Sumo puntaje a => ', answer._id, ' - score: ', answer.score)
                                                        score_obtenido += parseFloat(answer.score)
                                                        // console.log('Puntaje obtenido actual =>', score_obtenido)
                                                    }
                                                }

                                                let hide_questions = []

                                                getFormsAnswersResponse.questions.forEach((item) => {
                                                    // recorro preguntas

                                                    let answerResponse = answers.find((answer) => answer.question_id === item._id) // reviso si conteste alguna pregunta

                                                    if (answerResponse !== undefined) {
                                                        if (item.input_type.type === 14) {
                                                            // tengo alguna de type 14 ?

                                                            // busco dentro de mis secondary alguna opcion que elegi
                                                            let secondarySelected = item.secondary_actions.find(
                                                                (action) => action._id === answerResponse.option_selected_id[0],
                                                            )

                                                            // la opcion elegida tiene hide_questions o show_questions
                                                            if (secondarySelected.hide_questions.length > 0) {
                                                                // hide_questions = secondarySelected.hide_questions

                                                                for (const question_id of secondarySelected.hide_questions) {
                                                                    hide_questions.push(question_id)
                                                                }
                                                            }
                                                        }

                                                        // si no tengo respuestas de type 14, pondero sobre todas las questions
                                                        // pero busco de mis questios si tengo alguna de type 14, si tengo y de esa no respondi, a la ponderacion le resto las que esten en hide_questions
                                                        // si respondi, le sumo las que estan en show questions
                                                    }
                                                })
                                                if (hide_questions.length > 0) {
                                                    // console.log("1767 ---- 🚀 ~ file: schedules.database.js:1767 ~ schedulesService ~ this.getFormsAnswers ~ hide_questions:", hide_questions)

                                                    const questionsTransform = getFormsAnswersResponse.questions

                                                    const cantQuestionPonderar = questionsTransform.filter(
                                                        (obj) => obj.ponderar === true && !hide_questions.includes(obj._id),
                                                    )

                                                    score_obtenido = 0
                                                    score_total_na = 0

                                                    const newAnswers = answers.filter((answ) => !hide_questions.includes(answ._id))

                                                    for (const answer of newAnswers) {
                                                        if (answer.ponderar) {
                                                            score_obtenido += parseFloat(answer.score)
                                                        }
                                                    }

                                                    scoreTotalQuestionsPonderar = cantQuestionPonderar.reduce((acc, item) => {
                                                        const score = item.questionScore > 0 ? item.questionScore : item.score > 0 ? item.score : 0
                                                        return acc + (parseFloat(score) || 0)
                                                    }, 0)
                                                }

                                                if (answersNa.length > 0) {
                                                    const questionsPonderar = cantQuestionPonderar.filter(
                                                        (question) => !answersNa.map((answer) => answer.question_id).includes(question._id),
                                                    )

                                                    scoreTotalQuestionsPonderar = questionsPonderar.reduce((acc, item) => {
                                                        const score = item.questionScore > 0 ? item.questionScore : item.score > 0 ? item.score : 0
                                                        return acc + (parseFloat(score) || 0)
                                                    }, 0)
                                                    // console.log(
                                                    //     'Ajuste por NA - Preguntas totales a ponderar >= ',
                                                    //     questionsPonderar.length + 'SCORE ==>' + scoreTotalQuestionsPonderar,
                                                    // )
                                                }

                                                let progressPonderacion = (score_obtenido * 100) / scoreTotalQuestionsPonderar

                                                // Verifica si es un número entero o no
                                                if (progressPonderacion % 1 !== 0) {
                                                    // Si no es entero, redondea a 2 decimales
                                                    progressPonderacion = parseFloat(progressPonderacion.toFixed(2))
                                                }
                                                console.log('Progreso de la ponderación =>', progressPonderacion)
                                                if (
                                                    typeof progressPonderacion !== 'number' ||
                                                    isNaN(progressPonderacion) ||
                                                    !isFinite(progressPonderacion)
                                                ) {
                                                    progressPonderacion = 0
                                                }

                                                if (score_obtenido > 0) {
                                                    score_obtenido = parseFloat(score_obtenido.toFixed(2))
                                                }

                                                this.updateScoreObtainedFormAnswers(score_obtenido, schedule_id, form_id, store_id)
                                                    .then(() => {
                                                        // console.log('updateScoreObtainedFormAnswers => Actualizado con exito')
                                                    })
                                                    .catch((error) => {
                                                        console.error('updateScoreObtainedFormAnswers => Error: ', error)
                                                    })

                                                this.updateProgressPonderacionFormAnswers(progressPonderacion, schedule_id, form_id, store_id)
                                                    .then(() => {
                                                        // console.log('updateProgressPonderacionFormAnswers => Actualizado con exito')
                                                    })
                                                    .catch(() => {
                                                        console.error('updateProgressPonderacionFormAnswers => Error: ', error)
                                                    })
                                            }
                                        })
                                    } else {
                                        console.error('No se encontró el registro para actualizar updateFormAnswerSchedule')

                                        const dataCapture = {
                                            log: 'No se encontró el registro para actualizar updateFormAnswerSchedule',
                                            date: new Date().toISOString(),
                                            end: '1updateFormAnswerSchedule',
                                            additional: {
                                                schedule_id: schedule_id,
                                                form_id: form_id,
                                                store_id: store_id,
                                                newAnswer: newAnswer,
                                            },
                                        }

                                        captureAndSaveDataError(dataCapture)
                                        reject('No se encontró el registro para actualizar updateFormAnswerSchedule')
                                    }
                                },
                                (_, error) => {
                                    console.error('Error al recuperar el valor de answers:', error)

                                    const dataCapture = {
                                        log: JSON.stringify(error),
                                        date: new Date().toISOString(),
                                        end: '2updateFormAnswerSchedule',
                                        additional: {
                                            schedule_id: schedule_id,
                                            form_id: form_id,
                                            store_id: store_id,
                                            newAnswer: newAnswer,
                                        },
                                    }

                                    captureAndSaveDataError(dataCapture)

                                    reject(error)
                                },
                            )
                        },
                        (error) => {
                            console.error('Error en la transacción:', error)

                            const dataCapture = {
                                log: JSON.stringify(error),
                                date: new Date().toISOString(),
                                end: '3updateFormAnswerSchedule',
                                additional: {
                                    schedule_id: schedule_id,
                                    form_id: form_id,
                                    store_id: store_id,
                                    newAnswer: newAnswer,
                                },
                            }

                            captureAndSaveDataError(dataCapture)

                            reject(error)
                        },
                        () => {
                            // Success callback de la transacción
                            schedulesService.isUpdating = false
                        },
                    )
                }
            })
            } catch (error) {
                // Catch del try principal
                const dataCapture = {
                    log: JSON.stringify(error),
                    date: new Date().toISOString(),
                    end: '4updateFormAnswerSchedule - Outer catch',
                    additional: {
                        schedule_id: schedule_id,
                        form_id: form_id,
                        store_id: store_id,
                        newAnswer: newAnswer,
                    },
                }

                captureAndSaveDataError(dataCapture)
                throw error // Re-throw para que queue lo maneje
            } finally {
                // ✅ SIEMPRE resetear el flag, incluso si hay error
                schedulesService.isUpdating = false
            }
        })
    }
    */
    // ========================================================================
    // FIN DEPRECATED
    // ========================================================================

    // removeKeyOldResponseFormAnswerSchedule = (schedule_id, form_id, store_id = null, arrayHideQuestions) => {
    //     const db = openDatabase()

    //     return new Promise((resolve, reject) => {
    //         if (isWeb()) {
    //         } else {
    //             let query
    //             let queryParams

    //             if (store_id) {
    //                 query = `SELECT answers
    //                         FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
    //                         WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND day_unique_id = ?;`
    //                 queryParams = [schedule_id, form_id, store_id, generateIdDate]
    //             } else {
    //                 query = `SELECT answers
    //                         FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
    //                         WHERE schedule_id = ? AND form_id = ? AND day_unique_id = ?;`
    //                 queryParams = [schedule_id, form_id, generateIdDate]
    //             }
    //             db.transaction((tx) => {
    //                 tx.executeSql(
    //                     query,
    //                     queryParams,
    //                     (_, result) => {
    //                         if (result.rows.length > 0) {
    //                             this.updateStatusFormAnswerSchedule(FORM_SCHEDULE_STATUS.IN_PROGRESS, schedule_id, form_id, store_id)

    //                             let answers = JSON.parse(result.rows.item(0).answers || '[]')

    //                             if (answers && answers.length > 0) {
    //                                 answers = answers.filter((ans) => !arrayHideQuestions.includes(ans['_id']))
    //                             }

    //                             const updatedAnswers = JSON.stringify(answers)

    //                             // actualizo
    //                             let updateFormAnswer
    //                             let insertQuery
    //                             if (store_id) {
    //                                 updateFormAnswer = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
    //                                                         SET answers = ?
    //                                                         WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND day_unique_id = ?;`
    //                                 insertQuery = [updatedAnswers, schedule_id, form_id, store_id, generateIdDate]
    //                             } else {
    //                                 updateFormAnswer = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
    //                                                         SET answers = ?
    //                                                         WHERE schedule_id = ? AND form_id = ? AND day_unique_id = ?;`
    //                                 insertQuery = [updatedAnswers, schedule_id, form_id, generateIdDate]
    //                             }
    //                             tx.executeSql(
    //                                 updateFormAnswer,
    //                                 insertQuery,
    //                                 (_, result) => {
    //                                     //   console.log('🟢 Registro actualizado con exito: removeKeyOldResponseFormAnswerSchedule')
    //                                     resolve()
    //                                 },
    //                                 (_, error) => {
    //                                     console.error('Transaction error: removeKeyOldResponseFormAnswerSchedule =>', error)
    //                                     reject(error)
    //                                 },
    //                             )

    //                             // get de todas mis answers para caulcular mi suma total

    //                             this.getFormsAnswers(schedule_id, form_id, store_id).then((getFormsAnswersResponse) => {
    //                                 if (getFormsAnswersResponse) {
    //                                     const answers = getFormsAnswersResponse.answers // las respuestas que guarde
    //                                     const questions = getFormsAnswersResponse.questions // las respuestas que guarde
    //                                     console.log(answers.length, 'answers.length')
    //                                     let { scoreTotalQuestionsPonderar } = getCantQuestionPonderar(answers)

    //                                     let score_obtenido = 0
    //                                     let score_total_na = 0

    //                                     for (const answer of answers) {
    //                                         if (answer.ponderar) {
    //                                             score_obtenido += parseInt(answer.score)
    //                                             // if (answer.option_selected_text[0] === 'yes') {
    //                                             //     score_obtenido += answer.questionScore
    //                                             // }

    //                                             // // resto las na del score total posible
    //                                             // if (answer.option_selected_text[0] === 'na') {
    //                                             //     score_total_na += answer.questionScore
    //                                             // }
    //                                         }
    //                                     }

    //                                     let hide_questions = []

    //                                     getFormsAnswersResponse.questions.forEach((item) => {
    //                                         // recorro preguntas

    //                                         let answerResponse = answers.find((answer) => answer.question_id === item._id) // reviso si conteste alguna pregunta

    //                                         if (answerResponse !== undefined) {
    //                                             if (item.input_type.type === 14) {
    //                                                 // tengo alguna de type 14 ?

    //                                                 // busco dentro de mis secondary alguna opcion que elegi
    //                                                 let secondarySelected = item.secondary_actions.find(
    //                                                     (action) => action._id === answerResponse.option_selected_id[0],
    //                                                 )

    //                                                 // la opcion elegida tiene hide_questions o show_questions
    //                                                 if (secondarySelected.hide_questions.length > 0) {
    //                                                     // hide_questions = secondarySelected.hide_questions

    //                                                     for (const question_id of secondarySelected.hide_questions) {
    //                                                         hide_questions.push(question_id)
    //                                                     }
    //                                                 }
    //                                             }

    //                                             // si no tengo respuestas de type 14, pondero sobre todas las questions
    //                                             // pero busco de mis questios si tengo alguna de type 14, si tengo y de esa no respondi, a la ponderacion le resto las que esten en hide_questions
    //                                             // si respondi, le sumo las que estan en show questions
    //                                         }
    //                                     })
    //                                     if (hide_questions.length > 0) {
    //                                         // console.log("1767 ---- 🚀 ~ file: schedules.database.js:1767 ~ schedulesService ~ this.getFormsAnswers ~ hide_questions:", hide_questions)

    //                                         const questionsTransform = getFormsAnswersResponse.questions

    //                                         const cantQuestionPonderar = questionsTransform.filter(
    //                                             (obj) => obj.ponderar === true && !hide_questions.includes(obj._id),
    //                                         )

    //                                         score_obtenido = 0
    //                                         score_total_na = 0

    //                                         const newAnswers = answers.filter((answ) => !hide_questions.includes(answ._id))

    //                                         for (const answer of newAnswers) {
    //                                             if (answer.ponderar) {
    //                                                 score_obtenido += parseInt(answer.score)
    //                                                 // if (answer.option_selected_text[0] === 'yes') {
    //                                                 //     score_obtenido += answer.questionScore
    //                                                 // }

    //                                                 // if (answer.option_selected_text[0] === 'na') {
    //                                                 //     score_total_na += answer.questionScore
    //                                                 // }
    //                                             }
    //                                         }

    //                                         scoreTotalQuestionsPonderar = cantQuestionPonderar.reduce(
    //                                             (accumulator, currentValue) => accumulator + currentValue.questionScore,
    //                                             0,
    //                                         )
    //                                     }

    //                                     let progressPonderacion = (score_obtenido * 100) / scoreTotalQuestionsPonderar
    //                                     // Verifica si es un número entero o no
    //                                     if (progressPonderacion % 1 !== 0) {
    //                                         // Si no es entero, redondea a 2 decimales
    //                                         progressPonderacion = parseFloat(progressPonderacion.toFixed(2))
    //                                     }
    //                                     if (typeof progressPonderacion !== 'number' || isNaN(progressPonderacion)) {
    //                                         progressPonderacion = 0
    //                                     }

    //                                     // updatear la key scoreObtained
    //                                     if (score_obtenido > 0) {
    //                                         score_obtenido = parseFloat(score_obtenido.toFixed(2))
    //                                     }
    //                                     console.log('SCORE OBTENIDO FINAL ===> ', score_obtenido)
    //                                     this.updateScoreObtainedFormAnswers(score_obtenido, schedule_id, form_id, store_id)
    //                                         .then(() => {
    //                                             // console.log('updateScoreObtainedFormAnswers => Actualizado con exito')
    //                                         })
    //                                         .catch((error) => {
    //                                             console.error('updateScoreObtainedFormAnswers => Error: ', error)
    //                                         })

    //                                     this.updateProgressPonderacionFormAnswers(parseInt(progressPonderacion), schedule_id, form_id, store_id)
    //                                         .then(() => {
    //                                             // console.log('updateProgressPonderacionFormAnswers => Actualizado con exito')
    //                                         })
    //                                         .catch(() => {
    //                                             console.error('updateProgressPonderacionFormAnswers => Error: ', error)
    //                                         })
    //                                 }
    //                             })
    //                         } else {
    //                             console.error('No se encontró el registro para actualizar removeKeyOldResponseFormAnswerSchedule')
    //                             reject('No se encontró el registro para actualizar removeKeyOldResponseFormAnswerSchedule')
    //                         }
    //                     },
    //                     (_, error) => {
    //                         console.error('Error al recuperar el valor de removeKeyOldResponseFormAnswerSchedule - answers:', error)
    //                         reject(error)
    //                     },
    //                 )
    //             })
    //         }
    //     })
    // }

    /**
     * @description funcion para updatear la key progress_questions_yes_na del registro de formulario de la tabla SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS
     * @param {*} progress
     * @param {*} schedule_id
     * @param {*} form_id
     * @param {*} store_id
     * @returns
     */
    updateProgressPonderacionFormAnswers = (progress, schedule_id, form_id, store_id) => {
        const db = openDatabase()

        // console.log("2061 ----------- 🚀 ~ file: schedules.database.js:1901 ~ schedulesService ~ progress:", progress)
        return new Promise((resolve, reject) => {
            if (isWeb()) {
            } else {
                db.transaction((tx) => {
                    const generateIdDate = generateIdDateNow()
                    let updateFormAnswer
                    let insertQuery

                    if (typeof progress !== 'number' || isNaN(progress) || !isFinite(progress)) {
                        const error = new Error('El valor de progress no es un número válido')
                        console.error('Validation error: progress - updateProgressPonderacionFormAnswers =>', error)
                        return reject(error)
                    }

                    if (store_id) {
                        updateFormAnswer = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                                                        SET progress_questions_yes_na = ?
                                                                        WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND day_unique_id = ?;`
                        insertQuery = [progress, schedule_id, form_id, store_id, generateIdDate]
                    } else {
                        updateFormAnswer = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                                                        SET progress_questions_yes_na = ?
                                                                        WHERE schedule_id = ? AND form_id = ? AND day_unique_id = ?;`
                        insertQuery = [progress, schedule_id, form_id, generateIdDate]
                    }

                    tx.executeSql(
                        updateFormAnswer,
                        insertQuery,
                        (_, result) => {
                            //   console.log('🟢 Registro actualizado con exito: updateProgressPonderacionFormAnswers - progress')
                            resolve()
                        },
                        (_, error) => {
                            console.error('Transaction error: progress - updateProgressPonderacionFormAnswers =>', error)
                            reject(error)
                        },
                    )
                })
            }
        })
    }

    /**
     * @description updateo la key scoreObtained de un registro de formulario, es la suma de de la key questionScore de cada answer
     * @param {*} totalScoreAnswer
     * @param {*} schedule_id
     * @param {*} form_id
     * @param {*} store_id
     * @returns
     */
    updateScoreObtainedFormAnswers = (totalScoreAnswer, schedule_id, form_id, store_id = null) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
            } else {
                db.transaction((tx) => {
                    const generateIdDate = generateIdDateNow()
                    let updateFormAnswer
                    let insertQuery

                    if (store_id) {
                        updateFormAnswer = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                                                        SET scoreObtainedFloat = ?
                                                                        WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND day_unique_id = ?;`
                        insertQuery = [totalScoreAnswer, schedule_id, form_id, store_id, generateIdDate]
                    } else {
                        updateFormAnswer = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                                                        SET scoreObtainedFloat = ?
                                                                        WHERE schedule_id = ? AND form_id = ? AND day_unique_id = ?;`
                        insertQuery = [totalScoreAnswer, schedule_id, form_id, generateIdDate]
                    }

                    // console.log("1312 -- updateFormAnswer: ", updateFormAnswer)
                    // console.log("1313 -- insertQuery: ", insertQuery)

                    tx.executeSql(
                        updateFormAnswer,
                        insertQuery,
                        (_, result) => {
                            // console.log('🟢 Registro actualizado con exito: updateScoreObtainedFormAnswers - scoreObtained')
                            resolve()
                        },
                        (_, error) => {
                            console.error('Transaction error: scoreObtained - updateScoreObtainedFormAnswers =>', error)
                            reject(error)
                        },
                    )
                })
            }
        })
    }

    /**
     * @description creo registo form, en donde guardo el id del form y una key answers que inicializa como un array vacio, a este array vacio despues le voy a pushear los objetos de las respuestas.
     * creo registro con status BLOCKED: 0
     *
     * @param {*} form
     * @returns
     */
    saveFormAnswers = (form) => {
        const db = openDatabase()

        const { schedule_id, form_id, store_id, repeat, questions, progress, totalScore, answers = '[]', status, type_form, checkin_id } = form

        // 🚨 DEBUG: Log para detectar llamadas problemáticas
        if (answers === '[]' && status && parseInt(status) === 5) {
            // console.log('🚨 ALERT: saveFormAnswers called with empty answers and completed status')
            // console.log('📋 Form details:', { form_id, schedule_id, store_id, status, answersLength: JSON.parse(answers).length })
        }

        if (JSON.parse(questions).length > 0 || type_form.type == FORM_TYPES.FORM_REDIRECT) {
            const { cantQuestionPonderar, scoreTotalQuestionsPonderar } = getCantQuestionPonderar(questions)

            const date_init = getDateNow()
            const date_finish = ''
            return new Promise((resolve, reject) => {
                if (isWeb()) {
                } else {
                    db.transaction(
                        (tx) => {
                            let query
                            let queryParams

                            const generateIdDate = generateIdDateNow()

                            if (store_id) {
                                if (repeat == 1) {
                                    query = `SELECT * 
                                        FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                        WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND status NOT IN (${FORM_SCHEDULE_STATUS.COMPLETED}, ${FORM_SCHEDULE_STATUS.PENDING_CONNECTION});`
                                    queryParams = [schedule_id, form_id, store_id]
                                } else {
                                    query = `SELECT * 
                                         FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                         WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND day_unique_id = ?;`
                                    queryParams = [schedule_id, form_id, store_id, generateIdDate]
                                }
                            } else {
                                query = `SELECT * 
                                     FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                     WHERE schedule_id = ? AND form_id = ? AND day_unique_id = ? AND status NOT IN (${FORM_SCHEDULE_STATUS.COMPLETED}, ${FORM_SCHEDULE_STATUS.PENDING_CONNECTION});`
                                queryParams = [schedule_id, form_id, generateIdDate]
                            }
                            tx.executeSql(query, queryParams, (_, result) => {
                                // const count = result.rows.item(0).count
                                const registersExistent = result.rows._array
                                // console.log('registersExistent: ', registersExistent.length)

                                const isExists = validateDateRegister(registersExistent)
                                // console.log('isExists: ', isExists)
                                if (!isExists) {
                                    // agrego el registro porq no existe
                                    let insert_form
                                    let insertQuery

                                    if (store_id) {
                                        insert_form = [
                                            repeat,
                                            date_init,
                                            date_finish,
                                            schedule_id,
                                            form_id,
                                            store_id,
                                            status,
                                            answers,
                                            questions,
                                            progress,
                                            totalScore,
                                            form.scoreObtained,
                                            cantQuestionPonderar,
                                            form.progress_questions_yes_na,
                                            scoreTotalQuestionsPonderar,
                                            form.name_form,
                                            form.heladera,
                                            form.create_refrigerator,
                                            JSON.stringify(form.type_form),
                                            form.form_id_father,
                                            form.checkout,
                                            generateIdDate,
                                            checkin_id,
                                        ]
                                        insertQuery = `INSERT INTO ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} (
                                                            repeat, 
                                                            date_init, 
                                                            date_finish,
                                                            schedule_id, 
                                                            form_id, 
                                                            store_id, 
                                                            status, 
                                                            answers, 
                                                            questions, 
                                                            progress, 
                                                            totalScore, 
                                                            scoreObtainedFloat, 
                                                            cant_questions_yes_na, 
                                                            progress_questions_yes_na, 
                                                            scoreTotalQuestionsPonderarFloat, 
                                                            name_form, 
                                                            heladera, 
                                                            create_refrigerator, 
                                                            type_form, 
                                                            form_id_father,
                                                            checkout,
                                                            day_unique_id,
                                                            checkin_id
                                                        ) VALUES (?, ?, ? , ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?);`
                                    } else {
                                        insert_form = [
                                            repeat,
                                            date_init,
                                            schedule_id,
                                            form_id,
                                            '',
                                            status,
                                            answers,
                                            questions,
                                            progress,
                                            totalScore,
                                            0,
                                            cantQuestionPonderar,
                                            0,
                                            scoreTotalQuestionsPonderar,
                                            form.name_form,
                                            form.heladera,
                                            form.create_refrigerator,
                                            JSON.stringify(form.type_form),
                                            form.form_id_father,
                                            generateIdDate,
                                            checkin_id,
                                        ]
                                        insertQuery = `INSERT INTO ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} (
                                                            repeat, date_init, schedule_id, form_id, store_id, status, answers, questions, progress, totalScore, scoreObtainedFloat, cant_questions_yes_na, progress_questions_yes_na, scoreTotalQuestionsPonderarFloat, name_form, heladera, create_refrigerator, type_form, form_id_father, day_unique_id, checkin_id
                                                        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?);`
                                    }

                                    tx.executeSql(
                                        insertQuery,
                                        insert_form,
                                        (_, result) => {
                                            // ya lo cree
                                            // busco el registro creado
                                            let getQuery
                                            let queryParams

                                            if (store_id) {
                                                getQuery = `    SELECT *
                                                                    FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                                                    WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND day_unique_id = ?;`

                                                queryParams = [schedule_id, form_id, store_id, generateIdDate]
                                            } else {
                                                getQuery = `    SELECT *
                                                                    FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                                                    WHERE schedule_id = ? AND form_id = ? AND day_unique_id = ?;`

                                                queryParams = [schedule_id, form_id, generateIdDate]
                                            }

                                            tx.executeSql(getQuery, queryParams, (_, result) => {
                                                if (result.rows._array.length > 0) {
                                                    let res = {
                                                        end: 'saveFormAnswers',
                                                        result: result.rows.item(0),
                                                        message: 'Agrego el registro con exito: saveFormAnswers',
                                                    }
                                                    // console.log("1367 ----- 🚀 ~ file: schedules.database.js:1367 ~ schedulesService ~ returnnewPromise ~ res:", res)
                                                    resolve(res)
                                                } else {
                                                    let res = {
                                                        end: 'saveFormAnswers',
                                                        result: null,
                                                        message: 'saveFormAnswers: quiero devolver el registro creado pero no se encontro..',
                                                    }
                                                    resolve(res)
                                                }
                                            })
                                        },
                                        (_, error) => {
                                            let res = {
                                                end: 'saveFormAnswers',
                                                result: [],
                                                message: 'Transaction error:',
                                                error: error,
                                            }
                                            //  console.log(res)

                                            // console.error('Transaction error:', error)
                                            reject(res)
                                        },
                                    )
                                } else {
                                    //  ya existe el registro no hago nada
                                    let getQuery
                                    let queryParams

                                    if (store_id) {
                                        getQuery = `    SELECT *
                                                                    FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                                                    WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND day_unique_id = ?;`

                                        queryParams = [schedule_id, form_id, store_id, generateIdDate]
                                    } else {
                                        getQuery = `    SELECT *
                                                                    FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                                                    WHERE schedule_id = ? AND form_id = ? AND day_unique_id = ?;`

                                        queryParams = [schedule_id, form_id, generateIdDate]
                                    }

                                    tx.executeSql(getQuery, queryParams, (_, result) => {
                                        if (result.rows._array.length > 0) {
                                            const existingRecord = result.rows.item(0)
                                            // console.log('3909 -- el registro existe')
                                            // console.log("3451 --- 🚀 ~ schedulesService ~ tx.executeSql ~ existingRecord:", existingRecord)
                                            const existingAnswers = JSON.parse(existingRecord.answers)
                                            // console.log('4913 -- existingAnswers.length:', existingAnswers.length)
                                            const newAnswers = JSON.parse(answers)
                                            // console.log("4915 --- 🚀 ~ schedulesService ~ tx.executeSql ~ newAnswers:", newAnswers)
                                            // console.log('4916 ---- status => ', status, typeof status)
                                            const newStatus = status ? parseInt(status) : status
                                            const existingStatus = existingRecord.status ? parseInt(existingRecord.status) : existingRecord.status

                                            let needsUpdate = false

                                            // Comparar la clave moderation dentro de answers
                                            for (let i = 0; i < newAnswers.length; i++) {
                                                const newAnswer = newAnswers[i]
                                                const existingAnswer = existingAnswers.find((ans) => ans._id === newAnswer._id)
                                                if (existingAnswer) {
                                                    if (JSON.stringify(existingAnswer.moderation) !== JSON.stringify(newAnswer.moderation)) {
                                                        needsUpdate = true
                                                        break
                                                    }
                                                } else {
                                                    needsUpdate = true
                                                    break
                                                }
                                            }

                                            // console.log('4937 - status => ', status, 'existingStatus => ', existingStatus, 'newStatus => ', newStatus)
                                            if (status && existingStatus != newStatus) {
                                                needsUpdate = true
                                            }

                                            if (needsUpdate) {
                                                // console.log('3940 --- updateo anwers.. estas => ', existingAnswers)
                                                // console.log('3941 --- updateo anwers.. por estas => ', newAnswers)

                                                // 🚨 VALIDACIÓN: No sobrescribir respuestas existentes con array vacío
                                                let answersToUpdate = newAnswers
                                                if (newAnswers.length === 0 && existingAnswers.length > 0) {
                                                    // console.log('⚠️ PREVENTED: Se intentó sobrescribir respuestas existentes con array vacío')
                                                    // console.log('📋 Existing answers length:', existingAnswers.length)
                                                    // console.log('📋 Form ID:', form_id, 'Status:', newStatus)

                                                    answersToUpdate = existingAnswers //TODO: Mantener las respuestas existentes
                                                }

                                                let updateQuery = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} SET answers = ?`
                                                const updateParams = [JSON.stringify(answersToUpdate)]

                                                if (status) {
                                                    updateQuery += `, status = ?`
                                                    updateParams.push(newStatus)
                                                }

                                                updateQuery += ` WHERE schedule_id = ? AND form_id = ?`
                                                updateParams.push(schedule_id, form_id)

                                                if (store_id) {
                                                    updateQuery += ` AND store_id = ?`
                                                    updateParams.push(store_id)
                                                }

                                                if (generateIdDate) {
                                                    updateQuery += ` AND day_unique_id = ?`
                                                    updateParams.push(generateIdDate)
                                                }

                                                updateQuery += `;`

                                                // let updateQuery = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                                //                      SET answers = ? status = ?
                                                //                      WHERE schedule_id = ? AND form_id = ? ${store_id ? 'AND store_id = ?' : ''};`

                                                // const updateParams = [JSON.stringify(newAnswers), newStatus, schedule_id, form_id]

                                                // if (store_id) updateParams.push(store_id)

                                                console.log('😎query de actualizacion: ', updateQuery)
                                                console.log('😎params de actualizacion: ', updateParams)

                                                tx.executeSql(
                                                    updateQuery,
                                                    updateParams,
                                                    (_, result) => {
                                                        tx.executeSql(
                                                            getQuery,
                                                            queryParams,
                                                            (_, result) => {
                                                                if (result.rows.length > 0) {
                                                                    const updatedRecord = result.rows.item(0)
                                                                    // const existingRecord = result.rows.item(0)
                                                                    // console.log("34888 --- 🚀 ~ schedulesService ~ tx.executeSql ~ updatedRecord:", updatedRecord)
                                                                    resolve({
                                                                        end: 'saveFormAnswers',
                                                                        result: updatedRecord,
                                                                        message: 'Registro actualizado con éxito',
                                                                    })
                                                                } else {
                                                                    reject(new Error('No se pudo obtener el registro actualizado'))
                                                                }
                                                            },
                                                            (tx, error) => {
                                                                reject(error)
                                                            },
                                                        )
                                                        // resolve({
                                                        //     end: 'saveFormAnswers',
                                                        //     result: result.rowsAffected,
                                                        //     message: 'Registro actualizado con éxito',
                                                        // });
                                                    },
                                                    (tx, error) => {
                                                        reject(error)
                                                    },
                                                )
                                            } else {
                                                // console.log('3493 --- NO UPDATEO NADA --')
                                                resolve({
                                                    end: 'saveFormAnswers',
                                                    result: existingRecord,
                                                    message: 'El registro ya existe y no se necesita actualizar',
                                                })
                                            }
                                        } else {
                                            let res = {
                                                end: 'saveFormAnswers',
                                                result: null,
                                                message: 'saveFormAnswers: quiero devolver el registro que ya existe pero no se encontro..',
                                            }
                                            // console.log("1449 ----- 🚀 ~ file: schedules.database.js:1375 ~ schedulesService ~ returnnewPromise ~ res:", res)
                                            resolve(res)
                                        }
                                    })
                                }
                            })
                        },
                        (error) => {
                            console.error('Transaction saveFormAnswers error:', error)
                            reject(error)
                        },
                        (_, success) => {
                            resolve()
                        },
                    )
                }
            })
        }
    }

    /**
     * @description traigo todas las respuestas de formulario guardadas
     * @deprecated
     */
    getFormAnswerSchedule = () => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                const transaction = db.transaction([SCHEDULES_STORE_NAMES.FORMS_ANSWERS], 'readonly')
                const objectStore = transaction.objectStore(SCHEDULES_STORE_NAMES.FORMS_ANSWERS)
                const request = objectStore.getAll()

                request.onsuccess = (event) => {
                    const courses = event.target.result
                    resolve(courses)
                }

                request.onerror = (event) => {
                    reject(event.target.error)
                }
            } else {
                db.transaction(
                    (tx) => {
                        tx.executeSql(
                            `SELECT * FROM ${SCHEDULES_STORE_NAMES.FORMS_ANSWERS};`,
                            [],
                            (_, result) => {
                                const form_answers = result.rows._array
                                console.log('form_answers: ', form_answers.length)

                                const form_modified = form_answers.map((form) => {
                                    return {
                                        ...form,
                                        option_selected_id: JSON.parse(option_selected_id),
                                        option_selected_text: JSON.parse(option_selected_text),
                                    }
                                })

                                resolve(form_modified)
                            },
                            (error) => {
                                console.error('Error en la transacción getFormAnswersSchedule1 :', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Error en la transacción course -sqlite:', error)
                        reject(error)
                    },
                    null,
                )
            }
        })
    }

    /**
     * @description traigo una respuesta del formulario en base al id
     * @param {*} answer_id
     */
    getOneFormAnswerSchedule = (answer_id) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                // const transaction = db.transaction([LEARNING_STORE_NAMES.EVALUATIONS], 'readonly')
                // const objectStore = transaction.objectStore(LEARNING_STORE_NAMES.EVALUATIONS)
                // const request = objectStore.get(evaluationId)
                // request.onsuccess = (event) => {
                //     const evaluation = event.target.result
                //     // Realiza las transformaciones necesarias
                //     const modifiedEvaluation = transformEvaluation(evaluation)
                //     resolve(modifiedEvaluation)
                // }
                // request.onerror = (event) => {
                //     reject(event.target.error)
                // }
            } else {
                db.transaction(
                    (tx) => {
                        tx.executeSql(
                            `SELECT * FROM ${SCHEDULES_STORE_NAMES.FORMS_ANSWERS} WHERE _id = ?;`,
                            [answer_id],
                            (_, result) => {
                                if (result.rows.length > 0) {
                                    const question_answer = result.rows.item(0)
                                    const question_answer_modified = {
                                        ...question_answer,
                                        option_selected_id: JSON.parse(option_selected_id),
                                        option_selected_text: JSON.parse(option_selected_text),
                                    }

                                    resolve(question_answer_modified)
                                } else {
                                    console.log('la respuesta de la question no fue encontrada ...')
                                    resolve(null)
                                }
                            },
                            (_, error) => {
                                console.error('Error en la transacción getOneFormAnswerSchedule:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Error en la transacción getOneFormAnswerSchedule by ID -sqlite:', error)
                        reject(error)
                    },
                    null,
                )
            }
        })
    }

    /**
     * @description agrego respuesta de formulario a mi tabla
     * @param {*} answer
     * @returns
     */
    addFormAnswerSchedule = (answer) => {
        const db = openDatabase()

        const { _id, question_text, option_selected_id, option_selected_text, question_id, question_input, media, date_init, date_finish } = answer

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                const transaction = db.transaction([TRACKINGS.TRACKINGS], 'readwrite')
                const objectStore = transaction.objectStore(TRACKINGS.TRACKINGS)

                const request = objectStore.add(interaction_id)

                request.onsuccess = () => {
                    resolve()
                }

                request.onerror = (event) => {
                    reject(event.target.error)
                }
            } else {
                db.transaction(
                    (tx) => {
                        tx.executeSql(
                            `SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.FORMS_ANSWERS} WHERE _id = ?`,
                            [_id],
                            (_, result) => {
                                // console.log("338 🚀 ~ file: schedules.database.js:338 ~ schedulesService ~ returnnewPromise ~ result:", result)
                                const count = result.rows.item(0)['COUNT(*)']
                                // console.log("339 -- 🚀 ~ file: schedules.database.js:339 ~ schedulesService ~ returnnewPromise ~ count:", count)

                                if (count === 0) {
                                    try {
                                        let insert_answer_form = [
                                            _id,
                                            question_text,
                                            JSON.stringify(option_selected_id),
                                            // option_selected_id,
                                            JSON.stringify(option_selected_text),
                                            // option_selected_text,
                                            question_id,
                                            question_input,
                                            media,
                                            date_init,
                                            date_finish,
                                        ]

                                        tx.executeSql(
                                            `INSERT INTO ${SCHEDULES_STORE_NAMES.FORMS_ANSWERS} (
                                                _id,
                                                question_text,
                                                option_selected_id,
                                                option_selected_text,
                                                question_id,
                                                question_input,
                                                media,
                                                date_init,
                                                date_finish
                                            ) VALUES (?,?,?,?,?,?,?,?,?);`,
                                            insert_answer_form,
                                            (_, result) => {
                                                resolve()
                                            },
                                            (_, error) => {
                                                console.error('Transaction form_answer error:', error)
                                                reject(error)
                                            },
                                        )
                                    } catch (error) {
                                        console.error('385 ---- ERROR => ', error)
                                    }
                                } else {
                                    resolve()
                                }
                            },
                            (_, error) => {
                                console.error('Error al verificar la existencia de la answer form:', error)
                                reject(error)
                            },
                            (_, success) => {
                                resolve()
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction addFormSchedule error:', error)
                        reject(error)
                    },
                    (_, success) => {
                        resolve()
                    },
                )
            }
        })
    }

    /**
     * @description funcion para validar que se hayan respondido todas las respuestas del formulario
     * @param {*} form
     * @returns {Promise<{message: string; complete: boolean}>}
     */
    isReponseTotalQuestions = (form) => {
        const db = openDatabase()

        const { schedule_id, form_id, store_id } = form

        return new Promise((resolve, reject) => {
            if (isWeb()) {
            } else {
                db.transaction((tx) => {
                    const generateIdDate = generateIdDateNow()
                    let query
                    let queryParams

                    if (store_id) {
                        query = `SELECT * 
                                 FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                 WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND day_unique_id = ?;`
                        queryParams = [schedule_id, form_id, store_id, generateIdDate]
                    } else {
                        query = `SELECT * 
                                 FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                 WHERE schedule_id = ? AND form_id = ? AND day_unique_id = ?;`
                        queryParams = [schedule_id, form_id, generateIdDate]
                    }

                    tx.executeSql(query, queryParams, (_, result) => {
                        let complete = false
                        if (result.rows.length > 0) {
                            const form_questions = JSON.parse(result.rows.item(0).questions)
                            const form_answers = JSON.parse(result.rows.item(0).answers)
                            console.log('result.rows.item(0): ', result.rows.item(0))
                            if (
                                form_questions &&
                                Array.isArray(form_questions) &&
                                form_questions.length > 0 &&
                                form_answers &&
                                Array.isArray(form_answers) &&
                                form_answers.length > 0
                            ) {
                                if (form_questions.length === form_answers.length) {
                                    console.log('-------- form_questions.length: ', form_questions.length)
                                    console.log('-------- form_answers.length: ', form_answers.length)
                                    complete = true
                                }
                            }

                            resolve({
                                message: 'isReponseTotalQuestions: Registro encontrado',
                                complete: complete,
                            })
                        } else {
                            resolve({
                                message: 'isReponseTotalQuestions: No se encontraron registros..',
                                complete: complete,
                            })
                        }
                    })
                })
            }
        })
    }

    /**
     * @description funcion para validar si esta dentro de la distancia de la tienda para saber si puede o no hacer checkin
     * @param {string} store_id id de la tienda a la que quiere realizar checkin
     * @param {{lat ; lng}} current_distance a cuanta distancia se encuentra el usuario que quiere hacer checking
     * @returns {Promise<boolean>}
     */
    isValidDistanceCheckin = (store_id, current_distance) => {
        const db = openDatabase()

        let { lat, lng } = current_distance

        if (lat == null || lat == undefined || lat == '') {
            lat = 0
        }

        if (lng == null || lng == undefined || lng == '') {
            lng = 0
        }

        return new Promise((resolve, reject) => {
            if (isWeb()) {
            } else {
                db.transaction((tx) => {
                    let query = `
                                    SELECT maximum_distance_for_checkin, lat, lng
                                    FROM ${SCHEDULES_STORE_NAMES.STORES}
                                    WHERE _id = ?;
                        `
                    let params = [store_id]

                    tx.executeSql(
                        query,
                        params,
                        (_, result) => {
                            const maximum_distance_for_checkin = result.rows.item(0).maximum_distance_for_checkin
                            const latStore = result.rows.item(0).lat
                            const lngStore = result.rows.item(0).lng

                            if (lat == 0 || lng == 0) {
                                resolve(true)
                            }

                            if (latStore == 0 || lngStore == 0) {
                                resolve(true)
                            }

                            if (latStore == null || latStore == undefined || latStore == '') {
                                resolve(true)
                            }

                            if (lngStore == null || lngStore == undefined || lngStore == '') {
                                resolve(true)
                            }

                            if (!maximum_distance_for_checkin || maximum_distance_for_checkin === 0) {
                                resolve(true)
                            } else {
                                // a cuantos metros de distancia se encuentra mi tienda
                                const store_distance = distanceInMeters(lat, lng, latStore, lngStore)

                                if (store_distance <= maximum_distance_for_checkin) {
                                    console.log(
                                        'la distancia en m de la que estoy de la tienda es menor o igual a la maxima seteada por la tienda para hacer checkin',
                                    ) // la distancia en m de la que estoy de la tienda es menor o igual a la maxima seteada por la tienda para hacer checkin
                                    resolve(true)
                                } else {
                                    console.log(
                                        'la distancia en m de la que estoy de la tienda es mayor a la maxima seteada por la tienda para hacer checkin',
                                    ) // la distancia en m de la que estoy de la tienda es menor o igual a la maxima seteada por la tienda para hacer checkin
                                    resolve(false)
                                }
                            }
                        },
                        (_, error) => {
                            console.error('No se pudo obtener la tienda:', error)
                            reject(error)
                        },
                    )
                })
            }
        })
    }

    addForm = (form, wise = false, heladera = false, create_refrigerator = false, form_id_father = '') => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                // En el caso de web, puedes manejar la inserción del formulario de la misma manera que la tienda
                const transaction = db.transaction([SCHEDULES_STORE_NAMES.FORMS], 'readwrite')
                const objectStore = transaction.objectStore(SCHEDULES_STORE_NAMES.FORMS)

                const request = objectStore.add(form)

                request.onsuccess = () => {
                    resolve()
                }

                request.onerror = (event) => {
                    reject(event.target.error)
                }
            } else {
                db.transaction(
                    (tx) => {
                        tx.executeSql(`SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.FORMS} WHERE _id = ?`, [form._id], (_, result) => {
                            const count = result.rows.item(0).count || 0
                            if (count === 0) {
                                let insertForm = [
                                    form._id,
                                    form.index_form,
                                    form.repeat,
                                    form.can_repeat,
                                    form.icon,
                                    form.check_in,
                                    form.name_form,
                                    form.icon_check,
                                    JSON.stringify(form.type_form),
                                    JSON.stringify(form.contents),
                                    JSON.stringify(form.segments),
                                    0, // status
                                    form.progress,
                                    form.totalScore,
                                    form.show_barcode,
                                    JSON.stringify(form.tags),
                                    wise,
                                    heladera,
                                    create_refrigerator,
                                    form_id_father,
                                ]
                                tx.executeSql(
                                    `INSERT INTO ${SCHEDULES_STORE_NAMES.FORMS} (
                                        _id,
                                        index_form,
                                        repeat,
                                        can_repeat,
                                        icon,
                                        check_in,
                                        name_form,
                                        icon_check,
                                        type_form,
                                        contents,
                                        segments,
                                        status,
                                        progress,
                                        totalScore,
                                        show_barcode,
                                        tags,
                                        wise,
                                        heladera,
                                        create_refrigerator,
                                        form_id_father
                                    ) VALUES (?, ?, ?, ?,?, ?, ?, ?,?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?);`,
                                    insertForm,
                                    (_, result) => {
                                        // console.log('🚀 ~ guardo el formulario', result)
                                        resolve()
                                    },
                                    (_, error) => {
                                        console.error('Transaction error:', error)
                                        reject(error)
                                    },
                                )
                            } else {
                                if (heladera) {
                                    this.updateQuestionForms(form)
                                }

                                resolve()
                            }
                        })
                    },
                    (error) => {
                        console.error('Transaction addForm error:', error)
                        reject(error)
                    },
                    (_, success) => {
                        resolve()
                    },
                )
            }
        })
    }

    updateQuestionForms = (form) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    let query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS}
                    SET contents = ?
                    WHERE _id = ?;`

                    let queryParams = [JSON.stringify(form.contents || []), form._id]
                    tx.executeSql(
                        query,
                        queryParams,
                        (_, result) => {
                            console.log('3166 --- contents updateados')
                            resolve(result)
                        },
                        (_, error) => {
                            console.error('Transaction updateQuestionForms Error => ', error)
                            reject(error)
                        },
                    )
                },
                (transactionError) => {
                    console.error('Transaction updateQuestionForms Error => ', transactionError)
                    reject(transactionError)
                },
            )
        })
    }

    addAllForms = (forms, wise = false, heladera = false, create_refrigerator = false, form_id_father = '', batchSize = 1000) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                return
            }

            db.transaction(
                (tx) => {
                    // const batchSize = 1000 // Tamaño del lote
                    // console.log("🚀 ~ schedulesService ~ returnnewPromise ~ totalForms:", totalForms)
                    let startIndex = 0

                    const insertNextBatch = () => {
                        const batchForms = forms && forms.length > 0 ? forms.slice(startIndex, startIndex + batchSize) : []
                        if (batchForms.length > 0) {
                            const placeholders = batchForms.map(() => '(?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)').join(',')

                            const values = batchForms.flatMap((form) => [
                                form._id,
                                form.index_form,
                                form.repeat,
                                form.can_repeat,
                                form.icon,
                                form.check_in,
                                form.name_form,
                                form.icon_check,
                                JSON.stringify(form.type_form),
                                JSON.stringify(form.contents),
                                JSON.stringify(form.segments),
                                0, // status
                                form.progress,
                                form.totalScore,
                                form.show_barcode,
                                JSON.stringify(form.tags),
                                wise,
                                heladera,
                                create_refrigerator,
                                form_id_father,
                                form.start_ejecution,
                                form.end_ejecution,
                                form.link,
                                form.objective,
                                form.weigh,
                            ])

                            tx.executeSql(
                                `INSERT OR REPLACE INTO ${SCHEDULES_STORE_NAMES.FORMS} (
                                    _id,
                                    index_form,
                                    repeat,
                                    can_repeat,
                                    icon,
                                    check_in,
                                    name_form,
                                    icon_check,
                                    type_form,
                                    contents,
                                    segments,
                                    status,
                                    progress,
                                    totalScore,
                                    show_barcode,
                                    tags,
                                    wise,
                                    heladera,
                                    create_refrigerator,
                                    form_id_father,
                                    start_ejecution,
                                    end_ejecution,
                                    link,
                                    objective,
                                    weigh
                                ) VALUES ${placeholders};`,
                                values,
                                (_, result) => {
                                    startIndex += batchSize
                                    insertNextBatch() // Insertar el próximo lote
                                },
                                (_, error) => {
                                    console.error('Error en executeSql:', error, 'en la consulta:', `INSERT INTO ${SCHEDULES_STORE_NAMES.FORMS} ...`)
                                    reject(error)
                                },
                            )
                        } else {
                            resolve() // Todas las inserciones completadas
                        }
                    }

                    insertNextBatch() // Comenzar la inserción en lotes
                },
                (transactionError) => {
                    console.error('Error en transaction:', transactionError)
                    reject(transactionError)
                },
                (_, success) => {
                    resolve(success) // Puedes devolver algún indicador de éxito si es necesario
                },
            )
        })
    }

    getFormsWise = () => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
            } else {
                db.transaction(
                    (tx) => {
                        tx.executeSql(
                            `SELECT * FROM ${SCHEDULES_STORE_NAMES.FORMS} WHERE wise = 1;`,
                            [],
                            (_, result) => {
                                const forms = result.rows._array
                                console.log('forms: ', forms.length)

                                try {
                                    for (const form of forms) {
                                        form.contents = JSON.parse(form.contents)
                                        form.type_form = JSON.parse(form.type_form)
                                    }
                                } catch (error) {}

                                resolve(forms)
                            },
                            (error) => {
                                console.error('Error en la transacción getFormsWise :', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Error en la transacción getFormsWise -sqlite:', error)
                        reject(error)
                    },
                    null,
                )
            }
        })
    }

    deleteFormAnswer = (schedule_id, form_id, store_id = null) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                // Código para eliminar un registro en web (si es necesario)
                reject('La eliminación de registros no está soportada en la plataforma web.')
            } else {
                db.transaction(
                    (tx) => {
                        const generateIdDate = generateIdDateNow()
                        let query
                        let queryParams

                        if (store_id) {
                            query = `DELETE FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} 
                                    WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND day_unique_id = ?;`
                            queryParams = [schedule_id, form_id, store_id, generateIdDate]
                        } else {
                            query = `DELETE FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} 
                                    WHERE schedule_id = ? AND form_id = ? AND day_unique_id = ?;`
                            queryParams = [schedule_id, form_id, generateIdDate]
                        }

                        tx.executeSql(
                            query,
                            queryParams,
                            (_, result) => {
                                resolve(result.rowsAffected)
                            },
                            (error) => {
                                console.error('Error al eliminar el registro:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Error en la transacción deleteFormAnswer - sqlite:', error)
                        reject(error)
                    },
                    null,
                )
            }
        })
    }

    getAllCampaigns = () => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                reject(new Error('Not supported on web'))
            } else {
                db.transaction(
                    (tx) => {
                        tx.executeSql(
                            `SELECT * FROM ${SCHEDULES_STORE_NAMES.CAMPAINGS}`,
                            [],
                            (_, result) => {
                                //console.log('🚀 ~ returnnewPromise ~ result:', result)
                                const campaigns = []
                                for (let i = 0; i < result.rows.length; i++) {
                                    const row = result.rows.item(i)
                                    const scheduleIds = JSON.parse(row.schedules)
                                    campaigns.push({
                                        _id: row._id,
                                        name: row.name,
                                        code: row.code,
                                        image: row.image,
                                        date_int: row.date_init,
                                        date_finish: row.date_finish,
                                        steps: JSON.parse(row.steps),
                                        total_progres: row.total_progres,
                                        scheduleIds: scheduleIds,
                                        values: JSON.parse(row.camp_values),
                                        schedules: [],
                                        schedule_free: row.schedule_free,
                                        date_moderation_init: row.date_moderation_init,
                                        date_moderation_finish: row.date_moderation_finish,
                                        moderation: row.moderation,
                                    })
                                }
                                // console.log("🚀 ~ returnnewPromise ~ campaigns:", campaigns)

                                // Fetch schedules details for all campaigns
                                const schedulePromises = campaigns.map((campaign) => {
                                    const scheduleIds = campaign.scheduleIds
                                    const scheduleDetailPromises = scheduleIds.map((scheduleId) => {
                                        return new Promise((resolveSchedule, rejectSchedule) => {
                                            tx.executeSql(
                                                `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES} WHERE _id = ?`,
                                                [scheduleId],
                                                (_, scheduleResult) => {
                                                    if (scheduleResult.rows.length > 0) {
                                                        const scheduleRow = scheduleResult.rows.item(0)
                                                        const schedule = {
                                                            _id: scheduleRow._id,
                                                            date_finish: scheduleRow.date_finish,
                                                            date_init: scheduleRow.date_init,
                                                            form_finish:
                                                                scheduleRow.form_finish.length > 6 ? JSON.parse(scheduleRow.form_finish) : null,
                                                            form_init: scheduleRow.form_init.length > 6 ? JSON.parse(scheduleRow.form_init) : null,
                                                            stores: [], //JSON.parse(scheduleRow.stores), // [],
                                                            progress_diary: scheduleRow.progress_diary,
                                                            additional: JSON.parse(scheduleRow.additional),
                                                            distributor: JSON.parse(scheduleRow.distributor),
                                                            activities: JSON.parse(scheduleRow.activities),
                                                            others_activities: JSON.parse(scheduleRow.others_activities),
                                                            vacation: scheduleRow.vacation,
                                                            activities_advances: JSON.parse(scheduleRow.activities_advances),
                                                            others_activities_advances: JSON.parse(scheduleRow.others_activities_advances),
                                                            forms_additionals: JSON.parse(scheduleRow.forms_additionals),
                                                            forms_incidence: scheduleRow.forms_incidence,
                                                            reports: JSON.parse(scheduleRow.reports),
                                                            data_version: scheduleRow.data_version,
                                                        }
                                                        resolveSchedule(schedule)
                                                    } else {
                                                        rejectSchedule(new Error('Schedule not found'))
                                                    }
                                                },
                                                (_, error) => rejectSchedule(error),
                                            )
                                        })
                                    })

                                    return Promise.all(scheduleDetailPromises).then((schedulesData) => {
                                        // console.log('3937 -- 🚀 ~ Promise.all ~ schedulesData:', schedulesData)
                                        campaign.schedules = schedulesData
                                    })
                                })

                                Promise.all(schedulePromises)
                                    .then(() => {
                                        //   console.log("🚀 ~ Promise.all ~ campaigns:", campaigns)
                                        resolve(campaigns)
                                    })
                                    .catch((error) => reject(error))
                            },
                            (_, error) => {
                                console.error('Transaction error:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    addCampaing = (campaing) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                reject(new Error('Not supported on web'))
                return
            }

            db.transaction(
                (tx) => {
                    tx.executeSql(`SELECT * FROM ${SCHEDULES_STORE_NAMES.CAMPAINGS} WHERE _id = ?`, [campaing._id], (_, result) => {
                        const count = result.rows.length
                        let agendas_ids = campaing.schedules.map((item) => item._id)
                        if (count === 0) {
                            let insertstore = [
                                campaing._id,
                                campaing.name,
                                campaing.code,
                                campaing.image,
                                campaing.date_int,
                                campaing.date_finish,
                                JSON.stringify(campaing.steps || {}),
                                campaing.total_progres,
                                JSON.stringify(agendas_ids),
                                JSON.stringify(campaing.values),
                                campaing.schedule_free,
                                campaing.date_moderation_init,
                                campaing.date_moderation_finish,
                                campaing.moderation,
                            ]
                            tx.executeSql(
                                `INSERT INTO ${SCHEDULES_STORE_NAMES.CAMPAINGS} (
                                            _id,
                                            name,
                                            code,
                                            image,
                                            date_init,
                                            date_finish,
                                            steps,
                                            total_progres,
                                            schedules,
                                            camp_values,
                                            schedule_free,
                                            date_moderation_init,
                                            date_moderation_finish,
                                            moderation
                                        ) VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?);`,
                                insertstore,
                                (_, result) => {
                                    // console.log('🚀 ~ guardo la CAMPAING', result)
                                    resolve(result)
                                },
                                (_, error) => {
                                    console.error('Transaction error:', error)
                                    reject(error)
                                },
                            )
                        } else {
                            const existingCampaing = result.rows.item(0)
                            if (
                                existingCampaing.date_moderation_init !== campaing.date_moderation_init ||
                                existingCampaing.date_moderation_finish !== campaing.date_moderation_finish ||
                                existingCampaing.moderation !== campaing.moderation
                            ) {
                                tx.executeSql(
                                    `UPDATE ${SCHEDULES_STORE_NAMES.CAMPAINGS} SET 
                                    name = ?, 
                                    code = ?, 
                                    image = ?, 
                                    date_init = ?, 
                                    date_finish = ?, 
                                    steps = ?, 
                                    total_progres = ?, 
                                    schedules = ?, 
                                    camp_values = ?, 
                                    schedule_free = ?, 
                                    date_moderation_init = ?, 
                                    date_moderation_finish = ?, 
                                    moderation = ?
                                    
                                    WHERE _id = ?`,

                                    [
                                        campaing.name,
                                        campaing.code,
                                        campaing.image,
                                        campaing.date_int,
                                        campaing.date_finish,
                                        JSON.stringify(campaing.steps || {}),
                                        campaing.total_progres,
                                        JSON.stringify(agendas_ids),
                                        JSON.stringify(campaing.values),
                                        campaing.schedule_free,
                                        campaing.date_moderation_init,
                                        campaing.date_moderation_finish,
                                        campaing.moderation,
                                        campaing._id,
                                    ],
                                    // [campaing.date_moderation_init, campaing.date_moderation_finish, campaing.moderation, JSON.stringify(agendas_ids), campaing._id],
                                    (_, result) => {
                                        resolve(result)
                                    },
                                    (_, error) => {
                                        console.error('Transaction error:', error)
                                        reject(error)
                                    },
                                )
                            } else {
                                resolve()
                            }
                        }
                    })
                },
                (error) => {
                    console.error('Transaction addstore error:', error)
                    reject(error)
                },
                (_, success) => {
                    resolve(success)
                },
            )
        })
    }
    addSchedulesBatch = (schedules, campaign_id) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                reject(new Error('No se puede realizar una inserción masiva en IndexedDB en entorno web.'))
                return
            } else {
                db.transaction(
                    (tx) => {
                        const batchSize = 1000 // Tamaño del lote
                        const totalSchedules = schedules.length
                        let startIndex = 0

                        const insertNextBatch = () => {
                            const batchSchedules = schedules.slice(startIndex, startIndex + batchSize)
                            if (batchSchedules.length > 0) {
                                const placeholders = batchSchedules.map(() => '(?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)').join(',')
                                const values = batchSchedules.flatMap((schedule) => [
                                    schedule._id,
                                    schedule.date_finish,
                                    schedule.date_init,
                                    JSON.stringify(schedule.form_finish || {}),
                                    JSON.stringify(schedule.form_init || {}),
                                    '[]', // stores vacíos en este caso
                                    campaign_id,
                                    schedule.progress_diary,
                                    JSON.stringify(schedule.additional || {}),
                                    JSON.stringify(schedule.activities || []),
                                    JSON.stringify(schedule.others_activities || []),
                                    JSON.stringify(schedule.distributor),
                                    schedule.vacation || false,
                                    JSON.stringify(schedule.activities_advances || {}),
                                    JSON.stringify(schedule.others_activities_advances || {}),
                                    JSON.stringify(schedule.forms_additionals || []),
                                    schedule.forms_incidence || false,
                                    JSON.stringify(schedule.reports || {}),
                                    schedule.data_version,
                                ])

                                tx.executeSql(
                                    `INSERT OR REPLACE INTO ${SCHEDULES_STORE_NAMES.SCHEDULES} (
                                        _id,
                                        date_finish,
                                        date_init,
                                        form_finish,
                                        form_init,
                                        stores,
                                        campaign_id,
                                        progress_diary,
                                        additional,
                                        activities,
                                        others_activities,
                                        distributor,
                                        vacation,
                                        activities_advances,
                                        others_activities_advances,
                                        forms_additionals,
                                        forms_incidence,
                                        reports,
                                        data_version
                                    ) VALUES ${placeholders};`,
                                    values,
                                    (_, result) => {
                                        startIndex += batchSize
                                        insertNextBatch() // Insertar el próximo lote
                                    },
                                    (_, error) => {
                                        console.error('Error en la inserción del lote:', error)
                                        reject(error)
                                    },
                                )
                            } else {
                                resolve() // Todos los lotes han sido insertados
                            }
                        }

                        insertNextBatch() // Comenzar la inserción en lotes
                    },
                    (error) => {
                        console.error('Transaction error:', error)
                        reject(error)
                    },
                    (_, success) => {
                        resolve(success)
                    },
                )
            }
        })
    }

    addStores = (stores, batchSize = 1000, step, setProgress) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                reject(new Error('No se puede realizar una inserción masiva en IndexedDB en entorno web.'))
                return
            } else {
                db.transaction(
                    (tx) => {
                        // const batchSize = 1000 // Tamaño del lote
                        // const totalStores = stores.length
                        let startIndex = 0
                        const totalStores = stores.length

                        const updateProgress = (currentIndex) => {
                            const { start, end } = PROGRESS_STEPS[step]

                            const progress = Math.round(start + (end - start) * (currentIndex / totalStores))
                            setProgress(progress)
                        }

                        const insertNextBatch = () => {
                            const batchStores = stores.slice(startIndex, startIndex + batchSize)
                            if (batchStores.length > 0) {
                                for (let i = 0; i < batchStores.length; i++) {
                                    const store = batchStores[i]

                                    if (store.kpis_indicators && store.kpis_indicators.length > 0) {
                                        // console.log(`Store ${store._id} tiene KPIs`)
                                        this.updateStoreKPIS(store._id, store.kpis_indicators)
                                    }
                                }
                                const placeholders = batchStores.map(() => '(?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)').join(',')
                                const values = batchStores.flatMap((store) => [
                                    store._id,
                                    store.name,
                                    store.address,
                                    parseFloat(store.lat),
                                    parseFloat(store.lng),
                                    store.code,
                                    store.cluster,
                                    store.area,
                                    store.region,
                                    store.format,
                                    store.flag,
                                    store.maximum_distance_for_checkin,
                                    store.segment,
                                    true,
                                    store._id,
                                    store.frequency,
                                    store.topstore,
                                    store.potencial,
                                ])

                                tx.executeSql(
                                    `INSERT OR REPLACE INTO ${SCHEDULES_STORE_NAMES.STORES} (
                                            _id,
                                            name,
                                            address,
                                            lat,
                                            lng,
                                            code,
                                            cluster,
                                            area,
                                            region,
                                            format,
                                            flag,
                                            maximum_distance_for_checkin,
                                            segment,
                                            existsInDB,
                                            _id_mongoDB,
                                            frequency,
                                            topstore,
                                            potencial
                                        ) VALUES ${placeholders};`,
                                    values,
                                    (_, result) => {
                                        startIndex += batchSize
                                        if (step != null && setProgress != null) {
                                            updateProgress(startIndex)
                                        }
                                        insertNextBatch() // Insertar el próximo lote
                                    },
                                    (_, error) => {
                                        // console.log(error)
                                        reject(error)
                                    },
                                )
                            } else {
                                resolve()
                            }
                        }

                        insertNextBatch() // Comenzar la inserción en lotes
                    },
                    (error) => {
                        console.error('Transaction addStores error:', error)
                        reject(error)
                    },
                    (_, success) => {
                        resolve()
                    },
                )
            }
        })
    }

    addAllFormsPF = (data, batchSize = 1000) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                return
            } else {
                db.transaction(
                    (tx) => {
                        let startIndex = 0
                        const insertNextBatch = () => {
                            const batch = data.slice(startIndex, startIndex + batchSize)
                            if (batch.length > 0) {
                                const placeholders = batch.map(() => '(?,?,?,?,?,?,?,?,?,?)').join(',')
                                const values = batch.flatMap((item) => [
                                    item._id,
                                    item.status,
                                    item.index,
                                    item.title,
                                    item.icon,
                                    JSON.stringify(item.medias),
                                    JSON.stringify(item.type),
                                    item.start_ejecution,
                                    item.end_ejecution,
                                    item.tags,
                                ])

                                tx.executeSql(
                                    `INSERT OR REPLACE INTO ${SCHEDULES_STORE_NAMES.FORMS_PERFECT_STORE} (
                                            _id,
                                            status,
                                            form_index,
                                            title,
                                            icon,
                                            medias,
                                            type,
                                            start_ejecution,
                                            end_ejecution,
                                            tags
                                        ) VALUES ${placeholders};`,
                                    values,
                                    (_, result) => {
                                        startIndex += batchSize
                                        insertNextBatch() // Insertar el próximo lote
                                    },
                                    (_, error) => {
                                        // console.log(error)
                                        reject(error)
                                    },
                                )
                            } else {
                                resolve()
                            }
                        }

                        insertNextBatch() // Comenzar la inserción en lotes
                    },
                    (error) => {
                        console.error('Transaction addAllFormsPF error:', error)
                        reject(error)
                    },
                    (_, success) => {
                        resolve()
                    },
                )
            }
        })
    }

    addAllStoresPFrelation = (data, batchSize = 1000) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                return
            } else {
                db.transaction(
                    (tx) => {
                        let startIndex = 0
                        const insertNextBatch = () => {
                            const batch = data.slice(startIndex, startIndex + batchSize)
                            if (batch.length > 0) {
                                const placeholders = batch.map(() => '(?,?,?)').join(',')
                                const values = batch.flatMap((item) => [item.pos_id, item.pos_id, JSON.stringify(item.forms_ids)])

                                // console.log('4691 --- values: ', values)

                                tx.executeSql(
                                    `INSERT OR REPLACE INTO ${SCHEDULES_STORE_NAMES.STORES_PF_RELATION} (
                                            _id,
                                            pos_id,
                                            forms_id
                                        ) VALUES ${placeholders};`,
                                    values,
                                    (_, result) => {
                                        startIndex += batchSize
                                        insertNextBatch() // Insertar el próximo lote
                                    },
                                    (_, error) => {
                                        // console.log(error)
                                        reject(error)
                                    },
                                )
                            } else {
                                resolve()
                            }
                        }

                        insertNextBatch() // Comenzar la inserción en lotes
                    },
                    (error) => {
                        console.error('Transaction addAllStoresPFrelation error:', error)
                        reject(error)
                    },
                    (_, success) => {
                        resolve()
                    },
                )
            }
        })
    }

    deleteTableRegister = (tableName) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
            } else {
                db.transaction(
                    (tx) => {
                        tx.executeSql(`DELETE FROM ${tableName};`)
                    },
                    null,
                    () => {
                        console.log(`Tabla ${tableName} eliminada exitosamente`)
                        resolve()
                    },
                    (error) => {
                        console.error(`Error al eliminar la tabla ${tableName}:`, error)
                        reject(error)
                    },
                )
            }
        })
    }

    updateScheduleReport = (schedule_id, report) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                reject(new Error('Not supported on web'))
            } else {
                db.transaction(
                    (tx) => {
                        tx.executeSql(
                            `UPDATE ${SCHEDULES_STORE_NAMES.SCHEDULES} SET reports = ? WHERE _id = ?;`,
                            [report, schedule_id],
                            (_, result) => {
                                console.log('Reporte actualizado con éxito')
                                resolve(result)
                            },
                            (_, error) => {
                                console.error('Transaction error 1 - updateScheduleReport:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction error 2 - updateScheduleReport:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    saveFormsAdditionals = (array_forms_additional) => {
        const db = openDatabase()

        // console.log('1array_forms_additional: ', array_forms_additional.length);

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                reject(new Error('Not supported on web'))
            } else {
                db.transaction(
                    (tx) => {
                        // Eliminar todos los registros existentes
                        tx.executeSql(
                            `DELETE FROM ${SCHEDULES_STORE_NAMES.FORMS_ADDITIONALS}`,
                            [],
                            () => {
                                // console.log('2All records deleted from FORMS_ADDITIONALS');
                                // console.log('3array_forms_additional: ', array_forms_additional.length);
                                // Insertar los nuevos registros
                                array_forms_additional.forEach((form) => {
                                    tx.executeSql(
                                        `INSERT INTO ${SCHEDULES_STORE_NAMES.FORMS_ADDITIONALS} (
                                        _id, 
                                        title, 
                                        date, 
                                        date_finish, 
                                        icon, 
                                        questions
                                        ) VALUES (?, ?, ?, ?, ?, ?);`,
                                        [form._id, form.title, form.date, form.date_finish, form.icon, JSON.stringify(form.questions)],
                                        () => {
                                            // console.log('Record inserted into FORMS_ADDITIONALS');
                                        },
                                        (error) => {
                                            console.error('1Error inserting record into FORMS_ADDITIONALS:', error)
                                            reject(error)
                                        },
                                    )
                                })

                                resolve()
                            },
                            (error) => {
                                console.error('Error deleting records from FORMS_ADDITIONALS:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction error 2 - saveFormsAdditionals:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    getFormsAdditionals = () => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                reject(new Error('Not supported on web'))
            } else {
                db.transaction(
                    (tx) => {
                        tx.executeSql(
                            `SELECT * FROM ${SCHEDULES_STORE_NAMES.FORMS_ADDITIONALS};`,
                            [],
                            (_, result) => {
                                const forms_additionals = result.rows._array
                                // console.log('getFormsAdditionals: ', forms_additionals.length)

                                const newFormsAdditionals = forms_additionals.map((form) => {
                                    return {
                                        ...form,
                                        questions: JSON.parse(form.questions),
                                    }
                                })

                                resolve(newFormsAdditionals)
                            },
                            (error) => {
                                console.error('Error en la transacción getFormsAdditionals :', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Error en la transacción getFormsAdditionals -sqlite:', error)
                        reject(error)
                    },
                    null,
                )
            }
        })
    }

    getLatestFormAdditional = () => {
        const db = openDatabase()

        // console.log('4243 --- getLatestFormAdditional')
        return new Promise((resolve, reject) => {
            if (isWeb()) {
                reject(new Error('Not supported on web'))
            } else {
                // console.log('4248 --- else - getLatestFormAdditional')
                db.transaction(
                    (tx) => {
                        tx.executeSql(
                            `SELECT * 
                            FROM ${SCHEDULES_STORE_NAMES.FORMS_ADDITIONALS}
                            ORDER BY date_finish
                            DESC 
                            LIMIT 1;`,
                            [],
                            (_, result) => {
                                // console.log('4256 --- result.rows: ', result.rows.item(0))
                                let form_additional = result.rows.item(0)
                                if (form_additional && Object.keys(form_additional).length > 0) {
                                    form_additional.questions = JSON.parse(form_additional.questions)
                                    // console.log('4263 --- form_additional: ', form_additional)
                                    resolve(form_additional)
                                } else {
                                    // console.log('no hay registros')
                                    resolve(null) // No hay registros
                                }
                            },
                            (error) => {
                                console.error('1Error en la transacción getLatestFormAdditional:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('2Error en la transacción getLatestFormAdditional - sqlite:', error)
                        reject(error)
                    },
                )
            }
        })
    }
    addInfoCreateStores = (data) => {
        const db = openDatabase()

        if (data) {
            return new Promise((resolve, reject) => {
                if (isWeb()) {
                    return
                } else {
                    db.transaction(
                        (tx) => {
                            const query = `INSERT OR REPLACE INTO ${SCHEDULES_STORE_NAMES.CREATE_STORES} 
                                           (_id, forms, areas, maximum_distance_for_checkin) 
                                           VALUES (?, ?,?,?)`
                            const params = ['1', JSON.stringify(data.forms), JSON.stringify(data.areas || []), data.maximum_distance_for_checkin || 0]

                            tx.executeSql(
                                query,
                                params,
                                (_, result) => {
                                    console.log('POSInfo entry added or replaced successfully:', result)
                                    resolve(result)
                                },
                                (_, error) => {
                                    console.error('POSInfo insertion or replacement error:', error)
                                    reject(error)
                                },
                            )
                        },
                        (error) => {
                            console.error('Transaction error:', error)
                            reject(error)
                        },
                    )
                }
            })
        } else {
            return Promise.reject(new Error('Data is required'))
        }
    }

    getInfoCreateStores = async () => {
        console.log('🏪 getInfoCreateStores (wrapper)')
        
        // ✅ Usar la nueva implementación
        const scheduleRepository = await import('../db-sqlite/repositories/schedule.repository')
        return await scheduleRepository.getInfoCreateStores()
    }

    /**
     * @description crea store en pos_schedule y stores db sqlite
     */
    addStoreLocal = async (data, forms = []) => {
        console.log('🏪 addStoreLocal (wrapper) =>', data?.name)
        
        if (!data) {
            return Promise.reject(new Error('Data is required'))
        }
        
        // ✅ Usar la nueva implementación
        const scheduleRepository = await import('../db-sqlite/repositories/schedule.repository')
        return await scheduleRepository.addStoreLocal(data, forms)
    }

    addSchedulePos = (schedule_pos) => {
        const db = openDatabase()
        return new Promise((resolve, reject) => {
            if (isWeb()) {
                reject('web not found')
            } else {
                const existsInDB = schedule_pos.status_send == STATUS_SEND.SEND

                let schedulePosParams = [
                    schedule_pos._id,
                    schedule_pos.store_id,
                    schedule_pos.schedule_id,
                    JSON.stringify(schedule_pos.forms),
                    JSON.stringify([]),
                    JSON.stringify([]),
                    STORE_STATUS.PENDING,
                    schedule_pos.lat,
                    schedule_pos.lng,
                    existsInDB,
                    schedule_pos.status_send,
                ]

                let schedulePosQuery = `
                    INSERT INTO ${SCHEDULES_STORE_NAMES.SCHEDULES_POS} 
                    (_id, store_id, schedule_id, forms, forms_relevant, forms_featured, campaign_status, lat, lng, existsInDB, status_send) 
                    VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?);
                `

                db.transaction(
                    (tx) => {
                        tx.executeSql(
                            schedulePosQuery,
                            schedulePosParams,
                            () => {
                                tx.executeSql(
                                    `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_POS} WHERE _id = ?`,
                                    [schedule_pos._id],
                                    (_, result) => {
                                        resolve(result.rows.item(0))
                                    },
                                    (_, selectError) => {
                                        console.error('Error al obtener schedule POS:', selectError)
                                        reject(selectError)
                                    },
                                )
                            },
                            (_, error) => {
                                console.error('1schedule pos aggregado error:', error)
                                reject(error)
                            },
                        )
                    },
                    (_, error) => {
                        console.error('2Transaction addSchedulePos error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    addPos = (store) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                reject('web not found')
            } else {
                const existsInDB = store.status_send == STATUS_SEND.SEND

                db.transaction(
                    (tx) => {
                        const proceedInsert = () => {
                            let storeQuery = `INSERT OR REPLACE INTO ${SCHEDULES_STORE_NAMES.STORES} 
                            (_id, name, address, lat, lng, code, cluster, area, region, format, flag, maximum_distance_for_checkin, segment, distributor_id, existsInDB, organization_id, status_send) 
                            VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)`
                            let storeParams = [
                                store._id,
                                store.name,
                                store.address || '',
                                parseFloat(store.lat || 0),
                                parseFloat(store.lng || 0),
                                store.code || '',
                                store.cluster || '',
                                store.area || '',
                                store.region || '',
                                store.format || '',
                                store.flag || '',
                                store.maximum_distance_for_checking || 0,
                                store.store_segment || '',
                                store.distributor_id || null,
                                existsInDB,
                                store.organization_id || null,
                                store.status_send || STATUS_SEND.SEND,
                            ]

                            tx.executeSql(storeQuery, storeParams)

                            // hago el select despues del insert
                            const querySelect = `
                                SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES} 
                                WHERE _id = ?
                             `
                            tx.executeSql(
                                querySelect,
                                [store._id],
                                (_, result) => {
                                    const insertedPos = result.rows.item(0)
                                    resolve(insertedPos)
                                },
                                (_, error) => {
                                    console.error('Error al obtener la pos insertada:', error)
                                    reject(error)
                                    return true
                                },
                            )
                        }
                        proceedInsert()
                    },
                    (txError) => {
                        console.error('Transaction error => addPos:', txError)
                        reject(txError)
                    },
                )
            }
        })
    }

    addStoreAndSchedulePos = (dataStore, dataSchedulePos, forms = [], isCreateSuccess, data, organization_id) => {
        const db = openDatabase()

        // const status = isCreateSuccess ? STORE_STATUS.PENDING : STORE_STATUS.PENDING_CONNECTION
        const status = STORE_STATUS.PENDING

        return new Promise((resolve, reject) => {
            if (isWeb()) {
            } else {
                const newId = generateUniqueId()

                let posParams = isCreateSuccess
                    ? [
                          dataSchedulePos._id,
                          dataSchedulePos.store_id,
                          dataSchedulePos.schedule_id,
                          dataSchedulePos.forms,
                          JSON.stringify([]),
                          JSON.stringify([]),
                          status,
                          dataStore.lat,
                          dataStore.lng,
                          isCreateSuccess,
                      ]
                    : [newId, newId, data.schedule_id, forms, JSON.stringify([]), JSON.stringify([]), status, data.lat, data.lng, false]

                let storeParams = isCreateSuccess
                    ? [
                          dataStore._id,
                          dataStore.name,
                          dataStore.address || '',
                          parseFloat(dataStore.lat || 0),
                          parseFloat(dataStore.lng || 0),
                          dataStore.code || '',
                          dataStore.cluster || '',
                          dataStore.area || '',
                          dataStore.region || '',
                          dataStore.format || '',
                          dataStore.flag || '',
                          dataStore.maximum_distance_for_checking || 0,
                          dataStore.store_segment || '',
                          dataStore.distributor_id || null,
                          isCreateSuccess,
                          organization_id,
                      ]
                    : [
                          newId,
                          data.name,
                          data.address || '',
                          parseFloat(data.lat || 0),
                          parseFloat(data.lng || 0),
                          data.code || '',
                          data.cluster || '',
                          data.area || '',
                          data.region || '',
                          data.format || '',
                          data.flag || '',
                          data.maximum_distance_for_checking || 0,
                          data.store_segment || '',
                          data.distributor_id || null,
                          false,
                          organization_id,
                      ]
                db.transaction(
                    (tx) => {
                        // Inserción en la tabla SCHEDULES_POS
                        const posQuery = `INSERT INTO ${SCHEDULES_STORE_NAMES.SCHEDULES_POS} 
                        (_id, store_id, schedule_id, forms, forms_relevant, forms_featured, campaign_status, lat, lng, existsInDB) 
                        VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?);`

                        tx.executeSql(
                            posQuery,
                            posParams,
                            (_, result) => {
                                console.log('POS entry added successfully:', result)
                            },
                            (_, error) => {
                                console.error('POS insertion error:', error)
                                reject(error)
                            },
                        )

                        // Inserción en la tabla STORES
                        const storeQuery = `INSERT OR REPLACE INTO ${SCHEDULES_STORE_NAMES.STORES} 
                        (_id, name, address, lat, lng, code, cluster, area, region, format, flag, maximum_distance_for_checkin, segment, distributor_id, existsInDB, organization_id) 
                        VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)`

                        tx.executeSql(
                            storeQuery,
                            storeParams,
                            (_, result) => {
                                console.log('Store entry added successfully:', result)
                                resolve(result)
                            },
                            (_, error) => {
                                console.error('Store insertion error:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction error addStoreAndSchedulePos:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    updateStoreRecords = async (oldStoreId, newStoreId) => {
        console.log('🔄 updateStoreRecords (wrapper) =>', oldStoreId, '→', newStoreId)
        
        // ✅ Usar la nueva implementación TypeScript
        const scheduleRepository = await import('../db-sqlite/repositories/schedule.repository')
        return await scheduleRepository.updateStoreRecords(oldStoreId, newStoreId)
    }

    /**
     * 🆕 Limpia tiendas locales temporales cuando ya existe la versión definitiva
     * @description Elimina tiendas con _id que comience con "local_" si ya existe
     * la tienda definitiva en la base de datos (verificando _id_mongoDB)
     * @returns Promise con estadísticas de limpieza
     */
    cleanupLocalStores = async () => {
        console.log('🧹 cleanupLocalStores (wrapper)')
        
        // ✅ Usar la nueva implementación TypeScript
        const scheduleRepository = await import('../db-sqlite/repositories/schedule.repository')
        return await scheduleRepository.cleanupLocalStores()
    }

    getExistsInDB = (store_id) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                reject()
            } else {
                db.transaction(
                    (tx) => {
                        tx.executeSql(
                            `SELECT existsInDB, _id_mongoDB FROM ${SCHEDULES_STORE_NAMES.STORES} WHERE _id = ?;`,
                            [store_id],
                            (_, result) => {
                                const existsInDB = result.rows.item(0).existsInDB
                                const _id_mongoDB = result.rows.item(0)._id_mongoDB
                                resolve({ existsInDB, _id_mongoDB })
                            },
                            (error) => {
                                console.error('Error en la transacción getExistsInDB:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Error en la transacción getExistsInDB -sqlite:', error)
                        reject(error)
                    },
                    null,
                )
            }
        })
    }

    getPerfectStoreRelationByStore = (store_id) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
            } else {
                db.transaction(
                    (tx) => {
                        tx.executeSql(
                            `SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES_PF_RELATION} WHERE pos_id = ?;`,
                            [store_id],
                            (_, result) => {
                                const store = result.rows.item(0)
                                if (store) {
                                    store.forms_id = JSON.parse(store.forms_id)

                                    const sql = `SELECT * FROM ${SCHEDULES_STORE_NAMES.FORMS_PERFECT_STORE} WHERE _id IN (${store.forms_id
                                        .map(() => '?')
                                        .join(',')});`

                                    // console.log('5210 --- sql: ', JSON.stringify(sql))
                                    // console.log('🚀 ~ schedulesService ~ returnnewPromise ~ sql:', sql)
                                    tx.executeSql(sql, store.forms_id, (_, result) => {
                                        const forms = result.rows._array
                                        if (forms.length > 0) {
                                            const formsUpdate = forms.map((form) => {
                                                return {
                                                    ...form,
                                                    medias: JSON.parse(form.medias),
                                                    type: JSON.parse(form.type),
                                                    tags: JSON.parse(form.tags) || [],
                                                }
                                            })
                                            // console.log('5224 --- formsUpdate: ', formsUpdate)
                                            resolve(formsUpdate)
                                        } else {
                                            resolve([])
                                        }
                                    })
                                } else {
                                    resolve([])
                                }
                            },
                            (error) => {
                                console.error('Error en la transacción getPerfectStoreRelationByStore:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Error en la transacción getPerfectStoreRelationByStore -sqlite:', error)
                        reject(error)
                    },
                    null,
                )
            }
        })
    }

    hasStoresInDatabase = (totalStores) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                reject(new Error('Not supported on web'))
            } else {
                db.transaction(
                    (tx) => {
                        tx.executeSql(`SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.STORES};`, [], (_, result) => {
                            const count = result.rows.item(0).count || 0
                            console.log('🚀 ~ hasStoresInDatabase ~ stores guardados.. :', count)
                            console.log('🚀 ~ hasStoresInDatabase ~ stores que deberia tener.. :', totalStores)

                            resolve(count == totalStores)
                        })
                    },
                    (error) => {
                        console.error('hasStoresInDatabase - Transaction error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    hasSchedulePosInDatabase = (totalSchedulePos) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                reject(new Error('Not supported on web'))
            } else {
                db.transaction(
                    (tx) => {
                        tx.executeSql(`SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_POS};`, [], (_, result) => {
                            const count = result.rows.item(0).count || 0
                            console.log('🚀 ~ hasSchedulePosInDatabase ~ schedule_pos guardados.. :', count)
                            console.log('🚀 ~ hasSchedulePosInDatabase ~ schedule_pos que deberia tener.. :', totalSchedulePos)
                            resolve(count == totalSchedulePos)
                        })
                    },
                    (error) => {
                        console.error('hasSchedulePosInDatabase - Transaction error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    getCountSchedulePos = () => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                reject(new Error('Not supported on web'))
            } else {
                db.transaction(
                    (tx) => {
                        tx.executeSql(`SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_POS};`, [], (_, result) => {
                            const count = result.rows.item(0).count || 0
                            resolve(count)
                        })
                    },
                    (error) => {
                        console.error('getCountSchedulePos - Transaction error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    getCountStores = () => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                reject(new Error('Not supported on web'))
            } else {
                db.transaction(
                    (tx) => {
                        tx.executeSql(`SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.STORES};`, [], (_, result) => {
                            const count = result.rows.item(0).count || 0
                            resolve(count)
                        })
                    },
                    (error) => {
                        console.error('getCountStores - Transaction error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    getCountForms = () => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                reject(new Error('Not supported on web'))
            } else {
                db.transaction(
                    (tx) => {
                        tx.executeSql(`SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.FORMS};`, [], (_, result) => {
                            const count = result.rows.item(0).count || 0
                            resolve(count)
                        })
                    },
                    (error) => {
                        console.error('getCountFormsStore - Transaction error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    hasFormsPerfectStoreInDatabase = (totalFormsPerfecStore) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                reject(new Error('Not supported on web'))
            } else {
                db.transaction(
                    (tx) => {
                        tx.executeSql(`SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.FORMS_PERFECT_STORE};`, [], (_, result) => {
                            const count = result.rows.item(0).count || 0
                            console.log('🚀 ~ hasFormsPerfectStoreInDatabase ~ FormsPerfecStore guardados.. :', count)
                            console.log('🚀 ~ hasFormsPerfectStoreInDatabase ~ totalFormsPerfecStore que deberia tener.. :', totalFormsPerfecStore)
                            resolve(count == totalFormsPerfecStore)
                        })
                    },
                    (error) => {
                        console.error('hasFormsPerfectStoreInDatabase - Transaction error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    hasRelationPerfectStoreInDatabase = (totalRelationPerfectStore) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                reject(new Error('Not supported on web'))
            } else {
                db.transaction(
                    (tx) => {
                        tx.executeSql(`SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.STORES_PF_RELATION};`, [], (_, result) => {
                            const count = result.rows.item(0).count || 0
                            console.log('🚀 ~ hasRelationPerfectStoreInDatabase ~ relation ps guardados.. :', count)
                            console.log('🚀 ~ hasRelationPerfectStoreInDatabase ~ relation ps que deberia tener.. :', totalRelationPerfectStore)
                            resolve(count == totalRelationPerfectStore)
                        })
                    },
                    (error) => {
                        console.error('hasRelationPerfectStoreInDatabase - Transaction error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    CheckSavedRecords = (table, total) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                reject(new Error('Not supported on web'))
            } else {
                db.transaction(
                    (tx) => {
                        tx.executeSql(
                            `
                            SELECT COUNT(*) as count 
                            FROM ${table}
                            WHERE wise = 0;`,
                            [],
                            (_, result) => {
                                const count = result.rows.item(0).count || 0
                                resolve(count >= total)
                            },
                        )
                    },
                    (error) => {
                        console.error('CheckSavedRecords - Transaction error:', error)
                        resolve(false)
                        //reject(error)
                    },
                )
            }
        })
    }

    savePendingImage = (filename, pickerResult_uri) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                reject(new Error('Not supported on web'))
            } else {
                db.transaction(
                    (tx) => {
                        // Verificar si ya existe la imagen
                        const checkQuery = `SELECT * FROM ${SCHEDULES_STORE_NAMES.UPLOAD_IMAGES_PENDING} WHERE filename = ?`
                        tx.executeSql(
                            checkQuery,
                            [filename],
                            (_, { rows }) => {
                                if (rows.length > 0) {
                                    console.log('Imagen ya existe en la tabla de pendientes, no se guarda nuevamente.')
                                    resolve(false) // Ya existe, no se guarda
                                } else {
                                    // Insertar la imagen si no existe
                                    const insertQuery = `INSERT INTO ${SCHEDULES_STORE_NAMES.UPLOAD_IMAGES_PENDING} (
                                        filename,
                                        pickerResult_uri
                                    ) VALUES (?, ?)`
                                    tx.executeSql(
                                        insertQuery,
                                        [filename, pickerResult_uri],
                                        (_, result) => {
                                            console.log('Imagen guardada en la tabla de pendientes.')
                                            resolve(true) // Guardado exitoso
                                        },
                                        (_, error) => {
                                            console.error('Error al insertar en tabla de pendientes:', error)
                                            reject(error)
                                        },
                                    )
                                }
                            },
                            (_, error) => {
                                console.error('Error al verificar la tabla de pendientes:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    getPendingsImage = () => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                reject(new Error('Not supported on web'))
            } else {
                db.transaction(
                    (tx) => {
                        const query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.UPLOAD_IMAGES_PENDING}`
                        tx.executeSql(
                            query,
                            [],
                            (_, { rows }) => {
                                resolve(rows._array)
                            },
                            (_, error) => {
                                console.error('Error al verificar la tabla de pendientes:GET', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    deletePendingImage = (filename) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                reject(new Error('Not supported on web'))
            } else {
                db.transaction(
                    (tx) => {
                        const query = `DELETE FROM ${SCHEDULES_STORE_NAMES.UPLOAD_IMAGES_PENDING} WHERE filename = ?`
                        tx.executeSql(
                            query,
                            [filename],
                            (_, result) => {
                                if (result.rowsAffected > 0) {
                                    console.log(`Imagen con filename "${filename}" eliminada correctamente.`)
                                    resolve(true) // Imagen eliminada
                                } else {
                                    console.log(`No se encontró una imagen con filename "${filename}" para eliminar.`)
                                    resolve(false) // No se encontró la imagen
                                }
                            },
                            (_, error) => {
                                console.error('Error al intentar eliminar la imagen de la tabla de pendientes:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    updateFormsTotals = (schedule_id, form_id, count = null, count_of_done = null, name_form = null) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                console.warn('updateFormsTotals no está disponible en la plataforma web.')
                return resolve(null)
            } else {
                db.transaction(
                    (tx) => {
                        const whereClause = `_id = ? AND schedule_id = ?`
                        const selectParams = [form_id, schedule_id]

                        // Consulta SELECT
                        const query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.FORMS_TOTALS} WHERE ${whereClause};`

                        tx.executeSql(
                            query,
                            selectParams,
                            (_, result) => {
                                if (result.rows._array.length > 0) {
                                    // Registro existente: actualizar valores
                                    let updateQuery = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_TOTALS} SET `
                                    let updateParams = []

                                    if (count !== null) {
                                        updateQuery += `count = count + ?, `
                                        updateParams.push(count)
                                    }
                                    if (count_of_done !== null) {
                                        updateQuery += `count_of_done = count_of_done + ?, `
                                        updateParams.push(count_of_done)
                                    }

                                    // Remover la coma final y añadir cláusula WHERE
                                    updateQuery = updateQuery.slice(0, -2) + ` WHERE ${whereClause};`
                                    updateParams.push(...selectParams)

                                    tx.executeSql(
                                        updateQuery,
                                        updateParams,
                                        (_, updateResult) => {
                                            resolve(updateResult)
                                        },
                                        (_, error) => {
                                            console.error('Error en la actualización:', error)
                                            reject(error)
                                        },
                                    )
                                } else {
                                    if (count != null) {
                                        // Registro no existente: insertar nuevo
                                        const insertQuery = `
                                            INSERT INTO ${SCHEDULES_STORE_NAMES.FORMS_TOTALS} 
                                            (_id, schedule_id, name, count, count_of_done) 
                                            VALUES (?, ?, ?, ?, ?);
                                        `
                                        const insertParams = [form_id, schedule_id, name_form || '', count || 0, count_of_done || 0]

                                        tx.executeSql(
                                            insertQuery,
                                            insertParams,
                                            (_, insertResult) => {
                                                resolve(insertResult)
                                            },
                                            (_, error) => {
                                                console.error('Error en la inserción:', error)
                                                reject(error)
                                            },
                                        )
                                    }
                                }
                            },
                            (_, error) => {
                                console.error('Error en la consulta SELECT:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Error en la transacción:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    get_name_forms = (forms_required_names, forms, forms_store) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                return
            } else {
                db.transaction(
                    (tx) => {
                        tx.executeSql(
                            `SELECT name_form, _id FROM ${SCHEDULES_STORE_NAMES.FORMS} WHERE _id IN (${forms_store.map(() => '?').join(',')})`,
                            [...forms_store],
                            (_, result) => {
                                if (result.rows._array.length > 0) {
                                    const get_forms = result.rows._array
                                    let result_forms = []
                                    forms_required_names.map((name) => {
                                        const form_name = get_forms.find((f) => f.name_form == name)
                                        if (form_name && forms.some((f) => f == form_name._id)) {
                                            result_forms.push({ _id: form_name._id, name: form_name.name_form })
                                        }
                                    })

                                    resolve(result_forms) // Solo devolver el nombre del formulario
                                } else {
                                    resolve(null) // El formulario no fue encontrado
                                }
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction getFormName error:', error)
                        reject(error)
                    },
                )
            }
        })
    }
    getScheduleStore = (scheduleId, storeId) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    tx.executeSql(
                        `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_POS} WHERE schedule_id = ? AND store_id = ? ;`,
                        [scheduleId, storeId],
                        (_, result) => {
                            if (result.rows._array.length > 0) {
                                let schedule_store = {
                                    _id: result.rows._array[0]._id,
                                    forms: JSON.parse(result.rows._array[0].forms),
                                    store_id: result.rows._array[0].store_id,
                                    status: result.rows._array[0].status,
                                }
                                resolve(schedule_store)
                            } else {
                                resolve(null)
                            }
                        },
                        (_, error) => {
                            console.error('Transaction error:', error)
                            reject(error)
                        },
                    )
                },
                (error) => {
                    console.error('Transaction getPosSchedulesByScheduleId error:', error)
                    reject(error)
                },
            )
        })
    }
    deleteAllTablesTotalForms = () => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
            } else {
                db.transaction(
                    (tx) => {
                        tx.executeSql(`SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.FORMS_TOTALS};`, [], (_, result) => {
                            const count = result.rows.item(0).count
                            if (count > 0) {
                                tx.executeSql(`DELETE FROM ${SCHEDULES_STORE_NAMES.FORMS_TOTALS};`)
                            }
                        })
                    },
                    null,
                    () => {
                        resolve()
                    },
                    (error) => {
                        console.log('🚀 ~ returnnewPromise ~ error:', error)
                        console.error('Error al eliminar las tablas learning:', error)
                        reject(error)
                    },
                )
            }
        })
    }
    upsertScheduleAdvanceLocal = (schedule_id, store_id, status, dateInit, forms = [], objectives = [], textAnswer = null) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    // Obtén la fecha local en formato YYYY-MM-DD
                    const today = new Date().toISOString().split('T')[0] // Solo la parte de la fecha (YYYY-MM-DD)

                    // Consulta el registro existente para la fecha local de hoy
                    tx.executeSql(
                        `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} 
                         WHERE schedule_id = ? AND pos_id = ? AND date_init >= ?`,
                        [schedule_id, store_id, today],
                        (_, result) => {
                            let existingRecord = result.rows._array.length > 0 ? result.rows._array[0] : null
                            let existingForms = []
                            if (existingRecord && existingRecord.forms) {
                                try {
                                    existingForms = JSON.parse(existingRecord.forms)
                                } catch (parseError) {
                                    console.error('Error parsing existing forms:', parseError)
                                    reject(parseError)
                                    return
                                }
                            }

                            if (forms.length > 0 && (existingForms.length == 0 || !existingForms.find((f) => f._id == forms[0]['_id']))) {
                                existingForms.push(forms[0])
                            }

                            const query = existingRecord
                                ? `UPDATE ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES}
                                   SET forms = ?
                                   WHERE _id = ?`
                                : `INSERT INTO ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES}
                                   (_id, schedule_id, pos_id, status, date_init, forms, objectives, date_modified, incidence_answer)
                                   VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`

                            const values = existingRecord
                                ? [JSON.stringify(existingForms), existingRecord._id]
                                : [
                                      generateUniqueId(),
                                      schedule_id,
                                      store_id,
                                      status,
                                      today,
                                      JSON.stringify(forms),
                                      JSON.stringify(objectives),
                                      today,
                                      textAnswer,
                                  ]

                            tx.executeSql(
                                query,
                                values,
                                (_, result) => {
                                    resolve(result)
                                },

                                (_, error) => {
                                    console.error('Error during insert/update:', error)
                                    reject(error)
                                },
                            )
                        },
                        (_, selectError) => {
                            console.error('Error selecting record:', selectError)
                            reject(selectError)
                        },
                    )
                },
                (transactionError) => {
                    console.error('Transaction error:', transactionError)
                    reject(transactionError)
                },
            )
        })
    }
    findAndUpdateStatusProgres = (schedule_id, store_id) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    // fecha local en formato YYYY-MM-DD
                    const today = new Date().toISOString().split('T')[0]

                    tx.executeSql(
                        `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} 
                         WHERE schedule_id = ? AND pos_id = ? AND date_init >= ?`,
                        [schedule_id, store_id, today],
                        (_, result) => {
                            const records = result.rows._array
                            if (records.length === 0) {
                                resolve(false) // No hay registro para hoy
                                return
                            }

                            // Obtiene el registro
                            const record = records[0]
                            let objectives = []
                            let forms = []
                            try {
                                if (record.objectives) {
                                    objectives = JSON.parse(record.objectives)
                                }
                                if (record.forms) {
                                    forms = JSON.parse(record.forms)
                                }
                            } catch (parseError) {
                                console.error('Error parsing objectives or forms:', parseError)
                                reject(parseError)
                                return
                            }

                            if (objectives.length === 0 || forms.length == 0) {
                                resolve(false)
                                return
                            }

                            // Verifica si todos los objectives están en forms
                            const allObjectivesCompleted = objectives.every((objective) => forms.some((form) => form._id === objective._id))

                            resolve(allObjectivesCompleted) // Devuelve true si todos los objetivos están completados
                        },
                        (_, error) => {
                            console.error('Error fetching record:', error)
                            reject(error)
                        },
                    )
                },
                (transactionError) => {
                    console.error('Transaction error:', transactionError)
                    reject(transactionError)
                },
            )
        })
    }
    countCompletedObjectivesForSchedule = (schedule_id) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    const today = new Date().toISOString().split('T')[0]
                    tx.executeSql(
                        `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} 
                         WHERE schedule_id = ?`,
                        [schedule_id],
                        (_, result) => {
                            const records = result.rows._array

                            if (records.length === 0) {
                                resolve(0) // No hay registros para la agenda
                                return
                            }

                            let completedCount = 0
                            let storesCounted = new Set()

                            records.forEach((record) => {
                                let objectives = []
                                let forms = []

                                // Parsear objectives y forms
                                try {
                                    if (record.objectives) {
                                        objectives = JSON.parse(record.objectives)
                                    }
                                    if (record.forms) {
                                        forms = JSON.parse(record.forms)
                                    }
                                } catch (parseError) {
                                    console.error('Error parsing objectives or forms:', parseError)
                                    reject(parseError)
                                    return
                                }

                                //Verifica si todos los objectives están en forms
                                if (objectives && objectives.length > 0 && forms && forms.length > 0 && !storesCounted.has(record.pos_id)) {
                                    const allObjectivesCompleted = objectives.every((objective) => forms.some((form) => form._id === objective._id))

                                    if (allObjectivesCompleted) {
                                        storesCounted.add(record.pos_id)
                                        completedCount++
                                    }
                                }
                                // if (objectives && objectives.length > 0 && forms && forms.length > 0) {
                                //     const allObjectivesCompleted = objectives.every((objective) => forms.some((form) => form._id === objective._id))

                                //     if (allObjectivesCompleted) {
                                //         storesCounted.add(record.pos_id);
                                //         completedCount++
                                //     }
                                // }
                            })

                            resolve(completedCount) // Devuelve el total de registros completados
                        },
                        (_, error) => {
                            console.error('Error fetching records:', error)
                            reject(error)
                        },
                    )
                },
                (transactionError) => {
                    console.error('Transaction error:', transactionError)
                    reject(transactionError)
                },
            )
        })
    }

    getFormsRealizedByStore = (schedule_id, store_id_1, store_id_2, day_unique_id = null) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    let query, params

                    if (store_id_2 && store_id_2 != null && store_id_2 != undefined) {
                        query = `
                            SELECT * 
                            FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} 
                            WHERE schedule_id = ? AND (store_id = ? OR store_id = ?)
                        `

                        params = [schedule_id, store_id_1, store_id_2]
                    } else {
                        query = `
                        SELECT * 
                        FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} 
                        WHERE schedule_id = ? AND store_id = ?
                    `
                        params = [schedule_id, store_id_1]
                    }

                    if (day_unique_id != null && day_unique_id != undefined) {
                        query += ` AND day_unique_id = ?`
                        params.push(day_unique_id)
                    }

                    console.log('getFormsRealizedByStore query:', query, 'params:', params)

                    tx.executeSql(
                        query,
                        params,
                        (_, result) => {
                            if (result.rows._array.length > 0) {
                                let modifiedResult = result.rows._array
                                    .map((form) => {
                                        form.answers = JSON.parse(form.answers)
                                        return form
                                    })
                                    .filter((form) => form.answers.length > 0)

                                resolve(modifiedResult)
                            } else {
                                resolve([])
                            }
                        },
                        (_, error) => {
                            console.error('Error fetching forms:', error)
                            reject(error)
                        },
                    )
                },
                (transactionError) => {
                    console.error('Transaction error:', transactionError)
                    reject(transactionError)
                },
            )
        })
    }

    getFQAByStore = (formsIds, storeId, scheduleId) => {
        const db = openDatabase()
        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    let query, params

                    const generateIdDate = generateIdDateNow()

                    query = `
                        SELECT *
                        FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                        WHERE day_unique_id = ? AND schedule_id = ? AND store_id = ? AND form_id IN (${formsIds.map(() => '?').join(',')});

                    `
                    params = [generateIdDate, scheduleId, storeId, ...formsIds]

                    tx.executeSql(
                        query,
                        params,
                        (_, result) => {
                            if (result.rows._array.length > 0) {
                                let modifiedResult = result.rows._array.map((form) => {
                                    form.answers = JSON.parse(form.answers)
                                    return form
                                })
                                resolve(modifiedResult)
                            }
                        },
                        (_, error) => {
                            console.error('getFQAByStore - Error fetching forms:', error)
                            reject(error)
                        },
                    )
                },
                (transactionError) => {
                    console.error('getFQAByStore - Transaction error:', transactionError)
                    reject(transactionError)
                },
            )
        })
    }

    getFormsByStore = (schedule_id, store_id_1, store_id_2) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    let query = `
                        SELECT * 
                        FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} 
                        WHERE schedule_id = ? AND store_id = ?;
                    `
                    let params = [schedule_id, store_id_1]

                    if (store_id_2 && store_id_2 != null && store_id_2 != undefined) {
                        query = `
                            SELECT * 
                            FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} 
                            WHERE schedule_id = ? AND (store_id = ? OR store_id = ?);
                        `

                        params = [schedule_id, store_id_1, store_id_2]
                    }

                    tx.executeSql(
                        query,
                        params,
                        (_, result) => {
                            if (result.rows._array.length > 0) {
                                let modifiedResult = result.rows._array
                                    .map((form) => {
                                        form.answers = JSON.parse(form.answers)
                                        return form
                                    })
                                    .filter((form) => form.answers.length > 0)

                                resolve(modifiedResult)
                            } else {
                                resolve([])
                            }
                        },
                        (_, error) => {
                            console.error('Error fetching forms:', error)
                            reject(error)
                        },
                    )
                },
                (transactionError) => {
                    console.error('Transaction error:', transactionError)
                    reject(transactionError)
                },
            )
        })
    }

    updateKeyCheckoutFQA = (value, register_id) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    const query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                    SET checkout = ?
                                    WHERE _id = ?;`
                    const paramsQuery = [value, register_id]

                    tx.executeSql(
                        query,
                        paramsQuery,
                        (_, result) => {
                            console.log(`6387 - FORM QUESTIONS ANSWERS updated checkout = ${value} successfully:`, result)
                            resolve(result)
                        },
                        (_, error) => {
                            console.error('6390 - Error updating checkout FORMS_QUESTION_ANSWERS:', error)
                            reject(error)
                        },
                    )
                },
                (transactionError) => {
                    console.error('Transaction error:', transactionError)
                    reject(transactionError)
                },
            )
        })
    }

    saveAllLinksAssetsPS = (dataLinks) => {
        const db = openDatabase()

        console.log('dataLinks:', dataLinks.length)

        // array de urls
        return new Promise((resolve, reject) => {
            if (!dataLinks || dataLinks.length === 0) {
                console.error('6537 -- dataLinks vacio')
                resolve()
            }
            db.transaction(
                (tx) => {
                    const statusNoDownload = 0
                    dataLinks.forEach((link, index) => {
                        const query = `INSERT INTO ${SCHEDULES_STORE_NAMES.ASSETS_PF_LINKS}
                                    (indexRoute, status, rute)
                                    VALUES (?, ?, ?);`
                        const paramsQuery = [index, statusNoDownload, link]

                        tx.executeSql(
                            query,
                            paramsQuery,
                            (_, result) => {
                                // console.log(`Link inserted successfully:`, result)
                                resolve(result)
                            },
                            (_, error) => {
                                console.error('Error inserting link:', error)
                                reject(error)
                            },
                        )
                    })
                },
                (transactionError) => {
                    console.error('Transaction error:', transactionError)
                    reject(transactionError)
                },
            )
        })
    }

    updateStatusLinkAssetsPS = (status, rute) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    const query = `UPDATE ${SCHEDULES_STORE_NAMES.ASSETS_PF_LINKS}
                                    SET status = ?
                                    WHERE rute = ?;`
                    const paramsQuery = [status, rute]

                    tx.executeSql(
                        query,
                        paramsQuery,
                        (_, result) => {
                            // console.log(`Link updated successfully:`, result)
                            resolve(result)
                        },
                        (_, error) => {
                            console.error('Error updating link:', error)
                            reject(error)
                        },
                    )
                },
                (transactionError) => {
                    console.error('Transaction error:', transactionError)
                    reject(transactionError)
                },
            )
        })
    }

    getLinksAssetsPSDonwloadFailed = () => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    tx.executeSql(
                        `SELECT * FROM ${SCHEDULES_STORE_NAMES.ASSETS_PF_LINKS} WHERE status = 0;`,
                        [],
                        (_, result) => {
                            if (result.rows._array.length > 0) {
                                resolve(result.rows._array)
                            } else {
                                resolve([])
                            }
                        },
                        (_, error) => {
                            console.error('Error fetching links:', error)
                            reject(error)
                        },
                    )
                },
                (transactionError) => {
                    console.error('Transaction error:', transactionError)
                    reject(transactionError)
                },
            )
        })
    }

    isTotalAssetsDownloaded = () => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    tx.executeSql(
                        `SELECT COUNT(*) as count 
                        FROM ${SCHEDULES_STORE_NAMES.ASSETS_PF_LINKS}
                        WHERE status = 0;`,
                        [],
                        (_, result) => {
                            const count = result.rows.item(0).count
                            resolve(count === 0) // Devuelve true si todos los assets están descargados
                        },
                        (_, error) => {
                            console.error('Error fetching links:', error)
                            reject(error)
                        },
                    )
                },
                (transactionError) => {
                    console.error('Transaction error:', transactionError)
                    reject(transactionError)
                },
            )
        })
    }

    // create pos
    saveFieldsCreatePos = (fields) => {
        if (!fields || fields.length == 0) {
            console.error('invalid Fields')
            throw new Error('invalid Fields')
        }
        const db = openDatabase()
        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    fields.forEach((field) => {
                        tx.executeSql(
                            `INSERT OR REPLACE INTO ${SCHEDULES_STORE_NAMES.STORES_CREATE_FIELDS} 
                                (_id, name, title, type, required, dependency, placeholder, options, optionsFrom, configure, validate_unique, table_name)
                             VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
                            [
                                field._id,
                                field.name,
                                field.title,
                                field.type,
                                field.required ? 1 : 0,
                                field.dependency,
                                field.placeholder,
                                JSON.stringify(field.options),
                                field.optionsFrom,
                                JSON.stringify(field.configure) || null,
                                field.validate_unique ? 1 : 0,
                                field.table || null,
                            ],
                        )
                    })
                },
                (transactionError) => {
                    console.error('Transaction error => saveFieldsCreatePos:', transactionError)
                    reject(transactionError)
                },
                () => {
                    resolve()
                },
            )
        })
    }
    saveFieldsOrganizationCreatePos = (fields) => {
        if (!fields || fields.length == 0) {
            console.error('invalid Fields')
            throw new Error('invalid Fields')
        }
        const db = openDatabase()
        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    fields.forEach((field) => {
                        tx.executeSql(
                            `INSERT OR REPLACE INTO ${SCHEDULES_STORE_NAMES.STORES_CREATE_FIELDS_ORGANIZATIONS} 
                                (_id, name, title, type, required, dependency, placeholder, options, optionsFrom, configure, validate_unique, table_name)
                             VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
                            [
                                field._id,
                                field.name,
                                field.title,
                                field.type,
                                field.required ? 1 : 0,
                                field.dependency,
                                field.placeholder,
                                JSON.stringify(field.options),
                                field.optionsFrom,
                                JSON.stringify(field.configure) || null,
                                field.validate_unique ? 1 : 0,
                                field.table || null,
                            ],
                        )
                    })
                },
                (transactionError) => {
                    console.error('Transaction error => saveFieldsOrganizationCreatePos:', transactionError)
                    reject(transactionError)
                },
                () => {
                    resolve()
                },
            )
        })
    }
    getFieldsCreatePos = () => {
        const db = openDatabase()
        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    tx.executeSql(`SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES_CREATE_FIELDS}`, [], (_, result) => {
                        const fields = result.rows._array.map((row) => ({
                            _id: row._id,
                            name: row.name,
                            title: row.title,
                            type: row.type,
                            required: row.required === 1,
                            dependency: row.dependency,
                            placeholder: row.placeholder,
                            options: JSON.parse(row.options || '[]'),
                            optionsFrom: row.optionsFrom,
                            configure: row.configure ? JSON.parse(row.configure) : null,
                            validate_unique: row.validate_unique === 1,
                            table: row.table_name || null,
                        }))
                        resolve(fields)
                    })
                },
                (transactionError) => {
                    console.error('Transaction error => getFieldsCreatePos:', transactionError)
                    reject(transactionError)
                },
            )
        })
    }
    getFieldOrganizationsCreatePos = () => {
        const db = openDatabase()
        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    tx.executeSql(`SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES_CREATE_FIELDS_ORGANIZATIONS}`, [], (_, result) => {
                        const fields = result.rows._array.map((row) => ({
                            _id: row._id,
                            name: row.name,
                            title: row.title,
                            type: row.type,
                            required: row.required === 1,
                            dependency: row.dependency,
                            placeholder: row.placeholder,
                            options: JSON.parse(row.options || '[]'),
                            optionsFrom: row.optionsFrom,
                            configure: row.configure ? JSON.parse(row.configure) : null,
                            validate_unique: row.validate_unique === 1,
                            table: row.table_name || null,
                        }))
                        resolve(fields)
                    })
                },
                (transactionError) => {
                    console.error('Transaction error => getFieldORganizationsCreatePos:', transactionError)
                    reject(transactionError)
                },
            )
        })
    }

    saveCountryData = (country) => {
        const db = openDatabase()
        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    tx.executeSql(
                        `INSERT OR REPLACE INTO ${SCHEDULES_STORE_NAMES.STORES_CREATE_COUNTRIES} (_id, prefix, name, geo_code) VALUES (?, ?, ?, ?)`,
                        [country._id, country.prefix, country.name, country.geo_code],
                    )

                    country.provinces.forEach((province) => {
                        tx.executeSql(
                            `INSERT OR REPLACE INTO ${SCHEDULES_STORE_NAMES.STORES_CREATE_PROVINCES} (_id, name, country_id) VALUES (?, ?, ?)`,
                            [province._id, province.name, country._id],
                        )

                        province.cities.forEach((city) => {
                            tx.executeSql(
                                `INSERT OR REPLACE INTO ${SCHEDULES_STORE_NAMES.STORES_CREATE_CITIES} (_id, name, province_id, country_id) VALUES (?, ?, ?, ?)`,
                                [city._id, city.name, province._id, country._id],
                            )
                        })
                    })
                },
                (error) => {
                    console.error('Error saving country data:', error)
                    reject(error)
                },
                () => {
                    resolve()
                },
            )
        })
    }

    getLocationData = () => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    tx.executeSql(`SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES_CREATE_COUNTRIES};`, [], (_, countriesResult) => {
                        const countriesRows = countriesResult.rows._array

                        tx.executeSql(`SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES_CREATE_PROVINCES};`, [], (_, provinceResult) => {
                            const provinceRows = provinceResult.rows._array

                            tx.executeSql(`SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES_CREATE_CITIES};`, [], (_, citiesResult) => {
                                const citiesRows = citiesResult.rows._array

                                const dataReturn = {
                                    countries: countriesRows,
                                    provinces: provinceRows,
                                    cities: citiesRows,
                                }

                                resolve(dataReturn)
                            })
                        })
                    })
                },
                (error) => {
                    console.error('Error fetching getLocationData:', error)
                    reject(error)
                },
            )
        })
    }

    getCountryData = (countryId) => {
        console.log('getCountryData => countryId= ', countryId)
        if (!countryId || getCountryData.length == 0) {
            throw new Error('countryId invalid')
        }
        const db = openDatabase()
        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    tx.executeSql(
                        `SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES_CREATE_COUNTRIES} WHERE _id = ?;`,
                        [countryId],
                        (_, countryResult) => {
                            console.log('countryResult => ', countryResult)
                            if (countryResult.rows.length === 0) {
                                return reject(new Error('Country not found'))
                            }

                            const countryRow = countryResult.rows.item(0)
                            console.log('countryRow => ', countryRow)
                            const country = {
                                _id: countryRow._id,
                                name: countryRow.name,
                                prefix: countryRow.prefix,
                                geo_code: countryRow.geo_code,
                                provinces: [],
                            }

                            tx.executeSql(
                                `SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES_CREATE_PROVINCES} WHERE country_id = ?`,
                                [countryId],
                                (_, provinceResult) => {
                                    const provinces = []

                                    const provinceRows = provinceResult.rows._array
                                    let remaining = provinceRows.length
                                    console.log('remaining => ', remaining)

                                    if (remaining === 0) {
                                        country.provinces = []
                                        resolve(country)
                                    }

                                    provinceRows.forEach((provinceRow) => {
                                        tx.executeSql(
                                            `SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES_CREATE_CITIES} WHERE province_id = ?`,
                                            [provinceRow._id],
                                            (_, cityResult) => {
                                                const province = {
                                                    _id: provinceRow._id,
                                                    name: provinceRow.name,
                                                    cities: cityResult.rows._array.map((cityRow) => ({
                                                        _id: cityRow._id,
                                                        name: cityRow.name,
                                                    })),
                                                }

                                                provinces.push(province)
                                                remaining--

                                                if (remaining === 0) {
                                                    country.provinces = provinces
                                                    resolve(country)
                                                }
                                            },
                                        )
                                    })
                                },
                            )
                        },
                    )
                },
                (error) => {
                    console.error('Error fetching country data:', error)
                    reject(error)
                },
            )
        })
    }

    getProvinceById = (provinceId) => {
        const db = openDatabase()
        if (!provinceId || provinceId.trim().length == 0) {
            throw new Error('provinceId invalid')
        }
        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    const query = `
                            SELECT * 
                            FROM ${SCHEDULES_STORE_NAMES.STORES_CREATE_PROVINCES}
                            WHERE _id = ?;
                        `
                    const params = [provinceId]
                    tx.executeSql(query, params, (_, result) => {
                        const row = result.rows.length > 0 ? result.rows.item(0) : null
                        resolve(row)
                    })
                },
                (transactionError) => {
                    console.error('Transaction error => getProvinceById:', transactionError)
                    reject(transactionError)
                },
            )
        })
    }

    getDataAddressByCityId = (cityId) => {
        const db = openDatabase()
        if (!cityId || cityId.trim().length == 0) {
            throw new Error('cityId invalid')
        }
        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    // const query = `
                    //         SELECT *
                    //         FROM ${SCHEDULES_STORE_NAMES.STORES_CREATE_CITIES}
                    //         WHERE _id = ?;
                    //     `
                    // const params = [cityId]
                    const query = `
                            SELECT 
                                cities._id as city_id,
                                cities.name as city_name,
                                provinces._id as province_id,
                                provinces.name as province_name,
                                countries._id as country_id,
                                countries.name as country_name,
                                countries.prefix,
                                countries.geo_code
                            FROM ${SCHEDULES_STORE_NAMES.STORES_CREATE_CITIES} AS cities
                            JOIN ${SCHEDULES_STORE_NAMES.STORES_CREATE_PROVINCES} AS provinces
                                ON cities.province_id = provinces._id
                            JOIN ${SCHEDULES_STORE_NAMES.STORES_CREATE_COUNTRIES} AS countries
                                ON cities.country_id = countries._id
                            WHERE cities._id = ?;
                        `
                    const params = [cityId]

                    tx.executeSql(query, params, (_, result) => {
                        const row = result.rows.length > 0 ? result.rows.item(0) : null
                        console.log('row => , row')
                        resolve(row)
                    })
                },
                (transactionError) => {
                    console.error('Transaction error => getCityById:', transactionError)
                    reject(transactionError)
                },
            )
        })
    }

    getOrganizationsPending = () => {
        const db = openDatabase()
        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    const query = `
                            SELECT * 
                            FROM ${SCHEDULES_STORE_NAMES.STORES_ORGANIZATIONS}
                            WHERE status_send = ?;
                        `
                    const params = [STATUS_SEND.PENDING]
                    tx.executeSql(query, params, (_, result) => {
                        const rows = result.rows
                        resolve(rows)
                    })
                },
                (transactionError) => {
                    console.error('Transaction error => getOrganizationsPending:', transactionError)
                    reject(transactionError)
                },
            )
        })
    }

    addOrganizaciones = (organizations, status) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (!organizations || organizations.length === 0) {
                return resolve()
            }

            db.transaction(
                (tx) => {
                    const query = `
                        INSERT OR REPLACE INTO ${SCHEDULES_STORE_NAMES.STORES_ORGANIZATIONS} (
                            _id,
                            name,
                            cuit,
                            code,
                            status_send
                        ) VALUES (?, ?, ?, ?, ?)
                    `

                    organizations.forEach((org) => {
                        const params = [
                            org._id,
                            org.name || '',
                            org.cuit || '',
                            org.code,
                            status ?? STATUS_SEND.SEND, // por defecto ENVIADO
                        ]
                        tx.executeSql(query, params)
                    })
                },
                (txError) => {
                    console.error('Transaction error => addOrganizaciones:', txError)
                    reject(txError)
                },
                () => resolve(),
            )
        })
    }

    addOrganization = (organization) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (!organization || organization == null || organization == undefined) {
                return resolve(null)
            }

            db.transaction(
                (tx) => {
                    const proceedInsert = () => {
                        const query = `
                            INSERT OR REPLACE INTO ${SCHEDULES_STORE_NAMES.STORES_ORGANIZATIONS} (
                                _id,
                                name,
                                cuit,
                                code,
                                status_send
                            ) VALUES (?, ?, ?, ?, ?)
                        `

                        const params = [organization._id, organization.name || '', organization.cuit || '', organization.code, organization.status]

                        tx.executeSql(query, params)

                        // Luego de insertar, hacemos un SELECT para devolver la organización creada
                        const querySelect = `
                            SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES_ORGANIZATIONS}
                            WHERE _id = ?
                        `
                        tx.executeSql(
                            querySelect,
                            [organization._id],
                            (_, result) => {
                                const insertedOrg = result.rows.item(0)
                                resolve(insertedOrg)
                            },
                            (_, error) => {
                                console.error('Error al obtener la organización insertada:', error)
                                reject(error)
                                return true
                            },
                        )
                    }

                    proceedInsert()
                },
                (txError) => {
                    console.error('Transaction error => addOrganizaciones:', txError)
                    reject(txError)
                },
            )
        })
    }

    /**
     * @description si existe la organización devuelve true
     */
    organizationExists = (organization) => {
        const db = openDatabase()
        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    const duplicateCheckQuery = `
                            SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES_ORGANIZATIONS}
                            WHERE name = ? OR cuit = ?
                            LIMIT 1
                        `

                    tx.executeSql(
                        duplicateCheckQuery,
                        [organization.name, organization.cuit],
                        (_, result) => {
                            if (result.rows.length > 0) {
                                console.log('organizationExists Organización duplicada detectada, no se inserta resolve true')
                                resolve(true)
                            } else {
                                console.log('organizationExists Organización no existe resolve false')
                                resolve(false)
                            }
                        },
                        (_, error) => {
                            console.error('organizationExists Error al verificar duplicados de organización:', error)
                            reject(error)
                            return true
                        },
                    )
                },
                (txError) => {
                    console.error('Transaction error => organizationExists:', txError)
                    reject(txError)
                },
            )
        })
    }

    /**
     * @description si existe la organización devuelve true
     */
    posExists = (store) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                reject('web not found')
            } else {
                db.transaction(
                    (tx) => {
                        let duplicateCheckQuery
                        let duplicateParams

                        if (store.code && store.code !== '') {
                            duplicateCheckQuery = `
                                    SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES}
                                    WHERE name = ? OR code = ?
                                    LIMIT 1
                                `
                            duplicateParams = [store.name, store.code]
                        } else {
                            // valido solo por name
                            duplicateCheckQuery = `
                                    SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES}
                                    WHERE name = ?
                                    LIMIT 1
                                `
                            duplicateParams = [store.name]
                        }

                        tx.executeSql(
                            duplicateCheckQuery,
                            duplicateParams,
                            (_, result) => {
                                if (result.rows.length > 0) {
                                    console.log('posExists store duplicada detectada, no se inserta resolve true')
                                    resolve(true)
                                } else {
                                    console.log('posExists store no existe resolve false')
                                    resolve(false)
                                }
                            },
                            (_, error) => {
                                console.error('Error al verificar duplicados de organización:', error)
                                reject(error)
                                return true
                            },
                        )
                    },
                    (txError) => {
                        console.error('Transaction error => posExists:', txError)
                        reject(txError)
                    },
                )
            }
        })
    }

    getNextOrganizationCode = () => {
        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    tx.executeSql(
                        `SELECT code FROM ${SCHEDULES_STORE_NAMES.STORES_ORGANIZATIONS} ORDER BY code DESC LIMIT 1`,
                        [],
                        (_, result) => {
                            // const lastCode = result.rows.length > 0 ? result.rows.item(0).code : '00001'
                            if (result && result.rows.length === 0) {
                                resolve(_INITIAL_CODE_ORGANIZATION)
                            } else {
                                const lastCode = result.rows.item(0).code
                                const nextCode = padCodeComplete(parseInt(lastCode) + 1, CANT_DIGIT_CODE_COMPANY)
                                resolve(nextCode)
                            }
                        },
                        (_, error) => {
                            console.error('Error getNextOrganizationCode code:', error)
                            reject(error)
                        },
                    )
                },
                (transactionError) => {
                    console.error('Transaction error => getNextOrganizationCode:', transactionError)
                    reject(transactionError)
                },
            )
        })
    }

    saveStoresAndOrganizationsPendings = (dataSave, responseCreateSqlite) => {
        const db = openDatabase()
        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    const query = `
                        INSERT OR REPLACE INTO ${SCHEDULES_STORE_NAMES.STORES_PENDING_SEND} (
                            data_send,
                            data_match
                        ) VALUES (?, ?);
                    `

                    const params = [JSON.stringify(dataSave), JSON.stringify(responseCreateSqlite)]

                    tx.executeSql(
                        query,
                        params,
                        (_, result) => {
                            console.log('8141 === > ', result)
                            resolve(result)
                        },
                        (_, error) => {
                            console.error('Error saveStoresAndOrganizationsPendings:', error)
                            reject(error)
                            return true
                        },
                    )
                },
                (txError) => {
                    console.error('Transaction error => saveStoresAndOrganizationsPendings:', txError)
                    reject(txError)
                },
            )
        })
    }

    getStoresAndOrganizationsPendings = () => {
        const db = openDatabase()
        return new Promise((resolve, reject) => {
            db.transaction((tx) => {
                const query = `
                        SELECT *
                        FROM ${SCHEDULES_STORE_NAMES.STORES_PENDING_SEND};
                    `

                const params = []

                tx.executeSql(
                    query,
                    params,
                    (_, result) => {
                        if (result && result.rows._array && result.rows._array.length > 0) {
                            const dataPending = result.rows._array
                                .map((register) => {
                                    return {
                                        ...register,
                                        data_match: JSON.parse(register.data_match),
                                        data_send: JSON.parse(register.data_send),
                                    }
                                })
                                .filter((register) => {
                                    const createOrg = register.data_send?.createOrganization
                                    const orgId = register.data_send?.organization?._id

                                    if (createOrg === false && isLocalGeneratedId(orgId)) {
                                        return false
                                    }
                                    return true
                                })
                            resolve(dataPending)
                        }

                        resolve([])
                    },
                    (_, error) => {
                        console.error('Error saveStoresAndOrganizationsPendings:', error)
                        reject(error)
                    },
                )
            })
        })
    }

    getStoresAndOrganizationsPendingsWithLocalOrg = () => {
        const db = openDatabase()
        return new Promise((resolve, reject) => {
            db.transaction((tx) => {
                const query = `
                    SELECT *
                    FROM ${SCHEDULES_STORE_NAMES.STORES_PENDING_SEND};
                `
                const params = []

                tx.executeSql(
                    query,
                    params,
                    (_, result) => {
                        if (result && result.rows._array && result.rows._array.length > 0) {
                            const dataPending = result.rows._array
                                .map((register) => ({
                                    ...register,
                                    data_match: JSON.parse(register.data_match),
                                    data_send: JSON.parse(register.data_send),
                                }))
                                .filter((register) => {
                                    const createOrg = register.data_send?.createOrganization
                                    const orgId = register.data_send?.organization?._id
                                    // Solo devuelve los que cumplen ambas condiciones
                                    return createOrg === false && isLocalGeneratedId(orgId)
                                })
                            resolve(dataPending)
                        } else {
                            resolve([])
                        }
                    },
                    (_, error) => {
                        console.error('Error getStoresAndOrganizationsPendingsWithLocalOrg:', error)
                        reject(error)
                    },
                )
            })
        })
    }

    removeStoresAndOrganizationsPendingsById = (register_id) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                reject(new Error('Not supported on web'))
            } else {
                db.transaction(
                    (tx) => {
                        const query = `DELETE FROM ${SCHEDULES_STORE_NAMES.STORES_PENDING_SEND} WHERE _id = ?`
                        tx.executeSql(
                            query,
                            [register_id],
                            (_, result) => {
                                console.log('removeStoresAndOrganizationsPendingsById => ', result)

                                if (result.rowsAffected > 0) {
                                    resolve(true)
                                } else {
                                    resolve(false)
                                }
                            },
                            (_, error) => {
                                console.error('Error al intentar eliminar STORES_PENDING_SEND:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    removeStoresPendingsById = (register_id) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                reject(new Error('Not supported on web'))
            } else {
                db.transaction(
                    (tx) => {
                        const query = `DELETE FROM ${SCHEDULES_STORE_NAMES.STORES} WHERE _id = ?`
                        tx.executeSql(
                            query,
                            [register_id],
                            (_, result) => {
                                console.log('removeStoresPendingsById => ', result)

                                if (result.rowsAffected > 0) {
                                    resolve(true)
                                } else {
                                    resolve(false)
                                }
                            },
                            (_, error) => {
                                console.error('Error al intentar eliminar STORES:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    removeSchedulePosPendingsById = (register_id) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                reject(new Error('Not supported on web'))
            } else {
                db.transaction(
                    (tx) => {
                        const query = `DELETE FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_POS} WHERE _id = ?`
                        tx.executeSql(
                            query,
                            [register_id],
                            (_, result) => {
                                console.log('removeSchedulePosPendingsById => ', result)

                                if (result.rowsAffected > 0) {
                                    resolve(true)
                                } else {
                                    resolve(false)
                                }
                            },
                            (_, error) => {
                                console.error('Error al intentar eliminar SCHEDULES_POS:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    removeOrganizationPendingsById = (register_id) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                reject(new Error('Not supported on web'))
            } else {
                db.transaction(
                    (tx) => {
                        const query = `DELETE FROM ${SCHEDULES_STORE_NAMES.STORES_ORGANIZATIONS} WHERE _id = ?`
                        tx.executeSql(
                            query,
                            [register_id],
                            (_, result) => {
                                console.log('removeOrganizationPendingsById => ', result)
                                if (result.rowsAffected > 0) {
                                    resolve(true)
                                } else {
                                    resolve(false)
                                }
                            },
                            (_, error) => {
                                console.error('Error al intentar eliminar SCHEDULES_POS:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    getOrganizations = () => {
        const db = openDatabase()
        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    const query = `
                            SELECT *
                            FROM ${SCHEDULES_STORE_NAMES.STORES_ORGANIZATIONS}
                            ORDER BY name COLLATE NOCASE;`

                    const params = []

                    tx.executeSql(query, params, (_, result) => {
                        const rows = result.rows

                        const organizations = rows._array.map((row) => ({
                            _id: row._id,
                            code: row.code,
                            cuit: row.cuit,
                            name: row.name,
                        }))
                        resolve(organizations)
                    })
                },
                (transactionError) => {
                    console.error('Transaction error => getOrganizations:', transactionError)
                    reject(transactionError)
                },
            )
        })
    }

    existsInLocalDB = (table, fields) => {
        const db = openDatabase()
        return new Promise((resolve, reject) => {
            console.log('existsInLocalDB => table:', table, 'fields:', fields)

            const whereClauses = fields.map((f) => `${f.label} = ?`).join(' OR ')
            const params = fields.map((f) => f.value)
            const query = `SELECT * FROM ${table} WHERE ${whereClauses} LIMIT 1`

            console.log('existsInLocalDB => query:', query, 'params:', params)
            db.transaction(
                (tx) => {
                    tx.executeSql(
                        query,
                        params,
                        (_, result) => {
                            console.log('existsInLocalDB => result:', result)
                            // resolve(result.rows._array.length > 0)

                            if (result.rows.length > 0) {
                                console.log('11111posExists store duplicada detectada, no se inserta resolve true')
                                resolve(true)
                            } else {
                                console.log('2222posExists store no existe resolve false')
                                resolve(false)
                            }
                        },
                        (_, error) => {
                            console.error('Error al verificar duplicados:', error)
                            reject(error)
                            return true
                        },
                    )
                },
                (txError) => {
                    console.error('Transaction error:', txError)
                    reject(txError)
                },
            )
        })
    }

    updateKeyCheckinFqa = (checkinId, formsIdsFqa) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (!formsIdsFqa || formsIdsFqa.length === 0) {
                resolve()
                return
            }

            if (isWeb()) {
            } else {
                db.transaction(
                    (tx) => {
                        const placeholders = formsIdsFqa.map(() => '?').join(',')
                        const query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} SET checkin_id = ? WHERE _id IN (${placeholders});`
                        const params = [checkinId, ...formsIdsFqa]

                        tx.executeSql(
                            query,
                            params,
                            (_, result) => resolve(result),
                            (_, error) => {
                                console.error('Error updating checkin_id:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('updateKeyCheckinFqa error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    // forms pendingSync
    addFormPendingSync = (form) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            if (!form || form == null || form == undefined) {
                return resolve()
            }

            // console.log('addFormPendingSync form => ', form)
            // console.log('addFormPendingSync form => ', form)

            const { activity_id, status, date, count, form_name, status_send, schedule_id, store_id, checkin_id } = form

            // console.log('checkin_id => ', checkin_id)
            // console.log('store_id => ', store_id)

            let can_send_value = true

            // if (store_id && store_id !== '' && store_id !== null && store_id !== undefined) {
            //     console.error('Este form tiene id de tienda, no es vespertino ni matutino')
            //     reject(new Error('Este form tiene id de tienda, no es vespertino ni matutino'))
            //     return
            // }

            if (schedule_id == null || schedule_id == undefined || schedule_id == '' || schedule_id === 'wise') {
                // console.error('schedule_id is required')
                reject(new Error('addFormPendingSync - schedule_id is required => schedule_id => ', schedule_id))
                return
            }
            if (activity_id == null || activity_id == undefined || activity_id == '') {
                // console.error('activity_id is required')
                reject(new Error('addFormPendingSync - activity_id is required'))
                return
            }

            let isFormToStoreId = false

            if (store_id && store_id !== '' && store_id !== null && store_id !== undefined) {
                isFormToStoreId = true
            }

            let existingQuery, existingParams
            // validar si existe ya un registro con schedule_id y activity_id

            if (isFormToStoreId == false) {
                existingQuery = `SELECT * FROM ${SCHEDULES_STORE_NAMES.FORMS_PENDING_SYNC}
                                          WHERE schedule_id = ? AND activity_id = ?;`
                existingParams = [schedule_id, activity_id]
            } else {
                existingQuery = `SELECT * FROM ${SCHEDULES_STORE_NAMES.FORMS_PENDING_SYNC}
                                          WHERE schedule_id = ? AND activity_id = ? AND store_id = ? AND checkin_id = ?;`
                existingParams = [schedule_id, activity_id, store_id, checkin_id]
            }

            console.log(`${isFormToStoreId ? 'Con' : 'Sin'} store_id, buscando formulario existente:`)

            db.transaction(
                (tx) => {
                    tx.executeSql(
                        existingQuery,
                        existingParams,
                        (_, result) => {
                            if (result && result.rows && result.rows.length > 0) {
                                console.log('Existing form found:', result.rows.item(0))
                                // Si existe, se puede actualizar o manejar según sea necesario
                                resolve(result.rows.item(0)) // Resolvemos con el registro existente
                            } else {
                                console.log('No existing form found, proceeding to insert.')

                                // let valuesKeys = 'activity_id, status, date, count, form_name, status_send, schedule_id, can_send'
                                let valuesKeys = 'activity_id, status, date, count, form_name, status_send, schedule_id'
                                let valuesPlaceholders = '?,?,?,?,?,?,?'

                                let paramsQuery = [activity_id, status, date, count, form_name, status_send, schedule_id]
                                console.log('isFormToStoreId => ', isFormToStoreId)
                                if (isFormToStoreId == true) {
                                    paramsQuery.push(store_id) // Agregar store_id si es necesario
                                    valuesKeys += ', store_id'
                                    valuesPlaceholders += ',?'

                                    console.log('checkin_id => ', checkin_id)
                                    if (checkin_id) {
                                        paramsQuery.push(checkin_id)
                                        valuesKeys += ', checkin_id'
                                        valuesPlaceholders += ',?'
                                    }

                                    if (!isValidMongoId(checkin_id)) {
                                        can_send_value = false
                                    }

                                    paramsQuery.push(can_send_value) // Agregar can_send
                                    valuesKeys += ', can_send'
                                    valuesPlaceholders += ',?'
                                }

                                let query = `INSERT INTO ${SCHEDULES_STORE_NAMES.FORMS_PENDING_SYNC} 
                                                (${valuesKeys}) 
                                                VALUES (${valuesPlaceholders});`

                                // console.log('query => ', query)
                                // console.log('paramsQuery => ', paramsQuery)

                                db.transaction(
                                    (tx) => {
                                        tx.executeSql(
                                            query,
                                            paramsQuery,
                                            (_, result) => {
                                                console.log('Form added to pending sync:', result)
                                                resolve(result)
                                            },
                                            (_, error) => {
                                                console.error('Error adding form to pending sync:', error)
                                                reject(error)
                                            },
                                        )
                                    },
                                    (error) => {
                                        console.error('Transaction error:', error)
                                        reject(error)
                                    },
                                )
                            }
                        },
                        (_, error) => {
                            console.error('Error checking existing form:', error)
                            reject(error)
                        },
                    )
                },
                (error) => {
                    console.error('Transaction error:', error)
                    reject(error)
                },
            )
        })
    }

    getFormsPendingSync = () => {
        const db = openDatabase()
        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    // busco los status 0 (no enviados)
                    const query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.FORMS_PENDING_SYNC} WHERE status_send = ? AND can_send = ?;`
                    const params = [STATUS_FORM_PENDING_SYNC.NO_SENDED, true]

                    tx.executeSql(
                        query,
                        params,
                        (_, result) => {
                            if (result && result.rows && result.rows.length === 0) {
                                console.log('No forms pending sync found.')
                                resolve([]) // No hay formularios pendientes
                                return
                            }

                            console.log('Forms pending sync found:', result.rows.length)
                            resolve(result.rows._array)
                        },
                        (_, error) => {
                            console.error('Error fetching forms pending sync:', error)
                            reject(error)
                        },
                    )
                },
                (error) => {
                    console.error('Transaction error:', error)
                    reject(error)
                },
            )
        })
    }

    /**
     * @description Updatea el status_send del formulario, que que determina si hay que sincronizar o no
     * @param {*} schedule_id
     * @param {*} activity_id
     * @param {*} status_send
     * @returns
     */
    updateFormPendingSyncStatusSend = async (schedule_id, activity_id, status_send) => {
        console.log('🔄 updateFormPendingSyncStatusSend (wrapper) =>', { schedule_id, activity_id, status_send })
        
        // ✅ Usar la nueva implementación
        const scheduleRepository = await import('../db-sqlite/repositories/schedule.repository')
        return await scheduleRepository.updateFormPendingSyncStatusSend(schedule_id, activity_id, status_send)
    }

    /**
     * @description Updatea el status real del formulario
     * @param {*} schedule_id
     * @param {*} activity_id
     * @param {*} status
     * @returns
     */
    updateFormPendingSyncStatus = (schedule_id, activity_id, status) => {
        const db = openDatabase()

        return new Promise((resolve, reject) => {
            const query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_PENDING_SYNC} 
                           SET status = ?, date = datetime('now', 'localtime')
                           WHERE schedule_id = ? AND activity_id = ?;`
            const params = [status, schedule_id, activity_id]

            db.transaction(
                (tx) => {
                    tx.executeSql(
                        query,
                        params,
                        (_, result) => {
                            // console.log('Form pending sync status updated:', result)
                            resolve(result)
                        },
                        (_, error) => {
                            console.error('Error updating form pending sync status:', error)
                            reject(error)
                        },
                    )
                },
                (error) => {
                    console.error('Transaction error:', error)
                    reject(error)
                },
            )
        })
    }

    getStoreMaxDistance = (store_id) => {
        const db = openDatabase()

        if (!store_id || store_id === null || store_id === undefined) return 0
        return new Promise((resolve, reject) => {
            if (isWeb()) {
            } else {
                db.transaction((tx) => {
                    let query = `
                                    SELECT maximum_distance_for_checkin
                                    FROM ${SCHEDULES_STORE_NAMES.STORES}
                                    WHERE _id = ?;
                        `
                    let params = [store_id]

                    tx.executeSql(
                        query,
                        params,
                        (_, result) => {
                            const maximum_distance_for_checkin = result.rows.item(0).maximum_distance_for_checkin
                            resolve(maximum_distance_for_checkin)
                        },
                        (_, error) => {
                            console.error('No se pudo obtener la tienda:', error)
                            reject()
                        },
                    )
                })
            }
        })
    }

    getFormsPendingSyncByCheckinId = (checkin_id) => {
        const db = openDatabase()
        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    const query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.FORMS_PENDING_SYNC} WHERE checkin_id = ?;`
                    const params = [checkin_id]

                    tx.executeSql(
                        query,
                        params,
                        (_, result) => {
                            const forms = []
                            for (let i = 0; i < result.rows.length; i++) {
                                forms.push(result.rows.item(i))
                            }
                            resolve(forms)
                        },
                        (_, error) => {
                            console.error('Error buscando formularios pendientes de sincronización por checkin_id:', error)
                            reject(error)
                        },
                    )
                },
                (error) => {
                    console.error('Transaction findFormsPendingSyncByCheckinId error:', error)
                    reject(error)
                },
            )
        })
    }

    updateCanSendFormsPendingSyncById = (formIds, can_send) => {
        const db = openDatabase()
        return new Promise((resolve, reject) => {
            db.transaction((tx) => {
                if (!formIds || formIds.length === 0) return resolve()
                const placeholders = formIds.map(() => '?').join(',')

                const query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_PENDING_SYNC} SET can_send = ? WHERE _id IN (${placeholders});`
                const params = [can_send, ...formIds]

                tx.executeSql(
                    query,
                    params,
                    (_, result) => {
                        resolve(result)
                    },
                    (_, error) => {
                        console.error('Error updating can_send en formularios pendientes de sincronización:', error)
                        reject(error)
                    },
                )
            })
        })
    }

    updateCheckinIdFormsPendingSyncById = (formIds, checkin_id) => {
        const db = openDatabase()
        return new Promise((resolve, reject) => {
            db.transaction((tx) => {
                if (!formIds || formIds.length === 0) return resolve()
                const placeholders = formIds.map(() => '?').join(',')

                const query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_PENDING_SYNC} SET checkin_id = ? WHERE _id IN (${placeholders});`
                const params = [checkin_id, ...formIds]

                tx.executeSql(
                    query,
                    params,
                    (_, result) => {
                        resolve(result)
                    },
                    (_, error) => {
                        console.error('Error updating checkin_id en formularios pendientes de sincronización:', error)
                        reject(error)
                    },
                )
            })
        })
    }

    getFormsPendingSyncToSend = (schedule_id, checkin_id, forms) => {
        const db = openDatabase()
        // console.log('getFormsPendingSyncToSend - schedule_id:', schedule_id, 'checkin_id:', checkin_id, 'forms:', forms)
        return new Promise((resolve, reject) => {
            let query = ''
            let params = []

            if (checkin_id && checkin_id != null) {
                // console.log('hay checkin => ', checkin_id)
                // Si hay checkin_id, busco por ese id tambien
                query = `
                    SELECT *
                    FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                    WHERE schedule_id = ? AND checkin_id = ? AND form_id IN (${forms.map(() => '?').join(', ')})
                `
                params = [schedule_id, checkin_id, ...forms]
            } else {
                query = `
                    SELECT *
                    FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                    WHERE schedule_id = ? AND form_id IN (${forms.map(() => '?').join(', ')})
                `
                params = [schedule_id, ...forms]
            }

            db.transaction(
                (tx) => {
                    tx.executeSql(
                        query,
                        params,
                        (_, result) => {
                            if (result && result.rows && result.rows.length === 0) {
                                // console.log('No forms pending sync to send found.')
                                resolve([]) // No hay formularios pendientes
                                return
                            }
                            // Si hay formularios pendientes, resolver con los resultados

                            const formsMap = result.rows._array.map((form) => {
                                const answers = JSON.parse(form.answers)
                                const questions = JSON.parse(form.questions)
                                const type_form = JSON.parse(form.type_form)

                                return {
                                    ...form,
                                    answers,
                                    questions,
                                    type_form,
                                }
                            })

                            resolve(formsMap)
                        },
                        (error) => {
                            console.error('getFormsPendingSyncToSend - Transaction error:', error)
                            reject(error)
                        },
                    )
                },
                (error) => {
                    console.error('getFormsPendingSyncToSend - Transaction error:', error)
                    reject(error)
                },
            )
        })
    }
    updateStoreKPIS = (store_id, kpis) => {
        if (!store_id || !kpis || kpis.length === 0) return
        const db = openDatabase()
        return new Promise((resolve, reject) => {
            db.transaction((tx) => {
                if (!kpis || kpis.length === 0) return resolve()

                const query = `INSERT OR REPLACE INTO ${SCHEDULES_STORE_NAMES.STORES_INDICATORS} (store_id, indicators) VALUES (?, ?);`
                const params = [store_id, JSON.stringify(kpis)]
                tx.executeSql(
                    query,
                    params,
                    (_, result) => {
                        console.log('KPI guardado con exito')
                        resolve(result)
                    },
                    (_, error) => {
                        console.error(`Error updating KIPS in store ${store_id}:`, error)
                        reject(error)
                    },
                )
            })
        })
    }
    getStoreKPIS = (store_id) => {
        const db = openDatabase()
        return new Promise((resolve, reject) => {
            db.transaction((tx) => {
                const query = `SELECT indicators FROM ${SCHEDULES_STORE_NAMES.STORES_INDICATORS} WHERE store_id = ?;`
                const params = [store_id]
                tx.executeSql(
                    query,
                    params,
                    (_, result) => {
                        if (result && result.rows && result.rows.length > 0) {
                            const kpis = JSON.parse(result.rows.item(0).indicators)
                            console.log('KPI recuperado con exito')
                            resolve(kpis)
                        } else {
                            resolve([])
                        }
                    },
                    (_, error) => {
                        console.error(`Error getting KPIS for store ${store_id}:`, error)
                        reject(error)
                    },
                )
            })
        })
    }

    createActivityDistributorPending = (data) => {
        const db = openDatabase()
        return new Promise((resolve, reject) => {
            db.transaction((tx) => {
                const checkin_id = data?.checkin_id || null
                const activities = data?.activities || null
                const schedule_id = data?.schedule_id || null

                let query = ''
                let valuesKeys = ''
                let valuesPlaceholders = ''
                let params = []

                if (checkin_id) {
                    valuesKeys += 'checkin_id, '
                    valuesPlaceholders += '?, '
                    params.push(checkin_id)
                }
                if (schedule_id) {
                    valuesKeys += 'schedule_id, '
                    valuesPlaceholders += '?, '
                    params.push(schedule_id)
                }
                if (activities) {
                    valuesKeys += 'activities, '
                    valuesPlaceholders += '?, '
                    params.push(activities)
                }

                query = `
                    INSERT INTO ${SCHEDULES_STORE_NAMES.SCHEDULE_ACTIVITIES_ADVANCES_PENDINGS} 
                    (${valuesKeys.slice(0, -2)}) 
                    VALUES (${valuesPlaceholders.slice(0, -2)});
                `

                tx.executeSql(
                    query,
                    params,
                    (_, result) => {
                        resolve(result)
                    },
                    (_, error) => {
                        console.error('Error creating activity distributor pending:', error)
                        reject(error)
                    },
                )
            })
        })
    }

    getActivitiesDistributorsPending = () => {
        const db = openDatabase()
        return new Promise((resolve, reject) => {
            db.transaction((tx) => {
                const query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULE_ACTIVITIES_ADVANCES_PENDINGS};`
                tx.executeSql(
                    query,
                    [],
                    (_, result) => {
                        const activities = []
                        for (let i = 0; i < result.rows.length; i++) {
                            activities.push(result.rows.item(i))
                        }
                        resolve(activities)
                    },
                    (_, error) => {
                        console.error('Error getting activities distributors pending:', error)
                        reject(error)
                    },
                )
            })
        })
    }

    deleteByIdActivitiesDistributorsPending = (id) => {
        const db = openDatabase()
        return new Promise((resolve, reject) => {
            db.transaction((tx) => {
                const query = `DELETE FROM ${SCHEDULES_STORE_NAMES.SCHEDULE_ACTIVITIES_ADVANCES_PENDINGS} WHERE _id = ?;`
                tx.executeSql(
                    query,
                    [id],
                    (_, result) => {
                        resolve(result)
                    },
                    (_, error) => {
                        console.error('Error deleting activities distributors pending:', error)
                        reject(error)
                    },
                )
            })
        })
    }

    getFormsByIds = (formsIds) => {
        const db = openDatabase()
        return new Promise((resolve, reject) => {
            db.transaction((tx) => {
                const query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.FORMS} WHERE _id IN (${formsIds.map(() => '?').join(', ')});`
                tx.executeSql(
                    query,
                    formsIds,
                    (_, result) => {
                        const forms = []

                        for (let i = 0; i < result.rows.length; i++) {
                            forms.push(result.rows.item(i))
                        }
                        resolve(forms)
                    },
                    (_, error) => {
                        console.error('Error getting forms by IDs:', error)
                        reject(error)
                    },
                )
            })
        })
    }
}

export const schedulesServices = new schedulesService(db)

/**
 * @description valida si existe un registro, si existe true, sino existe false
 * @param {@} registersExistent
 * @returns
 */
const validateDateRegister = (registersExistent) => {
    let isExists = false
    if (!registersExistent || registersExistent.length === 0) {
        return isExists
    }

    const today = new Date().toISOString().split('T')[0]
    // console.log('2registersExistent => today =>', today, registersExistent)
    for (let i = 0; i < registersExistent.length; i++) {
        const row = registersExistent[i]
        if (row.date_init && row.date_init.split('T')[0] === today) {
            isExists = true
            break
        }
    }

    return isExists
}
export function calculateDistance(lat1, lon1, lat2, lon2) {
    let rad = function (x) {
        return (x * Math.PI) / 180
    }
    let R = 6378.137 //Radio de la Earth en km
    let dLat = rad(lat2 - lat1)
    let dLong = rad(lon2 - lon1)
    let a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLong / 2) * Math.sin(dLong / 2)
    let c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

    //aquí obtienes la distancia en metros por la conversion 1Km =1000m
    var d = R * c * 1000
    return d
}

/**
 *
 * @param {*} questions
 * @returns {{cantQuestionPonderar<number> ; scoreTotalQuestionsPonderar<number>}}
 */
const getCantQuestionPonderar = (questions) => {
    const questionsTransform = JSON.parse(questions)
    const cantQuestionPonderar = questionsTransform.filter((obj) => obj.ponderar === true)
    const scoreTotalQuestionsPonderar = cantQuestionPonderar.reduce((accumulator, currentValue) => {
        const questionScore = currentValue.questionScore ?? 0
        const score = currentValue.score ?? 0
        return accumulator + questionScore + score
    }, 0)

    return {
        scoreTotalQuestionsPonderar,
        cantQuestionPonderar: cantQuestionPonderar.length,
    }
}

// Función para calcular la bounding box
function calculateBoundingBox(latitude, longitude, distance) {
    const earthRadius = 6371 // Radio de la Tierra en kilómetros
    const toRadians = (degrees) => (degrees * Math.PI) / 180
    const toDegrees = (radians) => (radians * 180) / Math.PI

    const angularDistance = distance / earthRadius // Convertir la distancia a un ángulo en radianes

    const minLatitude = latitude - toDegrees(angularDistance)
    const maxLatitude = latitude + toDegrees(angularDistance)

    const deltaLongitude = Math.asin(Math.sin(angularDistance) / Math.cos(toRadians(latitude)))

    const minLongitude = longitude - toDegrees(deltaLongitude)
    const maxLongitude = longitude + toDegrees(deltaLongitude)

    return {
        minLatitude: minLatitude,
        maxLatitude: maxLatitude,
        minLongitude: minLongitude,
        maxLongitude: maxLongitude,
    }
}

// Función para generar un ID único
export function generateUniqueId() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0,
            v = c === 'x' ? r : (r & 0x3) | 0x8
        return v.toString(16)
    })
}

const newcalculateDistance = (lat1, lng1, lat2, lng2) => {
    const toRadians = (degree) => degree * (Math.PI / 180)

    const R = 6371e3 // Radio de la Tierra en metros
    const φ1 = toRadians(lat1)
    const φ2 = toRadians(lat2)
    const Δφ = toRadians(lat2 - lat1)
    const Δλ = toRadians(lng2 - lng1)

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

    return R * c // Distancia en metros
}

const isNaOptionSelected = (answer) => {
    return (
        (answer.option_selected_text && answer.option_selected_text.includes('na')) ||
        (answer.questionOptionSelectedText && answer.questionOptionSelectedText.includes('na'))
    )
}
